import type { JSONSchemaType } from 'ajv'
import type { ActionContext, ActionDefinition, ActionOutcome, ActionRisk } from '../action'
import { getAction, registerAction } from '../registry'
import { isWorkspaceRole, roleCanPerform } from '../../../utils/workspacePermissions'
import {
  setOpsPhoneRoleAccess,
  applyOpsPhoneRecommendedSet,
  setOpsPhonePersonAccess,
  getMyOpsPhoneItems,
} from '../../opsPhoneAccessService'
import {
  checkOutRoom,
  markRoomOccupied,
  setRoomDueOut,
  startRoomClean,
  markRoomCleaned,
  inspectRoom,
  setRoomService,
  setRoomTags,
  setPropertyMinibarCheck,
  prepareRoomCleanRecord,
  assignRooms,
  unassignRoom,
  reassignRoom,
} from '../../opsRoomStatesService'
import {
  setPropertyGuestRequestsEnabled,
  confirmGuestExtraRequest,
  declineGuestExtraRequest,
} from '../../opsGuestRequestsService'
import {
  setBedState,
  listBedEvents,
} from '../../opsBedStatesService'
import {
  createPlace,
  renamePlace,
  setPlaceAccess,
  setPlaceDutyChain,
  setPlacePreset,
  setPropertyRoomTypes,
  setRoomType,
  movePlace,
  archivePlace,
  bulkCreatePlaces,
  createPlaceToken,
  revokePlaceToken,
  resolvePlaceToken,
} from '../../opsPlacesService'
import {
  createJobSite,
  updateJobSite,
  listCustomerContacts,
  scheduleJob,
  raiseRevisit,
  listJobBoard,
} from '../../opsJobsService'
import {
  createParty,
  updateParty,
  setPartyMembers,
  createWorker,
  updateWorker,
  archiveWorker,
  addGang,
} from '../../opsWorkforceService'
import {
  setSectorRoles,
  saveTemplateDraft,
  submitTemplateVersion,
  approveTemplateVersion,
  submitRecord,
  reviewRecord,
  returnRecord,
  approveRecord,
  addCorrection,
} from '../../opsRegistersService'
import {
  createDutyRole,
  updateDutyRole,
  archiveDutyRole,
  createDepartment,
  renameDepartment,
  archiveDepartment,
  createShift,
  updateShift,
  archiveShift,
  createCoverageRule,
  updateCoverageRule,
  archiveCoverageRule,
  createRoster,
  saveDraftAssignments,
  publishRoster,
  previewRosterWarnings,
  acknowledgeRoster,
  requestRosterChange,
  respondRosterChange,
  decideRosterChange,
  cancelRosterChange,
  markAttendanceRoll,
  requestAttendanceCorrection,
  decideAttendanceCorrection,
  importAttendanceCsv,
  assignRelief,
} from '../../opsRostersService'
import {
  createSchedule,
  updateSchedule,
  pauseSchedule,
  resumeSchedule,
  archiveSchedule,
  skipDue,
  recordCheckpointScan,
} from '../../opsSchedulesService'
import {
  upsertRequestKind,
  listRequestKinds,
  raiseRequest,
  attachRequestPhotos,
  acknowledgeRequest,
  startRequest,
  assignRequest,
  markRequestDone,
  verifyRequest,
  reopenRequest,
  cancelRequest,
} from '../../opsRequestsService'
import {
  createComplianceItem,
  updateComplianceItem,
  renewComplianceItem,
  attachComplianceEvidence,
  archiveComplianceItem,
} from '../../opsComplianceService'
import {
  createAsset,
  updateAsset,
  setAssetStatus,
  isolateAsset,
  releaseAsset,
  retireAsset,
  recordAssetServiceVisit,
} from '../../opsAssetsService'
import {
  createEngagement,
  updateEngagement,
  setEngagementStatus,
  setEngagementMilestones,
  setEngagementFinance,
  upsertAllocation,
  endAllocation,
  linkGoalToEngagement,
  unlinkGoalFromEngagement,
  recomputeEngagementMargin,
  exportDeliveryCsv,
} from '../../opsDeliveryService'
import {
  createChangeRequest,
} from '../../opsDeliveryChangeRequestsService'
import {
  issueClientLinks,
  revokeClientLink,
} from '../../opsClientLinksService'
import {
  createOutsideApproval,
  reissueApproverLink,
  revokeOutsideApproval,
  setApprovalSettings,
} from '../../opsApprovalsService'
import {
  importOpsCsv,
  exportOpsCsv,
} from '../../opsImportsService'
import {
  setOpsWageRate,
  previewOpsWageSheet,
  lockOpsWageSheet,
  unlockOpsWageSheet,
  exportOpsWageSheetCsv,
  getOpsWageSheetStatus,
} from '../../opsWagesService'
import {
  requestEvidenceExport,
  getEvidenceExportDownload,
  setRetentionSettings,
  setLegalHold,
  requestLegalHoldLift,
  confirmLegalHoldLift,
  cancelLegalHoldLift,
  restoreFromRetentionBin,
  listSealedArchives,
  requestSealedArchiveExport,
  getRetentionLockSummary,
} from '../../opsEvidenceService'
import {
  recordOpsRestrictedView,
  setOpsPrivacySettings,
} from '../../opsPrivacyService'
import {
  upsertCustomer,
  archiveCustomer,
  findCustomerByPhone,
  linkMyCustomerRecord,
  createBookingPlan,
  updateBookingPlan,
  archiveBookingPlan,
  grantMembership,
  renewMembership,
  freezeMembership,
  unfreezeMembership,
  cancelMembership,
  recordDuePaid,
  waiveDue,
  createBookable,
  updateBookable,
  archiveBookable,
  bookSession,
  bookInHouseExtra,
  cancelBooking,
  moveBooking,
  leaveWaitlist,
  waiveStrike,
  clearBookingBlock,
  issueQueueToken,
  callNextToken,
  markTokenServed,
  skipToken,
  getQueueBoard,
  listQueueTokens,
  getMyBookingQr,
  checkInByCustomerQr,
  bookingCheckInByPlaceQr,
  prepareBookingCheckInOtp,
  checkInByPhoneOtp,
  manualCheckIn,
  setPublicBookingLink,
  getPublicBookingInfo,
  publicBookSession,
  publicCancelBooking,
  listMyPublicBookings,
  listRenewalsDue,
  listChurnCalls,
  recordCustomerCall,
} from '../../opsBookingsService'
import {
  createDeviceEnrolCode,
  enrolSharedDevice,
  revokeSharedDevice,
  setSharedDeviceSettings,
  listSharedDevices,
  listSharedDeviceSessions,
  setMySharedDevicePin,
  setSharedDevicePinByPhone,
  resetSharedDevicePin,
  listSharedDevicePeople,
  sharedDeviceSwitchIn,
  sharedDeviceSwitchOut,
  submitSharedDeviceQueued,
  issueSharedDeviceUploadUrl,
  finalizeSharedDeviceUpload,
} from '../../opsSharedDevicesService'
import {
  createStaysPmsConnection,
  rotateStaysPmsSecret,
  updateStaysPmsConnection,
  deleteStaysPmsConnection,
  getStaysPmsStatus,
  retryStaysPmsOutbox,
  testStaysPmsEzeeConnection,
  testStaysPmsHotelogixConnection,
  importStaysPmsReport,
} from '../../opsStaysPmsService'
import {
  createOpsConnector,
  rotateOpsConnectorSecret,
  updateOpsConnector,
  deleteOpsConnector,
  getOpsConnectorStatus,
  replayOpsConnectorEvent,
} from '../../opsConnectorsService'
import {
  logFoundItem,
  recordFoundItemPhoto,
  moveFoundItemBin,
  startLostFoundClaim,
  reissueLostFoundClaimLink,
  cancelLostFoundClaim,
  verifyLostFoundClaim,
  completeLostFoundReturn,
  proposeLostFoundDisposal,
  signLostFoundDisposal,
  withdrawLostFoundDisposal,
  setLostFoundSettings,
  getLostFoundClaimant,
} from '../../opsLostFoundService'
import {
  setOwnerFeed,
  removeFeedOwner,
  reissueOwnerFeedLink,
  getOwnerFeed,
  listOwnerDigests,
  sendOwnerDigestNow,
} from '../../opsOwnerFeedService'
import {
  getStaysGroupView,
  pushStaysGroupTemplate,
} from '../../opsStaysGroupService'
import {
  createConProject,
  updateConProject,
  setConProjectPackages,
  setConProjectApprovers,
  createConDrawing,
  issueConDrawingRevision,
  withdrawConDrawing,
} from '../../conProjectsService'
import {
  saveDprDraft,
  submitDpr,
  reviewDpr,
  returnDpr,
  approveDpr,
  addDiaryEntry,
} from '../../conDprService'
import {
  saveConPourCard,
  requestConInspection,
  recordConInspectionOutcome,
  reassignConInspector,
  createConTest,
  addConTestResult,
} from '../../conQualityService'
import {
  createConIssue,
  updateConIssue,
  attachConIssuePhotos,
  respondConIssue,
  withdrawConIssue,
  closeConIssue,
  decideConIssue,
  sendConIssueForApproval,
  getConIssue,
  getConIssuePhotoUrls,
  listConIssues,
  listConIssueDecisions,
} from '../../conIssuesService'
import {
  upsertConParty,
  listConParties,
  saveConMeasurement,
  submitConMeasurement,
  acceptConMeasurement,
  disputeConMeasurement,
  correctConMeasurement,
  cancelConMeasurement,
  getConMeasurement,
  listConMeasurements,
  listDisputedConMeasurements,
  getConMeasureTotals,
} from '../../conMeasureService'
import { getConReport } from '../../conReportsService'
import { getPlantDowntimeReport } from '../../plantReportsService'
import { getPlantStarterStatus } from '../../opsPlantStartersService'
import { setPlantNearMissExportPeople } from '../../opsPlantQualityService'
import { getConStarterStatus } from '../../conStartersService'
import {
  acknowledgeOnCall,
  getOnCallBoard,
  listMyOnCallSlots,
  type AcknowledgeOnCallInput,
  type GetOnCallBoardInput,
  type ListMyOnCallSlotsInput,
} from '../../hosPeopleService'
import {
  getHosStarterStatus,
  type GetHosStarterStatusInput,
} from '../../hosStartersService'
import {
  setBmwSettings,
  saveBmwDay,
  submitBmwDay,
  correctBmwDay,
  recordBmwPickup,
  getBmwStatus,
  getBmwSummary,
  startBmwHandover,
  attachBmwPickupReceipt,
  type SetBmwSettingsInput,
  type SaveBmwDayInput,
  type SubmitBmwDayInput,
  type CorrectBmwDayInput,
  type RecordBmwPickupInput,
  type GetBmwStatusInput,
  type GetBmwSummaryInput,
} from '../../hosBmwService'
import {
  ensureClinicsStarterSet,
  importTempLog,
} from '../../opsClinicsService'
import {
  type StartBmwHandoverInput,
  type AttachBmwPickupReceiptInput,
  type EnsureClinicsStarterSetInput,
  type ImportTempLogInput,
} from '../../../types/ops'
import {
  setStaffStatus,
  listStaffStatus,
  getMyStaffStatus,
  type SetStaffStatusInput,
  type ListStaffStatusInput,
  type GetMyStaffStatusInput,
} from '../../hosStaffStatusService'
import {
  reportIncident,
  reviewIncident,
  addCapaItems,
  completeCapaItem,
  recordEffectiveness,
  listIncidents,
  getIncident,
  setIncidentSettings,
  getIncidentSettings,
  type ReportIncidentInput,
  type ReviewIncidentInput,
  type AddCapaItemsInput,
  type CompleteCapaItemInput,
  type RecordEffectivenessInput,
  type ListIncidentsInput,
  type GetIncidentInput,
  type SetIncidentSettingsInput,
  type GetIncidentSettingsInput,
} from '../../hosIncidentsService'
import {
  addHospitalComplianceStarters,
  type AddHospitalComplianceStartersInput,
} from '../../hosComplianceService'
import {
  CON_REPORT_KINDS,
  type ConReportKind,
  type GetConReportInput,
  type GetConStarterStatusInput,
  type GetPlantDowntimeReportInput,
  type GetPlantStarterStatusInput,
  type SetPlantNearMissExportPeopleInput,
} from '../../../types/ops'
import type {
  ConIssueKind,
  ConResponsibleParty,
  ConIssueImpact,
  ConSelectionOption,
  CreateConIssueInput,
  UpdateConIssueInput,
  AttachConIssuePhotosInput,
  RespondConIssueInput,
  WithdrawConIssueInput,
  CloseConIssueInput,
  DecideConIssueInput,
  SendConIssueForApprovalInput,
  GetConIssueInput,
  GetConIssuePhotoUrlsInput,
  ListConIssuesInput,
  ListConIssueDecisionsInput,
  ConPartyType,
  ConMeasurementState,
  ConInspectionRef,
  ConSourceLink,
  UpsertConPartyInput,
  ListConPartiesInput,
  SaveConMeasurementInput,
  SubmitConMeasurementInput,
  AcceptConMeasurementInput,
  DisputeConMeasurementInput,
  CorrectConMeasurementInput,
  CancelConMeasurementInput,
  GetConMeasurementInput,
  ListConMeasurementsInput,
  ListDisputedConMeasurementsInput,
  GetConMeasureTotalsInput,
  ConProjectStatus,
  ConPackage,
  ConApprovers,
  ConDrawingDiscipline,
  CreateConProjectInput,
  UpdateConProjectInput,
  SetConProjectPackagesInput,
  SetConProjectApproversInput,
  CreateConDrawingInput,
  IssueConDrawingRevisionInput,
  WithdrawConDrawingInput,
  ConDprShift,
  ConDiaryKind,
  ConDprLine,
  SaveDprDraftInput,
  SubmitDprInput,
  ReviewDprInput,
  ReturnDprInput,
  ApproveDprInput,
  AddDiaryEntryInput,
  SaveConPourCardInput,
  RequestConInspectionInput,
  RecordConInspectionOutcomeInput,
  ReassignConInspectorInput,
  CreateConTestInput,
  AddConTestResultInput,
  ConPourCardKind,
  ConInspectionOutcome,
  ConTestType,
  ConPourChecklistItem,
  ConTestSpec,
  SetBedStateInput,
  ListBedEventsInput,
  OpsBedActionType,
  OpsAssetServiceVisitKind,
} from '../../../types/ops'
import {
  CON_PROJECT_STATUSES,
  CON_DRAWING_DISCIPLINES,
  CON_DPR_SHIFTS,
  CON_DIARY_KINDS,
} from '../../../types/ops'
import type {
  EvidenceSourceCollection,
  CreateOutsideApprovalApproverInput,
  OutsideApprovalSubjectType,
  OutsideApprovalMode,
  CreatePlaceInput,
  CreateJobSiteInput,
  UpdateJobSiteInput,
  JobSiteAddress,
  JobSiteGeo,
  RenamePlaceInput,
  SetPlaceAccessInput,
  SetPlaceDutyChainInput,
  MovePlaceInput,
  ArchivePlaceInput,
  BulkCreatePlacesInput,
  BulkPlaceInputItem,
  CreatePlaceTokenInput,
  RevokePlaceTokenInput,
  ResolvePlaceTokenInput,
  CreatePartyInput,
  UpdatePartyInput,
  SetPartyMembersInput,
  CreateWorkerInput,
  UpdateWorkerInput,
  ArchiveWorkerInput,
  AddGangInput,
  SetSectorRolesInput,
  SaveTemplateDraftInput,
  SubmitTemplateVersionInput,
  ApproveTemplateVersionInput,
  SubmitRecordInput,
  ReviewRecordInput,
  ReturnRecordInput,
  ApproveRecordInput,
  AddCorrectionInput,
  CreateDutyRoleInput,
  UpdateDutyRoleInput,
  CreateShiftInput,
  UpdateShiftInput,
  CreateCoverageRuleInput,
  UpdateCoverageRuleInput,
  CreateRosterInput,
  SaveDraftAssignmentsInput,
  PublishRosterInput,
  PreviewRosterWarningsInput,
  AcknowledgeRosterInput,
  RequestRosterChangeInput,
  RespondRosterChangeInput,
  DecideRosterChangeInput,
  CancelRosterChangeInput,
  MarkAttendanceRollInput,
  RequestAttendanceCorrectionInput,
  DecideAttendanceCorrectionInput,
  ImportAttendanceCsvInput,
  AssignReliefInput,
  CreateScheduleInput,
  UpdateScheduleInput,
  PauseScheduleInput,
  ResumeScheduleInput,
  ArchiveScheduleInput,
  SkipDueInput,
  RecordCheckpointScanInput,
  OpsRequestSector,
  SetPlacePresetInput,
  SetPropertyRoomTypesInput,
  SetRoomTypeInput,
  StaysRoomType,
  OpsSector,
  OpsComplianceCategory,
  OpsPlaceLevel,
  OpsPartyKind,
  OpsWorkerStatus,
  OpsSectorRole,
  OpsTemplateField,
  SitesPreset,
  OpsAssetCriticality,
  OpsAssetStatus,
  ImportOpsCsvInput,
  ExportOpsCsvInput,
  OpsImportEntity,
  OpsEngagementModel,
  OpsEngagementStatus,
  OpsMilestoneStatus,
  OpsAllocationKind,
  OpsWagePersonRef,
  OpsRoomSource,
  CheckOutRoomInput,
  MarkRoomOccupiedInput,
  SetRoomDueOutInput,
  StartRoomCleanInput,
  MarkRoomCleanedInput,
  InspectRoomInput,
  SetRoomServiceInput,
  SetRoomTagsInput,
  SetPropertyMinibarCheckInput,
  CreateDepartmentInput,
  RenameDepartmentInput,
  ArchiveDepartmentInput,
  OpsBookingPlanPolicy,
  CreateBookableInput,
  UpdateBookableInput,
  OpsCustomerCallOutcome,
  SubmitSharedDeviceQueuedInput,
  IssueSharedDeviceUploadUrlInput,
  FinalizeSharedDeviceUploadInput,
  LostFoundCategory,
  LostFoundDisposalMethod,
  LostFoundReturnMode,
  LostFoundDeskVerdict,
} from '../../../types/ops'
import {
  OPS_SECTOR_KEYS,
  SITES_PRESETS,
  PMS_ADAPTER_TYPES,
  type PmsAdapterType,
  type ReducedStayflexiRow,
  OPS_CUSTOMER_CALL_OUTCOMES,
  LF_CATEGORIES,
  DISPOSAL_METHODS,
  LF_RETURN_MODES,
  LF_DESK_VERDICTS,
  type OpsPhoneItemKey,
} from '../../../types/ops'
import type { WorkspaceRole } from '../../../types/workspace'

const OPS_SECTOR_ENUM = [...OPS_SECTOR_KEYS] as unknown as [OpsSector, ...OpsSector[]]
const OPS_DUTY_ROLE_SECTOR_ENUM = [...OPS_SECTOR_KEYS, 'core'] as unknown as [
  OpsSector | 'core',
  ...(OpsSector | 'core')[],
]
const SITES_PRESET_ENUM = [...SITES_PRESETS] as unknown as [SitesPreset, ...SitesPreset[]]
const PMS_ADAPTER_ENUM: [PmsAdapterType, ...PmsAdapterType[]] = [
  ...PMS_ADAPTER_TYPES,
]
const LF_CATEGORY_ENUM: [LostFoundCategory, ...LostFoundCategory[]] = [
  ...LF_CATEGORIES,
]
const LF_DISPOSAL_METHOD_ENUM: [LostFoundDisposalMethod, ...LostFoundDisposalMethod[]] = [
  ...DISPOSAL_METHODS,
]
const LF_RETURN_MODE_ENUM: [LostFoundReturnMode, ...LostFoundReturnMode[]] = [
  ...LF_RETURN_MODES,
]
const LF_DESK_VERDICT_ENUM: [LostFoundDeskVerdict, ...LostFoundDeskVerdict[]] = [
  ...LF_DESK_VERDICTS,
]
const OPS_COMPLIANCE_CATEGORY_ENUM: [OpsComplianceCategory, ...OpsComplianceCategory[]] = [
  'LICENCE',
  'CERTIFICATE',
  'REGISTRATION',
  'INSPECTION',
  'INSURANCE',
  'OTHER',
]
const OPS_ASSET_CRITICALITY_ENUM: [OpsAssetCriticality, ...OpsAssetCriticality[]] = [
  'LOW',
  'MEDIUM',
  'HIGH',
  'CRITICAL',
]
const OPS_ASSET_STATUS_ENUM: [OpsAssetStatus, ...OpsAssetStatus[]] = [
  'AVAILABLE',
  'IN_USE',
  'ISOLATED',
  'MAINTENANCE',
  'RETIRED',
]
const OPS_ASSET_RELEASE_TO_STATUS_ENUM: [
  'AVAILABLE' | 'IN_USE',
  ...('AVAILABLE' | 'IN_USE')[],
] = ['AVAILABLE', 'IN_USE']

const OPS_ASSET_SERVICE_VISIT_KIND_ENUM: [
  OpsAssetServiceVisitKind,
  ...OpsAssetServiceVisitKind[],
] = ['PM', 'CALIBRATION', 'REPAIR', 'AMC_VISIT', 'INSTALL', 'OTHER']

const OPS_ENGAGEMENT_MODEL_ENUM: [
  OpsEngagementModel,
  ...OpsEngagementModel[],
] = ['FIXED', 'TIME_AND_MATERIALS', 'RETAINER']

const OPS_ENGAGEMENT_STATUS_ENUM: [
  OpsEngagementStatus,
  ...OpsEngagementStatus[],
] = ['ACTIVE', 'PAUSED', 'CLOSED']

const OPS_MILESTONE_STATUS_ENUM: [
  OpsMilestoneStatus,
  ...OpsMilestoneStatus[],
] = ['PLANNED', 'DONE', 'INVOICED', 'PAID']

const OPS_ALLOCATION_KIND_ENUM: [
  OpsAllocationKind,
  ...OpsAllocationKind[],
] = ['BILLED', 'SHADOW']

const OPS_CUSTOMER_CALL_OUTCOME_ENUM: [
  OpsCustomerCallOutcome,
  ...OpsCustomerCallOutcome[],
] = [...OPS_CUSTOMER_CALL_OUTCOMES] as unknown as [
  OpsCustomerCallOutcome,
  ...OpsCustomerCallOutcome[],
]
const OPS_ROOM_SOURCE_ENUM: [
  OpsRoomSource,
  ...OpsRoomSource[],
] = ['DESK', 'QR', 'CSV', 'BOARD']

export const OPS_CREATE_PLACE_ACTION_ID = 'ops.createPlace'
export const OPS_RENAME_PLACE_ACTION_ID = 'ops.renamePlace'
export const OPS_SET_PLACE_ACCESS_ACTION_ID = 'ops.setPlaceAccess'
export const OPS_SET_PLACE_DUTY_CHAIN_ACTION_ID = 'ops.setPlaceDutyChain'
export const OPS_SET_PLACE_PRESET_ACTION_ID = 'ops.setPlacePreset'
export const OPS_SET_PROPERTY_ROOM_TYPES_ACTION_ID = 'ops.setPropertyRoomTypes'
export const OPS_SET_ROOM_TYPE_ACTION_ID = 'ops.setRoomType'
export const OPS_CREATE_JOB_SITE_ACTION_ID = 'ops.createJobSite'
export const OPS_UPDATE_JOB_SITE_ACTION_ID = 'ops.updateJobSite'
export const OPS_LIST_CUSTOMER_CONTACTS_ACTION_ID = 'ops.listCustomerContacts'
export const OPS_MOVE_PLACE_ACTION_ID = 'ops.movePlace'
export const OPS_ARCHIVE_PLACE_ACTION_ID = 'ops.archivePlace'
export const OPS_BULK_CREATE_PLACES_ACTION_ID = 'ops.bulkCreatePlaces'
export const OPS_CREATE_PLACE_TOKEN_ACTION_ID = 'ops.createPlaceToken'
export const OPS_REVOKE_PLACE_TOKEN_ACTION_ID = 'ops.revokePlaceToken'
export const OPS_RESOLVE_PLACE_TOKEN_ACTION_ID = 'ops.resolvePlaceToken'

export const OPS_CREATE_PARTY_ACTION_ID = 'ops.createParty'
export const OPS_UPDATE_PARTY_ACTION_ID = 'ops.updateParty'
export const OPS_SET_PARTY_MEMBERS_ACTION_ID = 'ops.setPartyMembers'
export const OPS_CREATE_WORKER_ACTION_ID = 'ops.createWorker'
export const OPS_UPDATE_WORKER_ACTION_ID = 'ops.updateWorker'
export const OPS_ARCHIVE_WORKER_ACTION_ID = 'ops.archiveWorker'
export const OPS_ADD_GANG_ACTION_ID = 'ops.addGang'

export const OPS_SET_SECTOR_ROLES_ACTION_ID = 'ops.setSectorRoles'
export const OPS_SAVE_TEMPLATE_DRAFT_ACTION_ID = 'ops.saveTemplateDraft'
export const OPS_SUBMIT_TEMPLATE_VERSION_ACTION_ID = 'ops.submitTemplateVersion'
export const OPS_APPROVE_TEMPLATE_VERSION_ACTION_ID = 'ops.approveTemplateVersion'
export const OPS_SUBMIT_RECORD_ACTION_ID = 'ops.submitRecord'
export const OPS_REVIEW_RECORD_ACTION_ID = 'ops.reviewRecord'
export const OPS_RETURN_RECORD_ACTION_ID = 'ops.returnRecord'
export const OPS_APPROVE_RECORD_ACTION_ID = 'ops.approveRecord'
export const OPS_ADD_CORRECTION_ACTION_ID = 'ops.addCorrection'

export const OPS_CREATE_DUTY_ROLE_ACTION_ID = 'ops.createDutyRole'
export const OPS_UPDATE_DUTY_ROLE_ACTION_ID = 'ops.updateDutyRole'
export const OPS_ARCHIVE_DUTY_ROLE_ACTION_ID = 'ops.archiveDutyRole'
export const OPS_CREATE_DEPARTMENT_ACTION_ID = 'ops.createDepartment'
export const OPS_RENAME_DEPARTMENT_ACTION_ID = 'ops.renameDepartment'
export const OPS_ARCHIVE_DEPARTMENT_ACTION_ID = 'ops.archiveDepartment'
export const OPS_CREATE_SHIFT_ACTION_ID = 'ops.createShift'
export const OPS_UPDATE_SHIFT_ACTION_ID = 'ops.updateShift'
export const OPS_ARCHIVE_SHIFT_ACTION_ID = 'ops.archiveShift'
export const OPS_CREATE_COVERAGE_RULE_ACTION_ID = 'ops.createCoverageRule'
export const OPS_UPDATE_COVERAGE_RULE_ACTION_ID = 'ops.updateCoverageRule'
export const OPS_ARCHIVE_COVERAGE_RULE_ACTION_ID = 'ops.archiveCoverageRule'
export const OPS_CREATE_ROSTER_ACTION_ID = 'ops.createRoster'
export const OPS_SAVE_DRAFT_ASSIGNMENTS_ACTION_ID = 'ops.saveDraftAssignments'
export const OPS_PUBLISH_ROSTER_ACTION_ID = 'ops.publishRoster'
export const OPS_PREVIEW_ROSTER_WARNINGS_ACTION_ID = 'ops.previewRosterWarnings'
export const OPS_ACKNOWLEDGE_ROSTER_ACTION_ID = 'ops.acknowledgeRoster'
export const OPS_REQUEST_ROSTER_CHANGE_ACTION_ID = 'ops.requestRosterChange'
export const OPS_RESPOND_ROSTER_CHANGE_ACTION_ID = 'ops.respondRosterChange'
export const OPS_DECIDE_ROSTER_CHANGE_ACTION_ID = 'ops.decideRosterChange'
export const OPS_CANCEL_ROSTER_CHANGE_ACTION_ID = 'ops.cancelRosterChange'
export const OPS_MARK_ATTENDANCE_ROLL_ACTION_ID = 'ops.markAttendanceRoll'
export const OPS_REQUEST_ATTENDANCE_CORRECTION_ACTION_ID = 'ops.requestAttendanceCorrection'
export const OPS_DECIDE_ATTENDANCE_CORRECTION_ACTION_ID = 'ops.decideAttendanceCorrection'
export const OPS_IMPORT_ATTENDANCE_CSV_ACTION_ID = 'ops.importAttendanceCsv'
export const OPS_ASSIGN_RELIEF_ACTION_ID = 'ops.assignRelief'

export const OPS_CREATE_SCHEDULE_ACTION_ID = 'ops.createSchedule'
export const OPS_UPDATE_SCHEDULE_ACTION_ID = 'ops.updateSchedule'
export const OPS_PAUSE_SCHEDULE_ACTION_ID = 'ops.pauseSchedule'
export const OPS_RESUME_SCHEDULE_ACTION_ID = 'ops.resumeSchedule'
export const OPS_ARCHIVE_SCHEDULE_ACTION_ID = 'ops.archiveSchedule'
export const OPS_SKIP_DUE_ACTION_ID = 'ops.skipDue'
export const OPS_RECORD_CHECKPOINT_SCAN_ACTION_ID = 'ops.recordCheckpointScan'

export const OPS_UPSERT_REQUEST_KIND_ACTION_ID = 'ops.upsertRequestKind'
export const OPS_LIST_REQUEST_KINDS_ACTION_ID = 'ops.listRequestKinds'
export const OPS_RAISE_REQUEST_ACTION_ID = 'ops.raiseRequest'
export const OPS_ATTACH_REQUEST_PHOTOS_ACTION_ID = 'ops.attachRequestPhotos'
export const OPS_ACKNOWLEDGE_REQUEST_ACTION_ID = 'ops.acknowledgeRequest'
export const OPS_START_REQUEST_ACTION_ID = 'ops.startRequest'
export const OPS_ASSIGN_REQUEST_ACTION_ID = 'ops.assignRequest'
export const OPS_MARK_REQUEST_DONE_ACTION_ID = 'ops.markRequestDone'
export const OPS_VERIFY_REQUEST_ACTION_ID = 'ops.verifyRequest'
export const OPS_REOPEN_REQUEST_ACTION_ID = 'ops.reopenRequest'
export const OPS_CANCEL_REQUEST_ACTION_ID = 'ops.cancelRequest'
export const OPS_SCHEDULE_JOB_ACTION_ID = 'ops.scheduleJob'
export const OPS_RAISE_REVISIT_ACTION_ID = 'ops.raiseRevisit'
export const OPS_LIST_JOB_BOARD_ACTION_ID = 'ops.listJobBoard'

export const OPS_CREATE_COMPLIANCE_ITEM_ACTION_ID = 'ops.createComplianceItem'
export const OPS_UPDATE_COMPLIANCE_ITEM_ACTION_ID = 'ops.updateComplianceItem'
export const OPS_RENEW_COMPLIANCE_ITEM_ACTION_ID = 'ops.renewComplianceItem'
export const OPS_ATTACH_COMPLIANCE_EVIDENCE_ACTION_ID = 'ops.attachComplianceEvidence'
export const OPS_ARCHIVE_COMPLIANCE_ITEM_ACTION_ID = 'ops.archiveComplianceItem'

export const OPS_CREATE_ASSET_ACTION_ID = 'ops.createAsset'
export const OPS_UPDATE_ASSET_ACTION_ID = 'ops.updateAsset'
export const OPS_SET_ASSET_STATUS_ACTION_ID = 'ops.setAssetStatus'
export const OPS_ISOLATE_ASSET_ACTION_ID = 'ops.isolateAsset'
export const OPS_RELEASE_ASSET_ACTION_ID = 'ops.releaseAsset'
export const OPS_RETIRE_ASSET_ACTION_ID = 'ops.retireAsset'
export const OPS_RECORD_ASSET_SERVICE_VISIT_ACTION_ID = 'ops.recordAssetServiceVisit'

export const OPS_CREATE_ENGAGEMENT_ACTION_ID = 'ops.createEngagement'
export const OPS_UPDATE_ENGAGEMENT_ACTION_ID = 'ops.updateEngagement'
export const OPS_SET_ENGAGEMENT_STATUS_ACTION_ID = 'ops.setEngagementStatus'
export const OPS_SET_ENGAGEMENT_MILESTONES_ACTION_ID = 'ops.setEngagementMilestones'
export const OPS_SET_ENGAGEMENT_FINANCE_ACTION_ID = 'ops.setEngagementFinance'
export const OPS_UPSERT_ALLOCATION_ACTION_ID = 'ops.upsertAllocation'
export const OPS_END_ALLOCATION_ACTION_ID = 'ops.endAllocation'
export const OPS_LINK_GOAL_TO_ENGAGEMENT_ACTION_ID = 'ops.linkGoalToEngagement'
export const OPS_UNLINK_GOAL_FROM_ENGAGEMENT_ACTION_ID = 'ops.unlinkGoalFromEngagement'
export const OPS_RECOMPUTE_ENGAGEMENT_MARGIN_ACTION_ID = 'ops.recomputeEngagementMargin'
export const OPS_EXPORT_DELIVERY_CSV_ACTION_ID = 'ops.exportDeliveryCsv'
export const OPS_DEL_RAISE_CHANGE_REQUEST_ACTION_ID = 'del.raiseChangeRequest'
export const OPS_DEL_SEND_CLIENT_LINKS_ACTION_ID = 'del.sendClientLinks'
export const OPS_DEL_REVOKE_CLIENT_LINK_ACTION_ID = 'del.revokeClientLink'

export const OPS_CREATE_APPROVAL_ACTION_ID = 'ops.approval.create'
export const OPS_REISSUE_APPROVAL_LINK_ACTION_ID = 'ops.approval.reissue'
export const OPS_REVOKE_APPROVAL_ACTION_ID = 'ops.approval.revoke'
export const OPS_SET_APPROVAL_SETTINGS_ACTION_ID = 'ops.approval.setSettings'

export const OPS_IMPORT_CSV_ACTION_ID = 'ops.importCsv'
export const OPS_EXPORT_CSV_ACTION_ID = 'ops.exportCsv'

export const OPS_SET_OPS_WAGE_RATE_ACTION_ID = 'ops.setOpsWageRate'
export const OPS_PREVIEW_OPS_WAGE_SHEET_ACTION_ID = 'ops.previewOpsWageSheet'
export const OPS_LOCK_OPS_WAGE_SHEET_ACTION_ID = 'ops.lockOpsWageSheet'
export const OPS_UNLOCK_OPS_WAGE_SHEET_ACTION_ID = 'ops.unlockOpsWageSheet'
export const OPS_EXPORT_OPS_WAGE_SHEET_CSV_ACTION_ID = 'ops.exportOpsWageSheetCsv'
export const OPS_GET_OPS_WAGE_SHEET_STATUS_ACTION_ID = 'ops.getOpsWageSheetStatus'

export const OPS_REQUEST_EVIDENCE_EXPORT_ACTION_ID = 'ops.requestEvidenceExport'
export const OPS_GET_EVIDENCE_EXPORT_DOWNLOAD_ACTION_ID = 'ops.getEvidenceExportDownload'
export const OPS_SET_RETENTION_SETTINGS_ACTION_ID = 'ops.setRetentionSettings'
export const OPS_RECORD_OPS_RESTRICTED_VIEW_ACTION_ID = 'ops.recordOpsRestrictedView'
export const OPS_SET_OPS_PRIVACY_SETTINGS_ACTION_ID = 'ops.setOpsPrivacySettings'
export const OPS_SET_LEGAL_HOLD_ACTION_ID = 'ops.setLegalHold'
export const OPS_REQUEST_LEGAL_HOLD_LIFT_ACTION_ID = 'ops.requestLegalHoldLift'
export const OPS_CONFIRM_LEGAL_HOLD_LIFT_ACTION_ID = 'ops.confirmLegalHoldLift'
export const OPS_CANCEL_LEGAL_HOLD_LIFT_ACTION_ID = 'ops.cancelLegalHoldLift'
export const OPS_RESTORE_FROM_RETENTION_BIN_ACTION_ID = 'ops.restoreFromRetentionBin'
export const OPS_LIST_SEALED_ARCHIVES_ACTION_ID = 'ops.listSealedArchives'
export const OPS_REQUEST_SEALED_ARCHIVE_EXPORT_ACTION_ID = 'ops.requestSealedArchiveExport'
export const OPS_GET_RETENTION_LOCK_SUMMARY_ACTION_ID = 'ops.getRetentionLockSummary'

export const OPS_CHECK_OUT_ROOM_ACTION_ID = 'ops.checkOutRoom'
export const OPS_MARK_ROOM_OCCUPIED_ACTION_ID = 'ops.markRoomOccupied'
export const OPS_SET_ROOM_DUE_OUT_ACTION_ID = 'ops.setRoomDueOut'
export const OPS_START_ROOM_CLEAN_ACTION_ID = 'ops.startRoomClean'
export const OPS_MARK_ROOM_CLEANED_ACTION_ID = 'ops.markRoomCleaned'
export const OPS_INSPECT_ROOM_ACTION_ID = 'ops.inspectRoom'
export const OPS_SET_ROOM_SERVICE_ACTION_ID = 'ops.setRoomService'
export const OPS_SET_ROOM_TAGS_ACTION_ID = 'ops.setRoomTags'
export const OPS_SET_PROPERTY_MINIBAR_CHECK_ACTION_ID = 'ops.setPropertyMinibarCheck'
export const OPS_SET_PROPERTY_GUEST_REQUESTS_ENABLED_ACTION_ID = 'ops.setPropertyGuestRequestsEnabled'
export const OPS_CONFIRM_GUEST_EXTRA_REQUEST_ACTION_ID = 'ops.confirmGuestExtraRequest'
export const OPS_DECLINE_GUEST_EXTRA_REQUEST_ACTION_ID = 'ops.declineGuestExtraRequest'
export const OPS_PREPARE_ROOM_CLEAN_RECORD_ACTION_ID = 'ops.prepareRoomCleanRecord'
export const OPS_ASSIGN_ROOMS_ACTION_ID = 'ops.assignRooms'
export const OPS_UNASSIGN_ROOM_ACTION_ID = 'ops.unassignRoom'
export const OPS_REASSIGN_ROOM_ACTION_ID = 'ops.reassignRoom'
export const OPS_UPSERT_CUSTOMER_ACTION_ID = 'ops.upsertCustomer'
export const OPS_ARCHIVE_CUSTOMER_ACTION_ID = 'ops.archiveCustomer'
export const OPS_FIND_CUSTOMER_BY_PHONE_ACTION_ID = 'ops.findCustomerByPhone'
export const OPS_LINK_MY_CUSTOMER_RECORD_ACTION_ID = 'ops.linkMyCustomerRecord'

export const OPS_CREATE_BOOKING_PLAN_ACTION_ID = 'ops.createBookingPlan'
export const OPS_UPDATE_BOOKING_PLAN_ACTION_ID = 'ops.updateBookingPlan'
export const OPS_ARCHIVE_BOOKING_PLAN_ACTION_ID = 'ops.archiveBookingPlan'

export const OPS_GRANT_MEMBERSHIP_ACTION_ID = 'ops.grantMembership'
export const OPS_RENEW_MEMBERSHIP_ACTION_ID = 'ops.renewMembership'
export const OPS_FREEZE_MEMBERSHIP_ACTION_ID = 'ops.freezeMembership'
export const OPS_UNFREEZE_MEMBERSHIP_ACTION_ID = 'ops.unfreezeMembership'
export const OPS_CANCEL_MEMBERSHIP_ACTION_ID = 'ops.cancelMembership'
export const OPS_RECORD_DUE_PAID_ACTION_ID = 'ops.recordDuePaid'
export const OPS_WAIVE_DUE_ACTION_ID = 'ops.waiveDue'

export const OPS_CREATE_BOOKABLE_ACTION_ID = 'ops.createBookable'
export const OPS_UPDATE_BOOKABLE_ACTION_ID = 'ops.updateBookable'
export const OPS_ARCHIVE_BOOKABLE_ACTION_ID = 'ops.archiveBookable'

export const OPS_BOOK_SESSION_ACTION_ID = 'ops.bookSession'
export const OPS_BOOK_IN_HOUSE_EXTRA_ACTION_ID = 'ops.bookInHouseExtra'
export const OPS_CANCEL_BOOKING_ACTION_ID = 'ops.cancelBooking'
export const OPS_MOVE_BOOKING_ACTION_ID = 'ops.moveBooking'
export const OPS_LEAVE_WAITLIST_ACTION_ID = 'ops.leaveWaitlist'

export const OPS_WAIVE_STRIKE_ACTION_ID = 'ops.waiveStrike'
export const OPS_CLEAR_BOOKING_BLOCK_ACTION_ID = 'ops.clearBookingBlock'

export const OPS_CREATE_STAYS_PMS_CONNECTION_ACTION_ID = 'ops.createStaysPmsConnection'
export const OPS_ROTATE_STAYS_PMS_SECRET_ACTION_ID = 'ops.rotateStaysPmsSecret'
export const OPS_UPDATE_STAYS_PMS_CONNECTION_ACTION_ID = 'ops.updateStaysPmsConnection'
export const OPS_DELETE_STAYS_PMS_CONNECTION_ACTION_ID = 'ops.deleteStaysPmsConnection'
export const OPS_GET_STAYS_PMS_STATUS_ACTION_ID = 'ops.getStaysPmsStatus'
export const OPS_RETRY_STAYS_PMS_OUTBOX_ACTION_ID = 'ops.retryStaysPmsOutbox'
export const OPS_TEST_STAYS_PMS_EZEE_CONNECTION_ACTION_ID = 'ops.testStaysPmsEzeeConnection'
export const OPS_TEST_STAYS_PMS_HOTELOGIX_CONNECTION_ACTION_ID = 'ops.testStaysPmsHotelogixConnection'
export const OPS_IMPORT_STAYS_PMS_REPORT_ACTION_ID = 'ops.importStaysPmsReport'

export const OPS_CREATE_OPS_CONNECTOR_ACTION_ID = 'ops.createOpsConnector'
export const OPS_ROTATE_OPS_CONNECTOR_SECRET_ACTION_ID = 'ops.rotateOpsConnectorSecret'
export const OPS_UPDATE_OPS_CONNECTOR_ACTION_ID = 'ops.updateOpsConnector'
export const OPS_DELETE_OPS_CONNECTOR_ACTION_ID = 'ops.deleteOpsConnector'
export const OPS_GET_OPS_CONNECTOR_STATUS_ACTION_ID = 'ops.getOpsConnectorStatus'
export const OPS_REPLAY_OPS_CONNECTOR_EVENT_ACTION_ID = 'ops.replayOpsConnectorEvent'
export const OPS_ISSUE_QUEUE_TOKEN_ACTION_ID = 'ops.issueQueueToken'
export const OPS_CALL_NEXT_TOKEN_ACTION_ID = 'ops.callNextToken'
export const OPS_MARK_TOKEN_SERVED_ACTION_ID = 'ops.markTokenServed'
export const OPS_SKIP_TOKEN_ACTION_ID = 'ops.skipToken'
export const OPS_GET_QUEUE_BOARD_ACTION_ID = 'ops.getQueueBoard'
export const OPS_LIST_QUEUE_TOKENS_ACTION_ID = 'ops.listQueueTokens'

export const OPS_GET_MY_BOOKING_QR_ACTION_ID = 'ops.getMyBookingQr'
export const OPS_CHECK_IN_BY_CUSTOMER_QR_ACTION_ID = 'ops.checkInByCustomerQr'
export const OPS_BOOKING_CHECK_IN_BY_PLACE_QR_ACTION_ID = 'ops.bookingCheckInByPlaceQr'
export const OPS_PREPARE_BOOKING_CHECK_IN_OTP_ACTION_ID = 'ops.prepareBookingCheckInOtp'
export const OPS_CHECK_IN_BY_PHONE_OTP_ACTION_ID = 'ops.checkInByPhoneOtp'
export const OPS_MANUAL_CHECK_IN_ACTION_ID = 'ops.manualCheckIn'

export const OPS_SET_PUBLIC_BOOKING_LINK_ACTION_ID = 'ops.setPublicBookingLink'
export const OPS_GET_PUBLIC_BOOKING_INFO_ACTION_ID = 'ops.getPublicBookingInfo'
export const OPS_PUBLIC_BOOK_SESSION_ACTION_ID = 'ops.publicBookSession'
export const OPS_PUBLIC_CANCEL_BOOKING_ACTION_ID = 'ops.publicCancelBooking'
export const OPS_LIST_MY_PUBLIC_BOOKINGS_ACTION_ID = 'ops.listMyPublicBookings'

export const OPS_LIST_RENEWALS_DUE_ACTION_ID = 'ops.listRenewalsDue'
export const OPS_LIST_CHURN_CALLS_ACTION_ID = 'ops.listChurnCalls'
export const OPS_RECORD_CUSTOMER_CALL_ACTION_ID = 'ops.recordCustomerCall'

export const OPS_CREATE_DEVICE_ENROL_CODE_ACTION_ID = 'ops.createDeviceEnrolCode'
export const OPS_ENROL_SHARED_DEVICE_ACTION_ID = 'ops.enrolSharedDevice'
export const OPS_REVOKE_SHARED_DEVICE_ACTION_ID = 'ops.revokeSharedDevice'
export const OPS_SET_SHARED_DEVICE_SETTINGS_ACTION_ID = 'ops.setSharedDeviceSettings'
export const OPS_LIST_SHARED_DEVICES_ACTION_ID = 'ops.listSharedDevices'
export const OPS_LIST_SHARED_DEVICE_SESSIONS_ACTION_ID = 'ops.listSharedDeviceSessions'
export const OPS_SET_MY_SHARED_DEVICE_PIN_ACTION_ID = 'ops.setMySharedDevicePin'
export const OPS_SET_SHARED_DEVICE_PIN_BY_PHONE_ACTION_ID = 'ops.setSharedDevicePinByPhone'
export const OPS_RESET_SHARED_DEVICE_PIN_ACTION_ID = 'ops.resetSharedDevicePin'
export const OPS_LIST_SHARED_DEVICE_PEOPLE_ACTION_ID = 'ops.listSharedDevicePeople'
export const OPS_SHARED_DEVICE_SWITCH_IN_ACTION_ID = 'ops.sharedDeviceSwitchIn'
export const OPS_SHARED_DEVICE_SWITCH_OUT_ACTION_ID = 'ops.sharedDeviceSwitchOut'
export const OPS_SUBMIT_SHARED_DEVICE_QUEUED_ACTION_ID = 'ops.submitSharedDeviceQueued'
export const OPS_ISSUE_SHARED_DEVICE_UPLOAD_URL_ACTION_ID = 'ops.issueSharedDeviceUploadUrl'
export const OPS_FINALIZE_SHARED_DEVICE_UPLOAD_ACTION_ID = 'ops.finalizeSharedDeviceUpload'
export const OPS_LOG_FOUND_ITEM_ACTION_ID = 'ops.logFoundItem'
export const OPS_RECORD_FOUND_ITEM_PHOTO_ACTION_ID = 'ops.recordFoundItemPhoto'
export const OPS_MOVE_FOUND_ITEM_BIN_ACTION_ID = 'ops.moveFoundItemBin'
export const OPS_START_LOST_FOUND_CLAIM_ACTION_ID = 'ops.startLostFoundClaim'
export const OPS_REISSUE_LOST_FOUND_CLAIM_LINK_ACTION_ID = 'ops.reissueLostFoundClaimLink'
export const OPS_CANCEL_LOST_FOUND_CLAIM_ACTION_ID = 'ops.cancelLostFoundClaim'
export const OPS_VERIFY_LOST_FOUND_CLAIM_ACTION_ID = 'ops.verifyLostFoundClaim'
export const OPS_COMPLETE_LOST_FOUND_RETURN_ACTION_ID = 'ops.completeLostFoundReturn'
export const OPS_PROPOSE_LOST_FOUND_DISPOSAL_ACTION_ID = 'ops.proposeLostFoundDisposal'
export const OPS_SIGN_LOST_FOUND_DISPOSAL_ACTION_ID = 'ops.signLostFoundDisposal'
export const OPS_WITHDRAW_LOST_FOUND_DISPOSAL_ACTION_ID = 'ops.withdrawLostFoundDisposal'
export const OPS_SET_LOST_FOUND_SETTINGS_ACTION_ID = 'ops.setLostFoundSettings'
export const OPS_GET_LOST_FOUND_CLAIMANT_ACTION_ID = 'ops.getLostFoundClaimant'
export const OPS_SET_OPS_PHONE_ROLE_ACCESS_ACTION_ID = 'ops.setOpsPhoneRoleAccess'
export const OPS_APPLY_OPS_PHONE_RECOMMENDED_SET_ACTION_ID = 'ops.applyOpsPhoneRecommendedSet'
export const OPS_SET_OPS_PHONE_PERSON_ACCESS_ACTION_ID = 'ops.setOpsPhonePersonAccess'
export const OPS_GET_MY_OPS_PHONE_ITEMS_ACTION_ID = 'ops.getMyOpsPhoneItems'

export const OPS_CREATE_CON_PROJECT_ACTION_ID = 'ops.createConProject'
export const OPS_UPDATE_CON_PROJECT_ACTION_ID = 'ops.updateConProject'
export const OPS_SET_CON_PROJECT_PACKAGES_ACTION_ID = 'ops.setConProjectPackages'
export const OPS_SET_CON_PROJECT_APPROVERS_ACTION_ID = 'ops.setConProjectApprovers'
export const OPS_CREATE_CON_DRAWING_ACTION_ID = 'ops.createConDrawing'
export const OPS_ISSUE_CON_DRAWING_REVISION_ACTION_ID = 'ops.issueConDrawingRevision'
export const OPS_WITHDRAW_CON_DRAWING_ACTION_ID = 'ops.withdrawConDrawing'
export const OPS_SAVE_DPR_DRAFT_ACTION_ID = 'ops.saveDprDraft'
export const OPS_SUBMIT_DPR_ACTION_ID = 'ops.submitDpr'
export const OPS_REVIEW_DPR_ACTION_ID = 'ops.reviewDpr'
export const OPS_RETURN_DPR_ACTION_ID = 'ops.returnDpr'
export const OPS_APPROVE_DPR_ACTION_ID = 'ops.approveDpr'
export const OPS_ADD_DIARY_ENTRY_ACTION_ID = 'ops.addDiaryEntry'
export const OPS_SAVE_CON_POUR_CARD_ACTION_ID = 'ops.saveConPourCard'
export const OPS_REQUEST_CON_INSPECTION_ACTION_ID = 'ops.requestConInspection'
export const OPS_RECORD_CON_INSPECTION_OUTCOME_ACTION_ID = 'ops.recordConInspectionOutcome'
export const OPS_REASSIGN_CON_INSPECTOR_ACTION_ID = 'ops.reassignConInspector'
export const OPS_CREATE_CON_TEST_ACTION_ID = 'ops.createConTest'
export const OPS_ADD_CON_TEST_RESULT_ACTION_ID = 'ops.addConTestResult'
export const OPS_CREATE_CON_ISSUE_ACTION_ID = 'ops.createConIssue'
export const OPS_UPDATE_CON_ISSUE_ACTION_ID = 'ops.updateConIssue'
export const OPS_ATTACH_CON_ISSUE_PHOTOS_ACTION_ID = 'ops.attachConIssuePhotos'
export const OPS_RESPOND_CON_ISSUE_ACTION_ID = 'ops.respondConIssue'
export const OPS_WITHDRAW_CON_ISSUE_ACTION_ID = 'ops.withdrawConIssue'
export const OPS_CLOSE_CON_ISSUE_ACTION_ID = 'ops.closeConIssue'
export const OPS_DECIDE_CON_ISSUE_ACTION_ID = 'ops.decideConIssue'
export const OPS_SEND_CON_ISSUE_FOR_APPROVAL_ACTION_ID = 'ops.sendConIssueForApproval'
export const OPS_GET_CON_ISSUE_ACTION_ID = 'ops.getConIssue'
export const OPS_GET_CON_ISSUE_PHOTO_URLS_ACTION_ID = 'ops.getConIssuePhotoUrls'
export const OPS_LIST_CON_ISSUES_ACTION_ID = 'ops.listConIssues'
export const OPS_LIST_CON_ISSUE_DECISIONS_ACTION_ID = 'ops.listConIssueDecisions'
export const OPS_UPSERT_CON_PARTY_ACTION_ID = 'ops.upsertConParty'
export const OPS_LIST_CON_PARTIES_ACTION_ID = 'ops.listConParties'
export const OPS_SAVE_CON_MEASUREMENT_ACTION_ID = 'ops.saveConMeasurement'
export const OPS_SUBMIT_CON_MEASUREMENT_ACTION_ID = 'ops.submitConMeasurement'
export const OPS_ACCEPT_CON_MEASUREMENT_ACTION_ID = 'ops.acceptConMeasurement'
export const OPS_DISPUTE_CON_MEASUREMENT_ACTION_ID = 'ops.disputeConMeasurement'
export const OPS_CORRECT_CON_MEASUREMENT_ACTION_ID = 'ops.correctConMeasurement'
export const OPS_CANCEL_CON_MEASUREMENT_ACTION_ID = 'ops.cancelConMeasurement'
export const OPS_GET_CON_MEASUREMENT_ACTION_ID = 'ops.getConMeasurement'
export const OPS_LIST_CON_MEASUREMENTS_ACTION_ID = 'ops.listConMeasurements'
export const OPS_LIST_DISPUTED_CON_MEASUREMENTS_ACTION_ID = 'ops.listDisputedConMeasurements'
export const OPS_GET_CON_MEASURE_TOTALS_ACTION_ID = 'ops.getConMeasureTotals'
export const OPS_GET_CON_REPORT_ACTION_ID = 'ops.getConReport'
export const OPS_GET_PLANT_DOWNTIME_REPORT_ACTION_ID = 'ops.getPlantDowntimeReport'
export const OPS_GET_PLANT_STARTER_STATUS_ACTION_ID = 'ops.getPlantStarterStatus'
export const OPS_SET_PLANT_NEAR_MISS_EXPORT_PEOPLE_ACTION_ID = 'ops.setPlantNearMissExportPeople'
export const OPS_GET_CON_STARTER_STATUS_ACTION_ID = 'ops.getConStarterStatus'

export const OPS_SET_BED_STATE_ACTION_ID = 'ops.setBedState'
export const OPS_LIST_BED_EVENTS_ACTION_ID = 'ops.listBedEvents'

export const OPS_ACKNOWLEDGE_ON_CALL_ACTION_ID = 'ops.acknowledgeOnCall'
export const OPS_GET_ON_CALL_BOARD_ACTION_ID = 'ops.getOnCallBoard'
export const OPS_LIST_MY_ON_CALL_SLOTS_ACTION_ID = 'ops.listMyOnCallSlots'
export const OPS_GET_HOS_STARTER_STATUS_ACTION_ID = 'ops.getHosStarterStatus'
export const OPS_SET_BMW_SETTINGS_ACTION_ID = 'ops.setBmwSettings'
export const OPS_SAVE_BMW_DAY_ACTION_ID = 'ops.saveBmwDay'
export const OPS_SUBMIT_BMW_DAY_ACTION_ID = 'ops.submitBmwDay'
export const OPS_CORRECT_BMW_DAY_ACTION_ID = 'ops.correctBmwDay'
export const OPS_RECORD_BMW_PICKUP_ACTION_ID = 'ops.recordBmwPickup'
export const OPS_GET_BMW_STATUS_ACTION_ID = 'ops.getBmwStatus'
export const OPS_GET_BMW_SUMMARY_ACTION_ID = 'ops.getBmwSummary'
export const OPS_START_BMW_HANDOVER_ACTION_ID = 'ops.startBmwHandover'
export const OPS_ATTACH_BMW_PICKUP_RECEIPT_ACTION_ID = 'ops.attachBmwPickupReceipt'
export const OPS_ENSURE_CLINICS_STARTER_SET_ACTION_ID = 'ops.ensureClinicsStarterSet'
export const OPS_IMPORT_TEMP_LOG_ACTION_ID = 'ops.importTempLog'
export const OPS_SET_STAFF_STATUS_ACTION_ID = 'ops.setStaffStatus'
export const OPS_LIST_STAFF_STATUS_ACTION_ID = 'ops.listStaffStatus'
export const OPS_GET_MY_STAFF_STATUS_ACTION_ID = 'ops.getMyStaffStatus'
export const OPS_REPORT_INCIDENT_ACTION_ID = 'ops.reportIncident'
export const OPS_REVIEW_INCIDENT_ACTION_ID = 'ops.reviewIncident'
export const OPS_ADD_CAPA_ITEMS_ACTION_ID = 'ops.addCapaItems'
export const OPS_COMPLETE_CAPA_ITEM_ACTION_ID = 'ops.completeCapaItem'
export const OPS_RECORD_EFFECTIVENESS_ACTION_ID = 'ops.recordEffectiveness'
export const OPS_LIST_INCIDENTS_ACTION_ID = 'ops.listIncidents'
export const OPS_GET_INCIDENT_ACTION_ID = 'ops.getIncident'
export const OPS_SET_INCIDENT_SETTINGS_ACTION_ID = 'ops.setIncidentSettings'
export const OPS_GET_INCIDENT_SETTINGS_ACTION_ID = 'ops.getIncidentSettings'
export const OPS_ADD_HOSPITAL_COMPLIANCE_STARTERS_ACTION_ID = 'ops.addHospitalComplianceStarters'

export const OPS_SET_OWNER_FEED_ACTION_ID = 'ops.setOwnerFeed'
export const OPS_REMOVE_FEED_OWNER_ACTION_ID = 'ops.removeFeedOwner'
export const OPS_REISSUE_OWNER_FEED_LINK_ACTION_ID = 'ops.reissueOwnerFeedLink'
export const OPS_GET_OWNER_FEED_ACTION_ID = 'ops.getOwnerFeed'
export const OPS_LIST_OWNER_DIGESTS_ACTION_ID = 'ops.listOwnerDigests'
export const OPS_SEND_OWNER_DIGEST_NOW_ACTION_ID = 'ops.sendOwnerDigestNow'

export const OPS_GET_STAYS_GROUP_VIEW_ACTION_ID = 'ops.getStaysGroupView'
export const OPS_PUSH_STAYS_GROUP_TEMPLATE_ACTION_ID = 'ops.pushStaysGroupTemplate'

const CON_PROJECT_STATUS_ENUM = [...CON_PROJECT_STATUSES] as unknown as [
  ConProjectStatus,
  ...ConProjectStatus[],
]
const CON_DRAWING_DISCIPLINE_ENUM = [...CON_DRAWING_DISCIPLINES] as unknown as [
  ConDrawingDiscipline,
  ...ConDrawingDiscipline[],
]
const CON_DPR_SHIFT_ENUM = [...CON_DPR_SHIFTS] as unknown as [
  ConDprShift,
  ...ConDprShift[],
]
const CON_DIARY_KIND_ENUM = [...CON_DIARY_KINDS] as unknown as [
  ConDiaryKind,
  ...ConDiaryKind[],
]

const OPS_IMPORT_ENTITY_ENUM: [OpsImportEntity, ...OpsImportEntity[]] = [
  'PLACES',
  'ASSETS',
  'STAFF',
  'WORKERS',
  'PARTIES',
  'STOCK_ITEMS',
  'DEPARTURES',
]

const OPS_APPROVAL_SUBJECT_TYPE_ENUM: [OutsideApprovalSubjectType, ...OutsideApprovalSubjectType[]] = [
  'REQUEST',
  'RECORD',
]
const OPS_APPROVAL_MODE_ENUM: [OutsideApprovalMode, ...OutsideApprovalMode[]] = [
  'ALL',
  'ANY',
]

const opsAdminPermission = (context: { actor: { role?: unknown } }): boolean =>
  isWorkspaceRole(context.actor.role) ? roleCanPerform(context.actor.role, 'ADMIN') : false

const opsOwnerPermission = (context: { actor: { role?: unknown } }): boolean =>
  isWorkspaceRole(context.actor.role) ? roleCanPerform(context.actor.role, 'OWNER') : false

const opsEditorPermission = (context: { actor: { role?: unknown } }): boolean =>
  isWorkspaceRole(context.actor.role) ? roleCanPerform(context.actor.role, 'EDITOR') : false

const opsMemberOrHelperPermission = (context: { actor: { role?: unknown } }): boolean => {
  if (context.actor.role === 'HELPER') return true
  return isWorkspaceRole(context.actor.role)
    ? roleCanPerform(context.actor.role, 'VIEWER')
    : false
}

const opsPublicPermission = (): boolean => true

const formatOpsError = (err: unknown): string =>
  err instanceof Error ? err.message : String(err)

/* =======================================================================
   Args Types & JSON Schemas
   ======================================================================= */

export interface OpsCreatePlaceArgs {
  sector: OpsSector
  parentId?: string | null
  level: OpsPlaceLevel
  name: string
  code: string
  preset?: SitesPreset | null
  ownerRoleKey?: string | null
}

const createPlaceArgsSchema: JSONSchemaType<OpsCreatePlaceArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    parentId: { type: 'string', nullable: true },
    level: {
      type: 'string',
      enum: [
        'SITE',
        'BUILDING',
        'FLOOR',
        'ROOM',
        'CHAINAGE',
        'FACILITY',
        'DEPARTMENT',
        'UNIT',
        'WARD',
        'BED',
        'CLIENT',
        'POST',
        'AREA',
        'CHECKPOINT',
      ],
    },
    name: { type: 'string', minLength: 1, maxLength: 240 },
    code: { type: 'string', minLength: 1, maxLength: 80 },
    preset: { type: 'string', enum: SITES_PRESET_ENUM, nullable: true },
    ownerRoleKey: { type: 'string', nullable: true },
  },
  required: ['sector', 'level', 'name', 'code'],
  additionalProperties: false,
}

export interface OpsRenamePlaceArgs {
  placeId: string
  name?: string
  code?: string
  opId?: string
}

const renamePlaceArgsSchema: JSONSchemaType<OpsRenamePlaceArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    name: { type: 'string', minLength: 1, maxLength: 240, nullable: true },
    code: { type: 'string', minLength: 1, maxLength: 80, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsSetPlaceAccessArgs {
  placeId: string
  accessList?: string[] | null
  opId?: string
}

const setPlaceAccessArgsSchema: JSONSchemaType<OpsSetPlaceAccessArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    accessList: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      nullable: true,
    },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsSetPlaceDutyChainArgs {
  placeId: string
  dutyChain?: {
    inChargeRoleKey: string | null
    backupRoleKey: string | null
    onCallRoleKey: string | null
  } | null
  departmentKey?: string | null
  ownerRoleKey?: string | null
}

const setPlaceDutyChainArgsSchema: JSONSchemaType<OpsSetPlaceDutyChainArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    dutyChain: {
      type: 'object',
      properties: {
        inChargeRoleKey: { type: 'string', nullable: true },
        backupRoleKey: { type: 'string', nullable: true },
        onCallRoleKey: { type: 'string', nullable: true },
      },
      required: [],
      nullable: true,
      additionalProperties: false,
    },
    departmentKey: { type: 'string', nullable: true },
    ownerRoleKey: { type: 'string', nullable: true },
  },
  required: ['placeId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetPlaceDutyChainArgs>

export interface OpsSetPlacePresetArgs {
  placeId: string
  preset?: SitesPreset | null
  opId?: string
}

const setPlacePresetArgsSchema: JSONSchemaType<OpsSetPlacePresetArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    preset: { type: 'string', enum: SITES_PRESET_ENUM, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsSetPropertyRoomTypesArgs {
  propertyId: string
  roomTypes: StaysRoomType[]
  opId: string
}

const setPropertyRoomTypesArgsSchema: JSONSchemaType<OpsSetPropertyRoomTypesArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1 },
    roomTypes: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string', minLength: 1 },
          name: { type: 'string', minLength: 1 },
          requiredPhotos: {
            type: 'array',
            items: { type: 'string' },
          },
          standardCleanMinutes: { type: 'integer' },
        },
        required: ['id', 'name', 'requiredPhotos', 'standardCleanMinutes'],
        additionalProperties: false,
      },
    },
    opId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['propertyId', 'roomTypes', 'opId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetPropertyRoomTypesArgs>

export interface OpsSetRoomTypeArgs {
  placeIds: string[]
  roomTypeId: string
  opId: string
}

const setRoomTypeArgsSchema: JSONSchemaType<OpsSetRoomTypeArgs> = {
  type: 'object',
  properties: {
    placeIds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      minItems: 1,
    },
    roomTypeId: { type: 'string', minLength: 1, maxLength: 60 },
    opId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['placeIds', 'roomTypeId', 'opId'],
  additionalProperties: false,
}

export interface OpsCreateJobSiteArgs {
  customerId: string
  parentAreaId?: string | null
  name: string
  address: JobSiteAddress
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  opId: string
}

const createJobSiteArgsSchema: JSONSchemaType<OpsCreateJobSiteArgs> = {
  type: 'object',
  properties: {
    customerId: { type: 'string', minLength: 1 },
    parentAreaId: { type: 'string', nullable: true },
    name: { type: 'string', minLength: 1, maxLength: 80 },
    address: {
      type: 'object',
      properties: {
        line1: { type: 'string', minLength: 1, maxLength: 120 },
        line2: { type: 'string', nullable: true, maxLength: 120 },
        locality: { type: 'string', nullable: true, maxLength: 60 },
        city: { type: 'string', minLength: 1, maxLength: 60 },
        region: { type: 'string', nullable: true, maxLength: 60 },
        postcode: { type: 'string', nullable: true, maxLength: 12 },
      },
      required: ['line1', 'city'],
      additionalProperties: false,
    },
    geo: {
      type: 'object',
      nullable: true,
      properties: {
        lat: { type: 'number' },
        lng: { type: 'number' },
      },
      required: ['lat', 'lng'],
      additionalProperties: false,
    },
    accessNotes: { type: 'string', nullable: true, maxLength: 300 },
    opId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['customerId', 'name', 'address', 'opId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCreateJobSiteArgs>

export interface OpsUpdateJobSiteArgs {
  placeId: string
  name?: string
  address?: JobSiteAddress
  geo?: JobSiteGeo | null
  accessNotes?: string | null
  opId: string
}

const updateJobSiteArgsSchema: JSONSchemaType<OpsUpdateJobSiteArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    name: { type: 'string', nullable: true, minLength: 1, maxLength: 80 },
    address: {
      type: 'object',
      nullable: true,
      properties: {
        line1: { type: 'string', minLength: 1, maxLength: 120 },
        line2: { type: 'string', nullable: true, maxLength: 120 },
        locality: { type: 'string', nullable: true, maxLength: 60 },
        city: { type: 'string', minLength: 1, maxLength: 60 },
        region: { type: 'string', nullable: true, maxLength: 60 },
        postcode: { type: 'string', nullable: true, maxLength: 12 },
      },
      required: ['line1', 'city'],
      additionalProperties: false,
    },
    geo: {
      type: 'object',
      nullable: true,
      properties: {
        lat: { type: 'number' },
        lng: { type: 'number' },
      },
      required: ['lat', 'lng'],
      additionalProperties: false,
    },
    accessNotes: { type: 'string', nullable: true, maxLength: 300 },
    opId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['placeId', 'opId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsUpdateJobSiteArgs>

export interface OpsListCustomerContactsArgs {
  customerIds: string[]
}

const listCustomerContactsArgsSchema: JSONSchemaType<OpsListCustomerContactsArgs> = {
  type: 'object',
  properties: {
    customerIds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      minItems: 1,
      maxItems: 50,
    },
  },
  required: ['customerIds'],
  additionalProperties: false,
}

export interface OpsMovePlaceArgs {
  placeId: string
  newParentId?: string | null
}

const movePlaceArgsSchema: JSONSchemaType<OpsMovePlaceArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    newParentId: { type: 'string', nullable: true },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsArchivePlaceArgs {
  placeId: string
  archived?: boolean
}

const archivePlaceArgsSchema: JSONSchemaType<OpsArchivePlaceArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    archived: { type: 'boolean', nullable: true },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsBulkCreatePlacesArgs {
  sector: OpsSector
  outline?: string
  items?: BulkPlaceInputItem[]
  parentId?: string | null
  preset?: SitesPreset | null
  opId: string
}

const bulkCreatePlacesArgsSchema: JSONSchemaType<OpsBulkCreatePlacesArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    outline: { type: 'string', minLength: 1, nullable: true },
    items: { type: 'array', items: { type: 'object' }, nullable: true } as unknown as Record<string, unknown>,
    parentId: { type: 'string', nullable: true },
    preset: { type: 'string', enum: SITES_PRESET_ENUM, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['sector', 'opId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsBulkCreatePlacesArgs>

export interface OpsCreatePlaceTokenArgs {
  placeId: string
}

const createPlaceTokenArgsSchema: JSONSchemaType<OpsCreatePlaceTokenArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsRevokePlaceTokenArgs {
  placeId: string
  token?: string
}

const revokePlaceTokenArgsSchema: JSONSchemaType<OpsRevokePlaceTokenArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    token: { type: 'string', nullable: true },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsResolvePlaceTokenArgs {
  token: string
}

const resolvePlaceTokenArgsSchema: JSONSchemaType<OpsResolvePlaceTokenArgs> = {
  type: 'object',
  properties: {
    token: { type: 'string', minLength: 1 },
  },
  required: ['token'],
  additionalProperties: false,
}

export interface OpsCreatePartyArgs {
  sector: OpsSector
  kind: OpsPartyKind
  name: string
  externalRef?: string
  opId?: string
}

const createPartyArgsSchema: JSONSchemaType<OpsCreatePartyArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    kind: { type: 'string', enum: ['CONTRACTOR', 'VENDOR', 'CLIENT', 'CONSULTANT'] },
    name: { type: 'string', minLength: 1, maxLength: 240 },
    externalRef: { type: 'string', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['sector', 'kind', 'name'],
  additionalProperties: false,
}

export interface OpsUpdatePartyArgs {
  partyId: string
  kind?: OpsPartyKind
  name?: string
  externalRef?: string
  archived?: boolean
}

const updatePartyArgsSchema: JSONSchemaType<OpsUpdatePartyArgs> = {
  type: 'object',
  properties: {
    partyId: { type: 'string', minLength: 1 },
    kind: {
      type: 'string',
      enum: ['CONTRACTOR', 'VENDOR', 'CLIENT', 'CONSULTANT'],
      nullable: true,
    },
    name: { type: 'string', minLength: 1, maxLength: 240, nullable: true },
    externalRef: { type: 'string', nullable: true },
    archived: { type: 'boolean', nullable: true },
  },
  required: ['partyId'],
  additionalProperties: false,
}

export interface OpsSetPartyMembersArgs {
  partyId: string
  memberUids: string[]
}

const setPartyMembersArgsSchema: JSONSchemaType<OpsSetPartyMembersArgs> = {
  type: 'object',
  properties: {
    partyId: { type: 'string', minLength: 1 },
    memberUids: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
    },
  },
  required: ['partyId', 'memberUids'],
  additionalProperties: false,
}

export interface OpsCreateWorkerArgs {
  sector: OpsSector
  displayName: string
  partyId: string
  trade: string
  skills?: string[]
  placeIds?: string[]
  phone?: string
  memberUid?: string
  workerCode?: string
  opId: string
}

const createWorkerArgsSchema: JSONSchemaType<OpsCreateWorkerArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    displayName: { type: 'string', minLength: 1, maxLength: 240 },
    partyId: { type: 'string', minLength: 1 },
    trade: { type: 'string', minLength: 1, maxLength: 240 },
    skills: { type: 'array', items: { type: 'string' }, nullable: true },
    placeIds: { type: 'array', items: { type: 'string' }, nullable: true },
    phone: { type: 'string', nullable: true },
    memberUid: { type: 'string', nullable: true },
    workerCode: { type: 'string', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['sector', 'displayName', 'partyId', 'trade', 'opId'],
  additionalProperties: false,
}

export interface OpsUpdateWorkerArgs {
  workerId: string
  displayName?: string
  partyId?: string
  trade?: string
  skills?: string[]
  placeIds?: string[]
  phone?: string | null
  memberUid?: string | null
  workerCode?: string
  status?: OpsWorkerStatus
}

const updateWorkerArgsSchema: JSONSchemaType<OpsUpdateWorkerArgs> = {
  type: 'object',
  properties: {
    workerId: { type: 'string', minLength: 1 },
    displayName: { type: 'string', minLength: 1, nullable: true },
    partyId: { type: 'string', minLength: 1, nullable: true },
    trade: { type: 'string', minLength: 1, nullable: true },
    skills: { type: 'array', items: { type: 'string' }, nullable: true },
    placeIds: { type: 'array', items: { type: 'string' }, nullable: true },
    phone: { type: 'string', nullable: true },
    memberUid: { type: 'string', nullable: true },
    workerCode: { type: 'string', nullable: true },
    status: { type: 'string', enum: ['ACTIVE', 'INACTIVE', 'ARCHIVED'], nullable: true },
  },
  required: ['workerId'],
  additionalProperties: false,
}

export interface OpsArchiveWorkerArgs {
  workerId: string
}

const archiveWorkerArgsSchema: JSONSchemaType<OpsArchiveWorkerArgs> = {
  type: 'object',
  properties: {
    workerId: { type: 'string', minLength: 1 },
  },
  required: ['workerId'],
  additionalProperties: false,
}

export interface OpsAddGangArgs {
  sector: OpsSector
  partyId: string
  count: number
  trade: string
  placeIds?: string[]
  namePrefix: string
  opId: string
}

const addGangArgsSchema: JSONSchemaType<OpsAddGangArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    partyId: { type: 'string', minLength: 1 },
    count: { type: 'integer', minimum: 1, maximum: 500 },
    trade: { type: 'string', minLength: 1 },
    placeIds: { type: 'array', items: { type: 'string' }, nullable: true },
    namePrefix: { type: 'string', minLength: 1 },
    opId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['sector', 'partyId', 'count', 'trade', 'namePrefix', 'opId'],
  additionalProperties: false,
}

export interface OpsSetSectorRolesArgs {
  uid: string
  roleKeys: OpsSectorRole[]
}

const setSectorRolesArgsSchema: JSONSchemaType<OpsSetSectorRolesArgs> = {
  type: 'object',
  properties: {
    uid: { type: 'string', minLength: 1 },
    roleKeys: {
      type: 'array',
      items: {
        type: 'string',
        enum: ['TEMPLATE_APPROVER', 'RECORD_REVIEWER', 'RECORD_APPROVER'],
      },
    },
  },
  required: ['uid', 'roleKeys'],
  additionalProperties: false,
}

export interface OpsSaveTemplateDraftArgs {
  sector: OpsSector
  name: string
  kind: string
  fields: Array<{
    key: string
    label: string
    type: 'NUMBER' | 'CHOICE' | 'YES_NO' | 'DATE_TIME' | 'PHOTO' | 'READING' | 'ACKNOWLEDGE'
    required?: boolean
  }>
  safetyTagged?: boolean
  patientLinked?: boolean
  lateAfterMinutes?: number
  source?: string
  edition?: string
  jurisdiction?: string
  starter?: boolean
  templateId?: string
}

const saveTemplateDraftArgsSchema: JSONSchemaType<OpsSaveTemplateDraftArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    name: { type: 'string', minLength: 1, maxLength: 160 },
    kind: { type: 'string', minLength: 1, maxLength: 80 },
    fields: {
      type: 'array',
      minItems: 1,
      maxItems: 60,
      items: {
        type: 'object',
        properties: {
          key: { type: 'string', pattern: '^[a-z0-9_]{1,40}$' },
          label: { type: 'string', minLength: 1, maxLength: 120 },
          type: {
            type: 'string',
            enum: ['NUMBER', 'CHOICE', 'YES_NO', 'DATE_TIME', 'PHOTO', 'READING', 'ACKNOWLEDGE'],
          },
          required: { type: 'boolean', nullable: true },
        },
        required: ['key', 'label', 'type'],
        additionalProperties: true,
      },
    },
    safetyTagged: { type: 'boolean', nullable: true },
    patientLinked: { type: 'boolean', nullable: true },
    lateAfterMinutes: { type: 'number', nullable: true },
    source: { type: 'string', nullable: true },
    edition: { type: 'string', nullable: true },
    jurisdiction: { type: 'string', nullable: true },
    starter: { type: 'boolean', nullable: true },
    templateId: { type: 'string', nullable: true },
  },
  required: ['sector', 'name', 'kind', 'fields'],
  additionalProperties: false,
}

export interface OpsSubmitTemplateVersionArgs {
  templateId: string
  version: number
}

const submitTemplateVersionArgsSchema: JSONSchemaType<OpsSubmitTemplateVersionArgs> = {
  type: 'object',
  properties: {
    templateId: { type: 'string', minLength: 1 },
    version: { type: 'integer', minimum: 1 },
  },
  required: ['templateId', 'version'],
  additionalProperties: false,
}

export interface OpsApproveTemplateVersionArgs {
  templateId: string
  version: number
  selfApprovalReason?: string
}

const approveTemplateVersionArgsSchema: JSONSchemaType<OpsApproveTemplateVersionArgs> = {
  type: 'object',
  properties: {
    templateId: { type: 'string', minLength: 1 },
    version: { type: 'integer', minimum: 1 },
    selfApprovalReason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
  },
  required: ['templateId', 'version'],
  additionalProperties: false,
}

export interface OpsSubmitRecordArgs {
  templateId: string
  placeId: string
  values: Record<string, unknown>
  occurredAt: string
  capturedAt: string
  opId: string
  shiftId?: string | null
  asDraft?: boolean
  recordId?: string
}

const submitRecordArgsSchema: JSONSchemaType<OpsSubmitRecordArgs> = {
  type: 'object',
  properties: {
    templateId: { type: 'string', minLength: 1 },
    placeId: { type: 'string', minLength: 1 },
    values: {
      type: 'object',
      required: [],
      additionalProperties: true,
    },
    occurredAt: { type: 'string', minLength: 1 },
    capturedAt: { type: 'string', minLength: 1 },
    opId: { type: 'string', minLength: 1, maxLength: 120 },
    shiftId: { type: 'string', nullable: true },
    asDraft: { type: 'boolean', nullable: true },
    recordId: { type: 'string', nullable: true },
  },
  required: ['templateId', 'placeId', 'values', 'occurredAt', 'capturedAt', 'opId'],
  additionalProperties: false,
}

export interface OpsReviewRecordArgs {
  recordId: string
}

const reviewRecordArgsSchema: JSONSchemaType<OpsReviewRecordArgs> = {
  type: 'object',
  properties: {
    recordId: { type: 'string', minLength: 1 },
  },
  required: ['recordId'],
  additionalProperties: false,
}

export interface OpsReturnRecordArgs {
  recordId: string
  reason: string
}

const returnRecordArgsSchema: JSONSchemaType<OpsReturnRecordArgs> = {
  type: 'object',
  properties: {
    recordId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
  },
  required: ['recordId', 'reason'],
  additionalProperties: false,
}

export interface OpsApproveRecordArgs {
  recordId: string
  selfApprovalReason?: string
}

const approveRecordArgsSchema: JSONSchemaType<OpsApproveRecordArgs> = {
  type: 'object',
  properties: {
    recordId: { type: 'string', minLength: 1 },
    selfApprovalReason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
  },
  required: ['recordId'],
  additionalProperties: false,
}

export interface OpsAddCorrectionArgs {
  recordId: string
  reason: string
  changedValues: Record<string, unknown>
}

const addCorrectionArgsSchema: JSONSchemaType<OpsAddCorrectionArgs> = {
  type: 'object',
  properties: {
    recordId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    changedValues: {
      type: 'object',
      required: [],
      minProperties: 1,
      additionalProperties: true,
    },
  },
  required: ['recordId', 'reason', 'changedValues'],
  additionalProperties: false,
}

/* --- Roster, Shift, Change, and Attendance Action Schemas --- */

export interface OpsCreateDutyRoleArgs {
  key: string
  label: string
  sector?: OpsSector | 'core'
  opId?: string
}

const createDutyRoleArgsSchema: JSONSchemaType<OpsCreateDutyRoleArgs> = {
  type: 'object',
  properties: {
    key: { type: 'string', minLength: 1, maxLength: 40 },
    label: { type: 'string', minLength: 1, maxLength: 100 },
    sector: { type: 'string', enum: OPS_DUTY_ROLE_SECTOR_ENUM, nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['key', 'label'],
  additionalProperties: false,
}

export interface OpsUpdateDutyRoleArgs {
  key: string
  label?: string
  sector?: OpsSector | 'core'
  archived?: boolean
  opId?: string
}

const updateDutyRoleArgsSchema: JSONSchemaType<OpsUpdateDutyRoleArgs> = {
  type: 'object',
  properties: {
    key: { type: 'string', minLength: 1 },
    label: { type: 'string', nullable: true },
    sector: { type: 'string', enum: OPS_DUTY_ROLE_SECTOR_ENUM, nullable: true },
    archived: { type: 'boolean', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['key'],
  additionalProperties: false,
}

export interface OpsArchiveDutyRoleArgs {
  key: string
  opId?: string
}

const archiveDutyRoleArgsSchema: JSONSchemaType<OpsArchiveDutyRoleArgs> = {
  type: 'object',
  properties: {
    key: { type: 'string', minLength: 1 },
    opId: { type: 'string', nullable: true },
  },
  required: ['key'],
  additionalProperties: false,
}

export interface OpsCreateDepartmentArgs {
  key: string
  label: string
  expectedVersion?: number | null
  opId?: string | null
}

const createDepartmentArgsSchema: JSONSchemaType<OpsCreateDepartmentArgs> = {
  type: 'object',
  properties: {
    key: { type: 'string', minLength: 1, maxLength: 40 },
    label: { type: 'string', minLength: 1, maxLength: 100 },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['key', 'label'],
  additionalProperties: false,
}

export interface OpsRenameDepartmentArgs {
  key: string
  label: string
  expectedVersion?: number | null
  opId?: string | null
}

const renameDepartmentArgsSchema: JSONSchemaType<OpsRenameDepartmentArgs> = {
  type: 'object',
  properties: {
    key: { type: 'string', minLength: 1, maxLength: 40 },
    label: { type: 'string', minLength: 1, maxLength: 100 },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['key', 'label'],
  additionalProperties: false,
}

export interface OpsArchiveDepartmentArgs {
  key: string
  expectedVersion?: number | null
  opId?: string | null
}

const archiveDepartmentArgsSchema: JSONSchemaType<OpsArchiveDepartmentArgs> = {
  type: 'object',
  properties: {
    key: { type: 'string', minLength: 1, maxLength: 40 },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['key'],
  additionalProperties: false,
}

export interface OpsCreateShiftArgs {
  code: string
  name: string
  startMinute: number
  endMinute: number
  dutyRoleKeys: string[]
  placeIds?: string[]
  opId?: string
}

const createShiftArgsSchema: JSONSchemaType<OpsCreateShiftArgs> = {
  type: 'object',
  properties: {
    code: { type: 'string', minLength: 1, maxLength: 40 },
    name: { type: 'string', minLength: 1, maxLength: 120 },
    startMinute: { type: 'integer', minimum: 0, maximum: 1439 },
    endMinute: { type: 'integer', minimum: 0, maximum: 1439 },
    dutyRoleKeys: { type: 'array', items: { type: 'string' } },
    placeIds: { type: 'array', items: { type: 'string' }, nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['code', 'name', 'startMinute', 'endMinute', 'dutyRoleKeys'],
  additionalProperties: false,
}

export interface OpsUpdateShiftArgs {
  shiftId: string
  code?: string
  name?: string
  startMinute?: number
  endMinute?: number
  dutyRoleKeys?: string[]
  placeIds?: string[]
  archived?: boolean
  opId?: string
}

const updateShiftArgsSchema: JSONSchemaType<OpsUpdateShiftArgs> = {
  type: 'object',
  properties: {
    shiftId: { type: 'string', minLength: 1 },
    code: { type: 'string', nullable: true },
    name: { type: 'string', nullable: true },
    startMinute: { type: 'integer', nullable: true },
    endMinute: { type: 'integer', nullable: true },
    dutyRoleKeys: { type: 'array', items: { type: 'string' }, nullable: true },
    placeIds: { type: 'array', items: { type: 'string' }, nullable: true },
    archived: { type: 'boolean', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['shiftId'],
  additionalProperties: false,
}

export interface OpsArchiveShiftArgs {
  shiftId: string
  opId?: string
}

const archiveShiftArgsSchema: JSONSchemaType<OpsArchiveShiftArgs> = {
  type: 'object',
  properties: {
    shiftId: { type: 'string', minLength: 1 },
    opId: { type: 'string', nullable: true },
  },
  required: ['shiftId'],
  additionalProperties: false,
}

export interface OpsCreateCoverageRuleArgs {
  placeId: string
  shiftId: string
  dutyRoleKey: string
  minCount: number
  requiredSkill?: string | null
  opId?: string
}

const createCoverageRuleArgsSchema: JSONSchemaType<OpsCreateCoverageRuleArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    shiftId: { type: 'string', minLength: 1 },
    dutyRoleKey: { type: 'string', minLength: 1 },
    minCount: { type: 'integer', minimum: 1 },
    requiredSkill: { type: 'string', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['placeId', 'shiftId', 'dutyRoleKey', 'minCount'],
  additionalProperties: false,
}

export interface OpsUpdateCoverageRuleArgs {
  ruleId: string
  minCount?: number
  requiredSkill?: string | null
  archived?: boolean
  opId?: string
}

const updateCoverageRuleArgsSchema: JSONSchemaType<OpsUpdateCoverageRuleArgs> = {
  type: 'object',
  properties: {
    ruleId: { type: 'string', minLength: 1 },
    minCount: { type: 'integer', nullable: true },
    requiredSkill: { type: 'string', nullable: true },
    archived: { type: 'boolean', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['ruleId'],
  additionalProperties: false,
}

export interface OpsArchiveCoverageRuleArgs {
  ruleId: string
  opId?: string
}

const archiveCoverageRuleArgsSchema: JSONSchemaType<OpsArchiveCoverageRuleArgs> = {
  type: 'object',
  properties: {
    ruleId: { type: 'string', minLength: 1 },
    opId: { type: 'string', nullable: true },
  },
  required: ['ruleId'],
  additionalProperties: false,
}

export interface OpsCreateRosterArgs {
  placeId: string
  periodStart: string
  periodEnd: string
  opId?: string
}

const createRosterArgsSchema: JSONSchemaType<OpsCreateRosterArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    periodStart: { type: 'string', minLength: 10, maxLength: 10 },
    periodEnd: { type: 'string', minLength: 10, maxLength: 10 },
    opId: { type: 'string', nullable: true },
  },
  required: ['placeId', 'periodStart', 'periodEnd'],
  additionalProperties: false,
}

export interface OpsSaveDraftAssignmentsArgs {
  rosterId: string
  draftAssignments: Array<{
    dayKey: string
    shiftId: string
    dutyRoleKey: string
    kind: 'SHIFT' | 'ON_CALL_PRIMARY' | 'ON_CALL_BACKUP'
    personRef: { kind: string; uid?: string; workerCode?: string }
  }>
  opId?: string
}

const saveDraftAssignmentsArgsSchema: JSONSchemaType<OpsSaveDraftAssignmentsArgs> = {
  type: 'object',
  properties: {
    rosterId: { type: 'string', minLength: 1 },
    draftAssignments: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          dayKey: { type: 'string' },
          shiftId: { type: 'string' },
          dutyRoleKey: { type: 'string' },
          kind: { type: 'string', enum: ['SHIFT', 'ON_CALL_PRIMARY', 'ON_CALL_BACKUP'] },
          personRef: {
            type: 'object',
            properties: {
              kind: { type: 'string' },
              uid: { type: 'string', nullable: true },
              workerCode: { type: 'string', nullable: true },
            },
            required: ['kind'],
            additionalProperties: true,
          },
        },
        required: ['dayKey', 'shiftId', 'dutyRoleKey', 'kind', 'personRef'],
        additionalProperties: true,
      },
    },
    opId: { type: 'string', nullable: true },
  },
  required: ['rosterId', 'draftAssignments'],
  additionalProperties: false,
}

export interface OpsPublishRosterArgs {
  rosterId: string
  acceptWarnings?: boolean
  changeId?: string
  opId?: string
}

const publishRosterArgsSchema: JSONSchemaType<OpsPublishRosterArgs> = {
  type: 'object',
  properties: {
    rosterId: { type: 'string', minLength: 1 },
    acceptWarnings: { type: 'boolean', nullable: true },
    changeId: { type: 'string', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['rosterId'],
  additionalProperties: false,
}

export interface OpsPreviewRosterWarningsArgs {
  rosterId: string
}

const previewRosterWarningsArgsSchema: JSONSchemaType<OpsPreviewRosterWarningsArgs> = {
  type: 'object',
  properties: {
    rosterId: { type: 'string', minLength: 1 },
  },
  required: ['rosterId'],
  additionalProperties: false,
}

export interface OpsAcknowledgeRosterArgs {
  rosterId: string
  version: number
  opId?: string
}

const acknowledgeRosterArgsSchema: JSONSchemaType<OpsAcknowledgeRosterArgs> = {
  type: 'object',
  properties: {
    rosterId: { type: 'string', minLength: 1 },
    version: { type: 'integer', minimum: 1 },
    opId: { type: 'string', nullable: true },
  },
  required: ['rosterId', 'version'],
  additionalProperties: false,
}

export interface OpsRequestRosterChangeArgs {
  type: 'LEAVE' | 'SWAP' | 'ON_CALL_SWAP'
  requesterPersonRef: { kind: string; uid?: string; workerCode?: string }
  dayKey: string
  endDayKey?: string | null
  shiftId?: string | null
  replacementShiftId?: string | null
  dutyRoleKey?: string | null
  placeId?: string | null
  rosterId?: string | null
  targetPersonRef?: { kind: string; uid?: string; workerCode?: string } | null
  reason?: string | null
  opId?: string
}

const requestRosterChangeArgsSchema: JSONSchemaType<OpsRequestRosterChangeArgs> = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['LEAVE', 'SWAP', 'ON_CALL_SWAP'] },
    requesterPersonRef: {
      type: 'object',
      properties: {
        kind: { type: 'string' },
        uid: { type: 'string', nullable: true },
        workerCode: { type: 'string', nullable: true },
      },
      required: ['kind'],
      additionalProperties: true,
    },
    dayKey: { type: 'string', minLength: 10, maxLength: 10 },
    endDayKey: { type: 'string', nullable: true },
    shiftId: { type: 'string', nullable: true },
    replacementShiftId: { type: 'string', nullable: true },
    dutyRoleKey: { type: 'string', nullable: true },
    placeId: { type: 'string', nullable: true },
    rosterId: { type: 'string', nullable: true },
    targetPersonRef: {
      type: 'object',
      properties: {
        kind: { type: 'string' },
        uid: { type: 'string', nullable: true },
        workerCode: { type: 'string', nullable: true },
      },
      required: [],
      nullable: true,
      additionalProperties: true,
    },
    reason: { type: 'string', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['type', 'requesterPersonRef', 'dayKey'],
  additionalProperties: false,
}

export interface OpsRespondRosterChangeArgs {
  changeId: string
  decision: 'ACCEPT' | 'REJECT'
  responseNote?: string | null
  opId?: string
}

const respondRosterChangeArgsSchema: JSONSchemaType<OpsRespondRosterChangeArgs> = {
  type: 'object',
  properties: {
    changeId: { type: 'string', minLength: 1 },
    decision: { type: 'string', enum: ['ACCEPT', 'REJECT'] },
    responseNote: { type: 'string', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['changeId', 'decision'],
  additionalProperties: false,
}

export interface OpsDecideRosterChangeArgs {
  changeId: string
  decision: 'APPROVE' | 'REJECT'
  decisionReason?: string | null
  selfApprovalReason?: string | null
  opId?: string
}

const decideRosterChangeArgsSchema: JSONSchemaType<OpsDecideRosterChangeArgs> = {
  type: 'object',
  properties: {
    changeId: { type: 'string', minLength: 1 },
    decision: { type: 'string', enum: ['APPROVE', 'REJECT'] },
    decisionReason: { type: 'string', nullable: true },
    selfApprovalReason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['changeId', 'decision'],
  additionalProperties: false,
}

export interface OpsCancelRosterChangeArgs {
  changeId: string
  reason?: string | null
  opId?: string
}

const cancelRosterChangeArgsSchema: JSONSchemaType<OpsCancelRosterChangeArgs> = {
  type: 'object',
  properties: {
    changeId: { type: 'string', minLength: 1 },
    reason: { type: 'string', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['changeId'],
  additionalProperties: false,
}

export interface OpsMarkAttendanceRollArgs {
  dayKey: string
  shiftId: string
  entries: Array<{
    personRef: { kind: string; uid?: string; workerCode?: string }
    status: 'PRESENT' | 'ABSENT' | 'LATE' | 'HALF_DAY' | 'ON_LEAVE'
    actualIn?: string | null
    actualOut?: string | null
  }>
  opId?: string
}

const markAttendanceRollArgsSchema: JSONSchemaType<OpsMarkAttendanceRollArgs> = {
  type: 'object',
  properties: {
    dayKey: { type: 'string', minLength: 10, maxLength: 10 },
    shiftId: { type: 'string', minLength: 1 },
    entries: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          personRef: {
            type: 'object',
            properties: {
              kind: { type: 'string' },
              uid: { type: 'string', nullable: true },
              workerCode: { type: 'string', nullable: true },
            },
            required: ['kind'],
            additionalProperties: true,
          },
          status: { type: 'string', enum: ['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY', 'ON_LEAVE'] },
          actualIn: { type: 'string', nullable: true },
          actualOut: { type: 'string', nullable: true },
        },
        required: ['personRef', 'status'],
        additionalProperties: true,
      },
    },
    opId: { type: 'string', nullable: true },
  },
  required: ['dayKey', 'shiftId', 'entries'],
  additionalProperties: false,
}

export interface OpsRequestAttendanceCorrectionArgs {
  attendanceId: string
  requestedStatus: 'PRESENT' | 'ABSENT' | 'LATE' | 'HALF_DAY' | 'ON_LEAVE'
  requestedActualIn?: string | null
  requestedActualOut?: string | null
  reason: string
  opId?: string
}

const requestAttendanceCorrectionArgsSchema: JSONSchemaType<OpsRequestAttendanceCorrectionArgs> = {
  type: 'object',
  properties: {
    attendanceId: { type: 'string', minLength: 1 },
    requestedStatus: { type: 'string', enum: ['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY', 'ON_LEAVE'] },
    requestedActualIn: { type: 'string', nullable: true },
    requestedActualOut: { type: 'string', nullable: true },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', nullable: true },
  },
  required: ['attendanceId', 'requestedStatus', 'reason'],
  additionalProperties: false,
}

export interface OpsDecideAttendanceCorrectionArgs {
  attendanceId: string
  correctionId: string
  decision: 'APPROVE' | 'REJECT'
  decisionReason?: string | null
  selfApprovalReason?: string | null
  opId?: string
}

const decideAttendanceCorrectionArgsSchema: JSONSchemaType<OpsDecideAttendanceCorrectionArgs> = {
  type: 'object',
  properties: {
    attendanceId: { type: 'string', minLength: 1 },
    correctionId: { type: 'string', minLength: 1 },
    decision: { type: 'string', enum: ['APPROVE', 'REJECT'] },
    decisionReason: { type: 'string', nullable: true },
    selfApprovalReason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['attendanceId', 'correctionId', 'decision'],
  additionalProperties: false,
}

export interface OpsImportAttendanceCsvArgs {
  rows: Array<{
    dayKey: string
    shiftId: string
    personRef: { kind: string; uid?: string; workerCode?: string }
    status: 'PRESENT' | 'ABSENT' | 'LATE' | 'HALF_DAY' | 'ON_LEAVE'
    actualIn?: string | null
    actualOut?: string | null
  }>
  dryRun: boolean
  opId?: string
}

const importAttendanceCsvArgsSchema: JSONSchemaType<OpsImportAttendanceCsvArgs> = {
  type: 'object',
  properties: {
    rows: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          dayKey: { type: 'string' },
          shiftId: { type: 'string' },
          personRef: {
            type: 'object',
            properties: {
              kind: { type: 'string' },
              uid: { type: 'string', nullable: true },
              workerCode: { type: 'string', nullable: true },
            },
            required: ['kind'],
            additionalProperties: true,
          },
          status: { type: 'string', enum: ['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY', 'ON_LEAVE'] },
          actualIn: { type: 'string', nullable: true },
          actualOut: { type: 'string', nullable: true },
        },
        required: ['dayKey', 'shiftId', 'personRef', 'status'],
        additionalProperties: true,
      },
    },
    dryRun: { type: 'boolean' },
    opId: { type: 'string', nullable: true },
  },
  required: ['rows', 'dryRun'],
  additionalProperties: false,
}

export interface OpsAssignReliefArgs {
  requestId: string
  reliefPersonRef: { kind: string; uid?: string; workerId?: string; workerCode?: string }
  opId?: string
}

const assignReliefArgsSchema: JSONSchemaType<OpsAssignReliefArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1 },
    reliefPersonRef: {
      type: 'object',
      properties: {
        kind: { type: 'string' },
        uid: { type: 'string', nullable: true },
        workerId: { type: 'string', nullable: true },
        workerCode: { type: 'string', nullable: true },
      },
      required: ['kind'],
      additionalProperties: true,
    },
    opId: { type: 'string', nullable: true },
  },
  required: ['requestId', 'reliefPersonRef'],
  additionalProperties: false,
}

export interface OpsScheduleCadenceArgs {
  kind: 'PER_SHIFT' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY' | 'EVERY_N_DAYS'
  shiftId?: string | null
  intervalDays?: number | null
  anchorDayKey?: string | null
}

export interface OpsScheduleWindowArgs {
  startMinute?: number | null
  durationMinutes?: number | null
  isoWeekday?: number | null
  dayOfMonth?: number | null
  monthOfQuarter?: number | null
  month?: number | null
}

const scheduleCadenceSchema: JSONSchemaType<OpsScheduleCadenceArgs> = {
  type: 'object',
  properties: {
    kind: {
      type: 'string',
      enum: [
        'PER_SHIFT',
        'DAILY',
        'WEEKLY',
        'MONTHLY',
        'QUARTERLY',
        'YEARLY',
        'EVERY_N_DAYS',
      ],
    },
    shiftId: { type: 'string', minLength: 1, nullable: true },
    intervalDays: { type: 'integer', minimum: 1, maximum: 365, nullable: true },
    anchorDayKey: { type: 'string', minLength: 10, maxLength: 10, nullable: true },
  },
  required: ['kind'],
  additionalProperties: false,
}

const scheduleWindowSchema: JSONSchemaType<OpsScheduleWindowArgs> = {
  type: 'object',
  properties: {
    startMinute: { type: 'integer', minimum: 0, maximum: 1439, nullable: true },
    durationMinutes: { type: 'integer', minimum: 1, maximum: 1440, nullable: true },
    isoWeekday: { type: 'integer', minimum: 1, maximum: 7, nullable: true },
    dayOfMonth: { type: 'integer', minimum: -1, maximum: 28, nullable: true },
    monthOfQuarter: { type: 'integer', minimum: 1, maximum: 3, nullable: true },
    month: { type: 'integer', minimum: 1, maximum: 12, nullable: true },
  },
  required: [],
  additionalProperties: false,
}

export interface OpsCreateScheduleArgs {
  templateId: string
  placeIds: string[]
  cadence: OpsScheduleCadenceArgs
  window: OpsScheduleWindowArgs
  assigneeDutyRoleKey: string
  escalationFirstDutyRoleKey?: string | null
  graceMinutes?: number | null
  escalateEveryMinutes?: number | null
  opId?: string | null
}

const createScheduleArgsSchema: JSONSchemaType<OpsCreateScheduleArgs> = {
  type: 'object',
  properties: {
    templateId: { type: 'string', minLength: 1 },
    placeIds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      minItems: 1,
      maxItems: 50,
    },
    cadence: scheduleCadenceSchema,
    window: scheduleWindowSchema,
    assigneeDutyRoleKey: { type: 'string', minLength: 1, maxLength: 40 },
    escalationFirstDutyRoleKey: { type: 'string', minLength: 1, maxLength: 40, nullable: true },
    graceMinutes: { type: 'integer', minimum: 0, maximum: 240, nullable: true },
    escalateEveryMinutes: { type: 'integer', minimum: 5, maximum: 1440, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['templateId', 'placeIds', 'cadence', 'window', 'assigneeDutyRoleKey'],
  additionalProperties: false,
}

export interface OpsUpdateScheduleArgs {
  scheduleId: string
  placeIds?: string[] | null
  cadence?: OpsScheduleCadenceArgs | null
  window?: OpsScheduleWindowArgs | null
  assigneeDutyRoleKey?: string | null
  escalationFirstDutyRoleKey?: string | null
  graceMinutes?: number | null
  escalateEveryMinutes?: number | null
  opId?: string | null
}

const updateScheduleArgsSchema: JSONSchemaType<OpsUpdateScheduleArgs> = {
  type: 'object',
  properties: {
    scheduleId: { type: 'string', minLength: 1 },
    placeIds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      minItems: 1,
      maxItems: 50,
      nullable: true,
    },
    cadence: { ...scheduleCadenceSchema, nullable: true },
    window: { ...scheduleWindowSchema, nullable: true },
    assigneeDutyRoleKey: { type: 'string', minLength: 1, maxLength: 40, nullable: true },
    escalationFirstDutyRoleKey: { type: 'string', minLength: 1, maxLength: 40, nullable: true },
    graceMinutes: { type: 'integer', minimum: 0, maximum: 240, nullable: true },
    escalateEveryMinutes: { type: 'integer', minimum: 5, maximum: 1440, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['scheduleId'],
  additionalProperties: false,
}

export interface OpsScheduleIdArgs {
  scheduleId: string
  opId?: string | null
}

const scheduleIdArgsSchema: JSONSchemaType<OpsScheduleIdArgs> = {
  type: 'object',
  properties: {
    scheduleId: { type: 'string', minLength: 1 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['scheduleId'],
  additionalProperties: false,
}

export interface OpsSkipDueArgs {
  dueId: string
  reason: string
  opId?: string | null
}

const skipDueArgsSchema: JSONSchemaType<OpsSkipDueArgs> = {
  type: 'object',
  properties: {
    dueId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['dueId', 'reason'],
  additionalProperties: false,
}

export interface OpsRecordCheckpointScanArgs {
  token: string
  geo?: {
    lat: number
    lng: number
    accuracy?: number | null
  } | null
  note?: string | null
  photoPath?: string | null
  opId?: string | null
}

const recordCheckpointScanArgsSchema: JSONSchemaType<OpsRecordCheckpointScanArgs> = {
  type: 'object',
  properties: {
    token: { type: 'string', minLength: 1 },
    geo: {
      type: 'object',
      properties: {
        lat: { type: 'number' },
        lng: { type: 'number' },
        accuracy: { type: 'number', nullable: true },
      },
      required: ['lat', 'lng'],
      nullable: true,
      additionalProperties: false,
    },
    note: { type: 'string', maxLength: 2000, nullable: true },
    photoPath: { type: 'string', maxLength: 1000, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['token'],
  additionalProperties: false,
}

/* --- Requests Action Schemas --- */

export interface OpsUpsertRequestKindArgs {
  kind: Record<string, unknown>
  opId?: string | null
}

const upsertRequestKindArgsSchema: JSONSchemaType<OpsUpsertRequestKindArgs> = {
  type: 'object',
  properties: {
    kind: { type: 'object', additionalProperties: true, required: [] },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['kind'],
  additionalProperties: false,
}

export interface OpsListRequestKindsArgs {
  sector?: string | null
  includeArchived?: boolean | null
  opId?: string | null
}

const listRequestKindsArgsSchema: JSONSchemaType<OpsListRequestKindsArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', minLength: 1, nullable: true },
    includeArchived: { type: 'boolean', nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
}

export interface OpsRaiseRequestArgs {
  sector: string
  kind: string
  placeId: string
  title: string
  details?: string | null
  photos?: string[] | null
  opId?: string | null
}

const raiseRequestArgsSchema: JSONSchemaType<OpsRaiseRequestArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', minLength: 1 },
    kind: { type: 'string', minLength: 1 },
    placeId: { type: 'string', minLength: 1 },
    title: { type: 'string', minLength: 1, maxLength: 120 },
    details: { type: 'string', maxLength: 2000, nullable: true },
    photos: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      maxItems: 4,
      nullable: true,
    },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['sector', 'kind', 'placeId', 'title'],
  additionalProperties: false,
}

export interface OpsAttachRequestPhotosArgs {
  requestId: string
  photos: string[]
  opId?: string | null
}

const attachRequestPhotosArgsSchema: JSONSchemaType<OpsAttachRequestPhotosArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1 },
    photos: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      minItems: 1,
      maxItems: 4,
    },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['requestId', 'photos'],
  additionalProperties: false,
}

export interface OpsRequestIdArgs {
  requestId: string
  opId?: string | null
}

const requestIdArgsSchema: JSONSchemaType<OpsRequestIdArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['requestId'],
  additionalProperties: false,
}

export interface OpsAssignRequestArgs {
  requestId: string
  assigneeUid: string
  opId?: string | null
}

const assignRequestArgsSchema: JSONSchemaType<OpsAssignRequestArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1 },
    assigneeUid: { type: 'string', minLength: 1 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['requestId', 'assigneeUid'],
  additionalProperties: false,
}

export interface OpsMarkRequestDoneArgs {
  requestId: string
  photos?: string[] | null
  opId?: string | null
}

const markRequestDoneArgsSchema: JSONSchemaType<OpsMarkRequestDoneArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1 },
    photos: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      maxItems: 4,
      nullable: true,
    },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['requestId'],
  additionalProperties: false,
}

export interface OpsVerifyRequestArgs {
  requestId: string
  decision: 'APPROVE' | 'REJECT'
  reason?: string | null
  selfApprovalReason?: string | null
  opId?: string | null
}

const verifyRequestArgsSchema: JSONSchemaType<OpsVerifyRequestArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1 },
    decision: { type: 'string', enum: ['APPROVE', 'REJECT'] },
    reason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    selfApprovalReason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['requestId', 'decision'],
  additionalProperties: false,
}

export interface OpsRequestReasonArgs {
  requestId: string
  reason: string
  opId?: string | null
}

const requestReasonArgsSchema: JSONSchemaType<OpsRequestReasonArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['requestId', 'reason'],
  additionalProperties: false,
}

export interface OpsScheduleJobArgs {
  requestId: string
  schedule?: {
    date: string
    windowStart?: string | null
    windowEnd?: string | null
  } | null
  assigneeUid?: string | null
  opId?: string | null
}

const scheduleJobArgsSchema: JSONSchemaType<OpsScheduleJobArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1 },
    schedule: {
      type: 'object',
      properties: {
        date: { type: 'string', minLength: 10, maxLength: 10 },
        windowStart: { type: 'string', nullable: true },
        windowEnd: { type: 'string', nullable: true },
      },
      required: ['date'],
      nullable: true,
    },
    assigneeUid: { type: 'string', nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['requestId'],
  additionalProperties: false,
}

export interface OpsRaiseRevisitArgs {
  requestId: string
  reason: string
  schedule?: {
    date: string
    windowStart?: string | null
    windowEnd?: string | null
  } | null
  opId?: string | null
}

const raiseRevisitArgsSchema: JSONSchemaType<OpsRaiseRevisitArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    schedule: {
      type: 'object',
      properties: {
        date: { type: 'string', minLength: 10, maxLength: 10 },
        windowStart: { type: 'string', nullable: true },
        windowEnd: { type: 'string', nullable: true },
      },
      required: ['date'],
      nullable: true,
    },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['requestId', 'reason'],
  additionalProperties: false,
}

export interface OpsListJobBoardArgs {
  date: string
}

const listJobBoardArgsSchema: JSONSchemaType<OpsListJobBoardArgs> = {
  type: 'object',
  properties: {
    date: { type: 'string', minLength: 10, maxLength: 10 },
  },
  required: ['date'],
  additionalProperties: false,
}

export interface OpsCreateComplianceItemArgs {
  sector: OpsSector
  title: string
  category: OpsComplianceCategory
  validTo: string
  ownerUid: string
  authority?: string | null
  reference?: string | null
  placeId?: string | null
  validFrom?: string | null
  reminderDays?: number[] | null
  backupUid?: string | null
  notes?: string | null
  evidence?: string[] | null
  opId?: string | null
}

const createComplianceItemArgsSchema: JSONSchemaType<OpsCreateComplianceItemArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    title: { type: 'string', minLength: 1, maxLength: 120 },
    category: { type: 'string', enum: OPS_COMPLIANCE_CATEGORY_ENUM },
    validTo: { type: 'string', minLength: 10, maxLength: 10 },
    ownerUid: { type: 'string', minLength: 1 },
    authority: { type: 'string', maxLength: 120, nullable: true },
    reference: { type: 'string', maxLength: 80, nullable: true },
    placeId: { type: 'string', minLength: 1, nullable: true },
    validFrom: { type: 'string', minLength: 10, maxLength: 10, nullable: true },
    reminderDays: {
      type: 'array',
      items: { type: 'integer', minimum: 0, maximum: 365 },
      minItems: 1,
      maxItems: 6,
      nullable: true,
    },
    backupUid: { type: 'string', minLength: 1, nullable: true },
    notes: { type: 'string', maxLength: 2000, nullable: true },
    evidence: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      maxItems: 10,
      nullable: true,
    },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['sector', 'title', 'category', 'validTo', 'ownerUid'],
  additionalProperties: false,
}

export interface OpsUpdateComplianceItemArgs {
  itemId: string
  title: string
  category: OpsComplianceCategory
  validTo: string
  ownerUid: string
  expectedVersion: number
  authority?: string | null
  reference?: string | null
  placeId?: string | null
  validFrom?: string | null
  reminderDays?: number[] | null
  backupUid?: string | null
  notes?: string | null
  opId?: string | null
}

const updateComplianceItemArgsSchema: JSONSchemaType<OpsUpdateComplianceItemArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1 },
    title: { type: 'string', minLength: 1, maxLength: 120 },
    category: { type: 'string', enum: OPS_COMPLIANCE_CATEGORY_ENUM },
    validTo: { type: 'string', minLength: 10, maxLength: 10 },
    ownerUid: { type: 'string', minLength: 1 },
    expectedVersion: { type: 'integer', minimum: 1 },
    authority: { type: 'string', maxLength: 120, nullable: true },
    reference: { type: 'string', maxLength: 80, nullable: true },
    placeId: { type: 'string', minLength: 1, nullable: true },
    validFrom: { type: 'string', minLength: 10, maxLength: 10, nullable: true },
    reminderDays: {
      type: 'array',
      items: { type: 'integer', minimum: 0, maximum: 365 },
      minItems: 1,
      maxItems: 6,
      nullable: true,
    },
    backupUid: { type: 'string', minLength: 1, nullable: true },
    notes: { type: 'string', maxLength: 2000, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['itemId', 'title', 'category', 'validTo', 'ownerUid', 'expectedVersion'],
  additionalProperties: false,
}

export interface OpsRenewComplianceItemArgs {
  itemId: string
  validTo: string
  validFrom?: string | null
  reference?: string | null
  evidence?: string[] | null
  expectedVersion?: number | null
  opId?: string | null
}

const renewComplianceItemArgsSchema: JSONSchemaType<OpsRenewComplianceItemArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1 },
    validTo: { type: 'string', minLength: 10, maxLength: 10 },
    validFrom: { type: 'string', minLength: 10, maxLength: 10, nullable: true },
    reference: { type: 'string', maxLength: 80, nullable: true },
    evidence: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      maxItems: 10,
      nullable: true,
    },
    expectedVersion: { type: 'integer', minimum: 1, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['itemId', 'validTo'],
  additionalProperties: false,
}

export interface OpsAttachComplianceEvidenceArgs {
  itemId: string
  evidence: string[]
  opId?: string | null
}

const attachComplianceEvidenceArgsSchema: JSONSchemaType<OpsAttachComplianceEvidenceArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1 },
    evidence: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      minItems: 1,
      maxItems: 10,
    },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['itemId', 'evidence'],
  additionalProperties: false,
}

export interface OpsArchiveComplianceItemArgs {
  itemId: string
  reason: string
  opId?: string | null
}

const archiveComplianceItemArgsSchema: JSONSchemaType<OpsArchiveComplianceItemArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['itemId', 'reason'],
  additionalProperties: false,
}

export interface OpsCreateAssetArgs {
  sector: OpsSector
  assetCode: string
  name: string
  criticality: OpsAssetCriticality
  placeId: string
  category?: string | null
  model?: string | null
  serial?: string | null
  amcUntil?: string | null
  warrantyUntil?: string | null
  opId?: string | null
}

const createAssetArgsSchema: JSONSchemaType<OpsCreateAssetArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    assetCode: { type: 'string', minLength: 1, maxLength: 40 },
    name: { type: 'string', minLength: 1, maxLength: 120 },
    criticality: { type: 'string', enum: OPS_ASSET_CRITICALITY_ENUM },
    placeId: { type: 'string', minLength: 1 },
    category: { type: 'string', maxLength: 60, nullable: true },
    model: { type: 'string', maxLength: 80, nullable: true },
    serial: { type: 'string', maxLength: 80, nullable: true },
    amcUntil: { type: 'string', minLength: 10, maxLength: 10, nullable: true },
    warrantyUntil: { type: 'string', minLength: 10, maxLength: 10, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['sector', 'assetCode', 'name', 'criticality', 'placeId'],
  additionalProperties: false,
}

export interface OpsUpdateAssetArgs {
  assetId: string
  name: string
  criticality: OpsAssetCriticality
  placeId: string
  expectedVersion: number
  category?: string | null
  model?: string | null
  serial?: string | null
  amcUntil?: string | null
  warrantyUntil?: string | null
  opId?: string | null
}

const updateAssetArgsSchema: JSONSchemaType<OpsUpdateAssetArgs> = {
  type: 'object',
  properties: {
    assetId: { type: 'string', minLength: 1 },
    name: { type: 'string', minLength: 1, maxLength: 120 },
    criticality: { type: 'string', enum: OPS_ASSET_CRITICALITY_ENUM },
    placeId: { type: 'string', minLength: 1 },
    expectedVersion: { type: 'integer', minimum: 1 },
    category: { type: 'string', maxLength: 60, nullable: true },
    model: { type: 'string', maxLength: 80, nullable: true },
    serial: { type: 'string', maxLength: 80, nullable: true },
    amcUntil: { type: 'string', minLength: 10, maxLength: 10, nullable: true },
    warrantyUntil: { type: 'string', minLength: 10, maxLength: 10, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['assetId', 'name', 'criticality', 'placeId', 'expectedVersion'],
  additionalProperties: false,
}

export interface OpsSetAssetStatusArgs {
  assetId: string
  status: OpsAssetStatus
  expectedVersion: number
  reason?: string | null
  opId?: string | null
}

const setAssetStatusArgsSchema: JSONSchemaType<OpsSetAssetStatusArgs> = {
  type: 'object',
  properties: {
    assetId: { type: 'string', minLength: 1 },
    status: { type: 'string', enum: OPS_ASSET_STATUS_ENUM },
    expectedVersion: { type: 'integer', minimum: 1 },
    reason: { type: 'string', maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['assetId', 'status', 'expectedVersion'],
  additionalProperties: false,
}

export interface OpsIsolateAssetArgs {
  assetId: string
  reason: string
  expectedVersion: number
  opId?: string | null
}

const isolateAssetArgsSchema: JSONSchemaType<OpsIsolateAssetArgs> = {
  type: 'object',
  properties: {
    assetId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    expectedVersion: { type: 'integer', minimum: 1 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['assetId', 'reason', 'expectedVersion'],
  additionalProperties: false,
}

export interface OpsReleaseAssetArgs {
  assetId: string
  toStatus: 'AVAILABLE' | 'IN_USE'
  expectedVersion: number
  selfRelease?: boolean | null
  reason?: string | null
  opId?: string | null
}

const releaseAssetArgsSchema: JSONSchemaType<OpsReleaseAssetArgs> = {
  type: 'object',
  properties: {
    assetId: { type: 'string', minLength: 1 },
    toStatus: { type: 'string', enum: OPS_ASSET_RELEASE_TO_STATUS_ENUM },
    expectedVersion: { type: 'integer', minimum: 1 },
    selfRelease: { type: 'boolean', nullable: true },
    reason: { type: 'string', maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['assetId', 'toStatus', 'expectedVersion'],
  additionalProperties: false,
}

export interface OpsRetireAssetArgs {
  assetId: string
  reason: string
  expectedVersion: number
  opId?: string | null
}

const retireAssetArgsSchema: JSONSchemaType<OpsRetireAssetArgs> = {
  type: 'object',
  properties: {
    assetId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    expectedVersion: { type: 'integer', minimum: 1 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['assetId', 'reason', 'expectedVersion'],
  additionalProperties: false,
}

export interface OpsRecordAssetServiceVisitArgs {
  assetId: string
  visitDayKey: string
  kind: OpsAssetServiceVisitKind
  summary: string
  vendorName?: string | null
  engineerName?: string | null
  invoiceRef?: string | null
  requestId?: string | null
  filePaths?: string[] | null
  opId?: string | null
}

const recordAssetServiceVisitArgsSchema: JSONSchemaType<OpsRecordAssetServiceVisitArgs> = {
  type: 'object',
  properties: {
    assetId: { type: 'string', minLength: 1 },
    visitDayKey: { type: 'string', minLength: 10, maxLength: 10 },
    kind: { type: 'string', enum: OPS_ASSET_SERVICE_VISIT_KIND_ENUM },
    summary: { type: 'string', minLength: 3, maxLength: 1000 },
    vendorName: { type: 'string', maxLength: 120, nullable: true },
    engineerName: { type: 'string', maxLength: 120, nullable: true },
    invoiceRef: { type: 'string', maxLength: 80, nullable: true },
    requestId: { type: 'string', maxLength: 120, nullable: true },
    filePaths: {
      type: 'array',
      items: { type: 'string' },
      nullable: true,
    },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['assetId', 'visitDayKey', 'kind', 'summary'],
  additionalProperties: false,
}

/* --- Delivery Action Schemas (R39 E1.a) --- */

export interface OpsMilestoneArgItem {
  id?: string | null
  title: string
  dueDate?: string | null
  status?: OpsMilestoneStatus | null
}

export interface OpsCreateEngagementArgs {
  clientId: string
  name: string
  model: OpsEngagementModel
  startDate: string
  endDate?: string | null
  milestones?: OpsMilestoneArgItem[] | null
  opId?: string | null
}

const milestoneArgItemSchema: JSONSchemaType<OpsMilestoneArgItem> = {
  type: 'object',
  properties: {
    id: { type: 'string', minLength: 1, nullable: true },
    title: { type: 'string', minLength: 1, maxLength: 240 },
    dueDate: { type: 'string', minLength: 1, maxLength: 30, nullable: true },
    status: { type: 'string', enum: OPS_MILESTONE_STATUS_ENUM, nullable: true },
  },
  required: ['title'],
  additionalProperties: false,
}

const createEngagementArgsSchema: JSONSchemaType<OpsCreateEngagementArgs> = {
  type: 'object',
  properties: {
    clientId: { type: 'string', minLength: 1, maxLength: 120 },
    name: { type: 'string', minLength: 1, maxLength: 240 },
    model: { type: 'string', enum: OPS_ENGAGEMENT_MODEL_ENUM },
    startDate: { type: 'string', minLength: 1, maxLength: 30 },
    endDate: { type: 'string', minLength: 1, maxLength: 30, nullable: true },
    milestones: {
      type: 'array',
      items: milestoneArgItemSchema,
      nullable: true,
    },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['clientId', 'name', 'model', 'startDate'],
  additionalProperties: false,
}

export interface OpsUpdateEngagementArgs {
  engagementId: string
  name?: string | null
  startDate?: string | null
  endDate?: string | null
  opId?: string | null
}

const updateEngagementArgsSchema: JSONSchemaType<OpsUpdateEngagementArgs> = {
  type: 'object',
  properties: {
    engagementId: { type: 'string', minLength: 1, maxLength: 120 },
    name: { type: 'string', minLength: 1, maxLength: 240, nullable: true },
    startDate: { type: 'string', minLength: 1, maxLength: 30, nullable: true },
    endDate: { type: 'string', minLength: 1, maxLength: 30, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['engagementId'],
  additionalProperties: false,
}

export interface OpsSetEngagementStatusArgs {
  engagementId: string
  status: OpsEngagementStatus
  opId?: string | null
}

const setEngagementStatusArgsSchema: JSONSchemaType<OpsSetEngagementStatusArgs> = {
  type: 'object',
  properties: {
    engagementId: { type: 'string', minLength: 1, maxLength: 120 },
    status: { type: 'string', enum: OPS_ENGAGEMENT_STATUS_ENUM },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['engagementId', 'status'],
  additionalProperties: false,
}

export interface OpsMilestoneStatusArgItem {
  id?: string | null
  title: string
  dueDate?: string | null
  status: OpsMilestoneStatus
}

export interface OpsSetEngagementMilestonesArgs {
  engagementId: string
  milestones: OpsMilestoneStatusArgItem[]
  reason?: string | null
  opId?: string | null
}

const milestoneStatusArgItemSchema: JSONSchemaType<OpsMilestoneStatusArgItem> = {
  type: 'object',
  properties: {
    id: { type: 'string', minLength: 1, nullable: true },
    title: { type: 'string', minLength: 1, maxLength: 240 },
    dueDate: { type: 'string', minLength: 1, maxLength: 30, nullable: true },
    status: { type: 'string', enum: OPS_MILESTONE_STATUS_ENUM },
  },
  required: ['title', 'status'],
  additionalProperties: false,
}

const setEngagementMilestonesArgsSchema: JSONSchemaType<OpsSetEngagementMilestonesArgs> = {
  type: 'object',
  properties: {
    engagementId: { type: 'string', minLength: 1, maxLength: 120 },
    milestones: {
      type: 'array',
      items: milestoneStatusArgItemSchema,
    },
    reason: { type: 'string', maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['engagementId', 'milestones'],
  additionalProperties: false,
}

export interface OpsRateCardArgItem {
  uid: string
  billRate: number
}

export interface OpsRetainerArgConfig {
  monthlyFee: number
  includedHours: number
  overageRate: number
  rolloverCapHours?: number | null
}

export interface OpsSetEngagementFinanceArgs {
  engagementId: string
  currency: string
  fixedPriceMinor?: number | null
  milestoneAmounts?: Record<string, number> | null
  retainer?: OpsRetainerArgConfig | null
  rateCard?: OpsRateCardArgItem[] | null
  targetMarginPercent?: number | null
  opId?: string | null
}

const rateCardArgItemSchema: JSONSchemaType<OpsRateCardArgItem> = {
  type: 'object',
  properties: {
    uid: { type: 'string', minLength: 1, maxLength: 120 },
    billRate: { type: 'integer', minimum: 0 },
  },
  required: ['uid', 'billRate'],
  additionalProperties: false,
}

const retainerArgConfigSchema: JSONSchemaType<OpsRetainerArgConfig> = {
  type: 'object',
  properties: {
    monthlyFee: { type: 'integer', minimum: 0 },
    includedHours: { type: 'number', minimum: 0 },
    overageRate: { type: 'integer', minimum: 0 },
    rolloverCapHours: { type: 'number', minimum: 0, nullable: true },
  },
  required: ['monthlyFee', 'includedHours', 'overageRate'],
  additionalProperties: false,
}

const setEngagementFinanceArgsSchema = {
  type: 'object',
  properties: {
    engagementId: { type: 'string', minLength: 1, maxLength: 120 },
    currency: { type: 'string', minLength: 3, maxLength: 3 },
    fixedPriceMinor: { type: 'integer', minimum: 0, nullable: true },
    milestoneAmounts: {
      type: 'object',
      required: [],
      additionalProperties: true,
      nullable: true,
    },
    retainer: { ...retainerArgConfigSchema, nullable: true },
    rateCard: {
      type: 'array',
      items: rateCardArgItemSchema,
      nullable: true,
    },
    targetMarginPercent: { type: 'number', minimum: 0, maximum: 100, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['engagementId', 'currency'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetEngagementFinanceArgs>

export interface OpsUpsertAllocationArgs {
  allocationId?: string | null
  engagementId: string
  uid: string
  hoursPerWeek?: number | null
  percent?: number | null
  from: string
  to?: string | null
  kind: OpsAllocationKind
  opId?: string | null
}

const upsertAllocationArgsSchema: JSONSchemaType<OpsUpsertAllocationArgs> = {
  type: 'object',
  properties: {
    allocationId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    engagementId: { type: 'string', minLength: 1, maxLength: 120 },
    uid: { type: 'string', minLength: 1, maxLength: 120 },
    hoursPerWeek: { type: 'number', minimum: 0, nullable: true },
    percent: { type: 'number', minimum: 0, maximum: 100, nullable: true },
    from: { type: 'string', minLength: 1, maxLength: 30 },
    to: { type: 'string', minLength: 1, maxLength: 30, nullable: true },
    kind: { type: 'string', enum: OPS_ALLOCATION_KIND_ENUM },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['engagementId', 'uid', 'from', 'kind'],
  additionalProperties: false,
}

export interface OpsEndAllocationArgs {
  allocationId: string
  toDate: string
  opId?: string | null
}

const endAllocationArgsSchema: JSONSchemaType<OpsEndAllocationArgs> = {
  type: 'object',
  properties: {
    allocationId: { type: 'string', minLength: 1, maxLength: 120 },
    toDate: { type: 'string', minLength: 1, maxLength: 30 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['allocationId', 'toDate'],
  additionalProperties: false,
}

export interface OpsLinkGoalToEngagementArgs {
  goalId: string
  engagementId: string
  opId?: string | null
}

const linkGoalToEngagementArgsSchema: JSONSchemaType<OpsLinkGoalToEngagementArgs> = {
  type: 'object',
  properties: {
    goalId: { type: 'string', minLength: 1, maxLength: 120 },
    engagementId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['goalId', 'engagementId'],
  additionalProperties: false,
}

export interface OpsUnlinkGoalFromEngagementArgs {
  goalId: string
  engagementId: string
  opId?: string | null
}

const unlinkGoalFromEngagementArgsSchema: JSONSchemaType<OpsUnlinkGoalFromEngagementArgs> = {
  type: 'object',
  properties: {
    goalId: { type: 'string', minLength: 1, maxLength: 120 },
    engagementId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['goalId', 'engagementId'],
  additionalProperties: false,
}

export interface OpsRecomputeEngagementMarginArgs {
  engagementId: string
}

const recomputeEngagementMarginArgsSchema: JSONSchemaType<OpsRecomputeEngagementMarginArgs> = {
  type: 'object',
  properties: {
    engagementId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['engagementId'],
  additionalProperties: false,
}

export interface OpsExportDeliveryCsvArgs {
  from: string
  to: string
  engagementId?: string | null
}

const exportDeliveryCsvArgsSchema: JSONSchemaType<OpsExportDeliveryCsvArgs> = {
  type: 'object',
  properties: {
    from: { type: 'string', minLength: 7, maxLength: 7 },
    to: { type: 'string', minLength: 7, maxLength: 7 },
    engagementId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['from', 'to'],
  additionalProperties: false,
}

export interface OpsDelRaiseChangeRequestArgs {
  engagementId: string
  title: string
  whatChanges: string
  hoursEstimate: number
  releaseTag?: string | null
  linkedNodeIds?: string[] | null
  opId?: string | null
}

const delRaiseChangeRequestArgsSchema: JSONSchemaType<OpsDelRaiseChangeRequestArgs> = {
  type: 'object',
  properties: {
    engagementId: { type: 'string', minLength: 1, maxLength: 120 },
    title: { type: 'string', minLength: 3, maxLength: 120 },
    whatChanges: { type: 'string', minLength: 3, maxLength: 2000 },
    hoursEstimate: { type: 'number', minimum: 0.5, maximum: 10000 },
    releaseTag: { type: 'string', minLength: 1, maxLength: 20, nullable: true },
    linkedNodeIds: {
      type: 'array',
      items: { type: 'string' },
      nullable: true,
      maxItems: 20,
    },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['engagementId', 'title', 'whatChanges', 'hoursEstimate'],
  additionalProperties: false,
}

export interface OpsDelSendClientLinksArgs {
  engagementId: string
  opId?: string | null
}

const delSendClientLinksArgsSchema: JSONSchemaType<OpsDelSendClientLinksArgs> = {
  type: 'object',
  properties: {
    engagementId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['engagementId'],
  additionalProperties: false,
}

export interface OpsDelRevokeClientLinkArgs {
  linkId: string
  opId?: string | null
}

const delRevokeClientLinkArgsSchema: JSONSchemaType<OpsDelRevokeClientLinkArgs> = {
  type: 'object',
  properties: {
    linkId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['linkId'],
  additionalProperties: false,
}

export interface OpsCreateApprovalArgs {
  subjectType: OutsideApprovalSubjectType
  subjectId: string
  approvers: CreateOutsideApprovalApproverInput[]
  mode: OutsideApprovalMode
  expiresInDays?: number | null
  message?: string | null
  opId?: string | null
}

const createApprovalApproverSchema: JSONSchemaType<CreateOutsideApprovalApproverInput> = {
  type: 'object',
  properties: {
    name: { type: 'string', minLength: 1, maxLength: 80 },
    phone: { type: 'string', minLength: 1, maxLength: 30 },
  },
  required: ['name', 'phone'],
  additionalProperties: false,
}

const createApprovalArgsSchema: JSONSchemaType<OpsCreateApprovalArgs> = {
  type: 'object',
  properties: {
    subjectType: { type: 'string', enum: OPS_APPROVAL_SUBJECT_TYPE_ENUM },
    subjectId: { type: 'string', minLength: 1 },
    approvers: {
      type: 'array',
      items: createApprovalApproverSchema,
      minItems: 1,
      maxItems: 5,
    },
    mode: { type: 'string', enum: OPS_APPROVAL_MODE_ENUM },
    expiresInDays: { type: 'integer', minimum: 1, maximum: 30, nullable: true },
    message: { type: 'string', maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['subjectType', 'subjectId', 'approvers', 'mode'],
  additionalProperties: false,
}

export interface OpsReissueApprovalLinkArgs {
  approvalId: string
  approverId: string
  opId?: string | null
}

const reissueApprovalLinkArgsSchema: JSONSchemaType<OpsReissueApprovalLinkArgs> = {
  type: 'object',
  properties: {
    approvalId: { type: 'string', minLength: 1 },
    approverId: { type: 'string', minLength: 1 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['approvalId', 'approverId'],
  additionalProperties: false,
}

export interface OpsRevokeApprovalArgs {
  approvalId: string
  reason: string
  opId?: string | null
}

const revokeApprovalArgsSchema: JSONSchemaType<OpsRevokeApprovalArgs> = {
  type: 'object',
  properties: {
    approvalId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['approvalId', 'reason'],
  additionalProperties: false,
}

export interface OpsSetApprovalSettingsArgs {
  adminOnlyKinds: string[]
  version?: number | null
  opId?: string | null
}

const setApprovalSettingsArgsSchema: JSONSchemaType<OpsSetApprovalSettingsArgs> = {
  type: 'object',
  properties: {
    adminOnlyKinds: {
      type: 'array',
      items: { type: 'string', minLength: 1, maxLength: 120 },
      maxItems: 200,
    },
    version: { type: 'integer', minimum: 1, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['adminOnlyKinds'],
  additionalProperties: false,
}

export interface OpsImportCsvArgs {
  sector?: OpsSector
  entity: OpsImportEntity
  csvText: string
  mapping?: Record<string, string>
  dryRun: boolean
  opId?: string
}

const importCsvArgsSchema: JSONSchemaType<OpsImportCsvArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM, nullable: true },
    entity: { type: 'string', enum: OPS_IMPORT_ENTITY_ENUM },
    csvText: { type: 'string', minLength: 1 },
    mapping: {
      type: 'object',
      required: [],
      nullable: true,
      additionalProperties: { type: 'string' },
    } as unknown as Record<string, unknown>,
    dryRun: { type: 'boolean' },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['entity', 'csvText', 'dryRun'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsImportCsvArgs>

export interface OpsExportCsvArgs {
  sector?: OpsSector
  entity: OpsImportEntity
  placeId?: string
}

const exportCsvArgsSchema: JSONSchemaType<OpsExportCsvArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM, nullable: true },
    entity: { type: 'string', enum: OPS_IMPORT_ENTITY_ENUM },
    placeId: { type: 'string', nullable: true },
  },
  required: ['entity'],
  additionalProperties: false,
}

export interface OpsSetWageRateArgs {
  personRef: Record<string, unknown>
  effectiveFrom: string
  dayRateMinor: number
  otHourRateMinor: number
  opId?: string
}

const setWageRateArgsSchema: JSONSchemaType<OpsSetWageRateArgs> = {
  type: 'object',
  properties: {
    personRef: { type: 'object', required: [] } as unknown as JSONSchemaType<Record<string, unknown>>,
    effectiveFrom: { type: 'string', minLength: 10, maxLength: 10 },
    dayRateMinor: { type: 'integer', minimum: 0 },
    otHourRateMinor: { type: 'integer', minimum: 0 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['personRef', 'effectiveFrom', 'dayRateMinor', 'otHourRateMinor'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetWageRateArgs>

export interface OpsPreviewWageSheetArgs {
  periodStart: string
  periodEnd: string
  partyId?: string
}

const previewWageSheetArgsSchema: JSONSchemaType<OpsPreviewWageSheetArgs> = {
  type: 'object',
  properties: {
    periodStart: { type: 'string', minLength: 10, maxLength: 10 },
    periodEnd: { type: 'string', minLength: 10, maxLength: 10 },
    partyId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['periodStart', 'periodEnd'],
  additionalProperties: false,
}

export interface OpsLockWageSheetArgs {
  periodStart: string
  periodEnd: string
  partyId?: string
  opId?: string
}

const lockWageSheetArgsSchema: JSONSchemaType<OpsLockWageSheetArgs> = {
  type: 'object',
  properties: {
    periodStart: { type: 'string', minLength: 10, maxLength: 10 },
    periodEnd: { type: 'string', minLength: 10, maxLength: 10 },
    partyId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['periodStart', 'periodEnd'],
  additionalProperties: false,
}

export interface OpsUnlockWageSheetArgs {
  sheetId: string
  reason: string
  opId?: string
}

const unlockWageSheetArgsSchema: JSONSchemaType<OpsUnlockWageSheetArgs> = {
  type: 'object',
  properties: {
    sheetId: { type: 'string', minLength: 1, maxLength: 240 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['sheetId', 'reason'],
  additionalProperties: false,
}

export interface OpsExportWageSheetCsvArgs {
  periodStart: string
  periodEnd: string
  partyId?: string
  sheetId?: string
}

const exportWageSheetCsvArgsSchema: JSONSchemaType<OpsExportWageSheetCsvArgs> = {
  type: 'object',
  properties: {
    periodStart: { type: 'string', minLength: 10, maxLength: 10 },
    periodEnd: { type: 'string', minLength: 10, maxLength: 10 },
    partyId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    sheetId: { type: 'string', minLength: 1, maxLength: 240, nullable: true },
  },
  required: ['periodStart', 'periodEnd'],
  additionalProperties: false,
}

export interface OpsGetWageSheetStatusArgs {
  sheetId: string
}

const getWageSheetStatusArgsSchema: JSONSchemaType<OpsGetWageSheetStatusArgs> = {
  type: 'object',
  properties: {
    sheetId: { type: 'string', minLength: 1, maxLength: 240 },
  },
  required: ['sheetId'],
  additionalProperties: false,
}

export interface OpsRequestEvidenceExportArgs {
  scope: {
    sectors: OpsSector[]
    placeIds?: string[]
    kindKeys?: string[]
    from: string
    to: string
    includeRestricted?: string[]
  }
  opId?: string
}

const requestEvidenceExportArgsSchema: JSONSchemaType<OpsRequestEvidenceExportArgs> = {
  type: 'object',
  properties: {
    scope: {
      type: 'object',
      properties: {
        sectors: {
          type: 'array',
          items: { type: 'string', enum: ['construction', 'hospital', 'sites', 'delivery', 'shops'] as const },
          minItems: 1,
        },
        placeIds: { type: 'array', items: { type: 'string' }, nullable: true },
        kindKeys: { type: 'array', items: { type: 'string' }, nullable: true },
        from: { type: 'string', minLength: 1 },
        to: { type: 'string', minLength: 1 },
        includeRestricted: { type: 'array', items: { type: 'string' }, nullable: true },
      },
      required: ['sectors', 'from', 'to'],
      additionalProperties: false,
    },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['scope'],
  additionalProperties: false,
}

export interface OpsGetEvidenceExportDownloadArgs {
  exportId: string
}

const getEvidenceExportDownloadArgsSchema: JSONSchemaType<OpsGetEvidenceExportDownloadArgs> = {
  type: 'object',
  properties: {
    exportId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['exportId'],
  additionalProperties: false,
}

export interface OpsSetRetentionSettingsArgs {
  classes: Record<string, { keepDays: number; reason?: string }>
  opId?: string
}

const setRetentionSettingsArgsSchema: JSONSchemaType<OpsSetRetentionSettingsArgs> = {
  type: 'object',
  properties: {
    classes: {
      type: 'object',
      required: [],
      additionalProperties: {
        type: 'object',
        properties: {
          keepDays: { type: 'integer', minimum: 30, maximum: 36500 },
          reason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
        },
        required: ['keepDays'],
        additionalProperties: false,
      },
    },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['classes'],
  additionalProperties: false,
}

export interface OpsRecordOpsRestrictedViewArgs {
  sourceType: 'REQUEST' | 'RECORD'
  sourceId: string
}

const recordOpsRestrictedViewArgsSchema: JSONSchemaType<OpsRecordOpsRestrictedViewArgs> = {
  type: 'object',
  properties: {
    sourceType: { type: 'string', enum: ['REQUEST', 'RECORD'] },
    sourceId: { type: 'string', minLength: 1, maxLength: 200 },
  },
  required: ['sourceType', 'sourceId'],
  additionalProperties: false,
}

export interface OpsSetOpsPrivacySettingsArgs {
  offlineMaxDays: number
}

const setOpsPrivacySettingsArgsSchema: JSONSchemaType<OpsSetOpsPrivacySettingsArgs> = {
  type: 'object',
  properties: {
    offlineMaxDays: { type: 'integer', minimum: 1, maximum: 30 },
  },
  required: ['offlineMaxDays'],
  additionalProperties: false,
}

export interface OpsSetLegalHoldArgs {
  target: {
    type: 'PLACE' | 'KIND' | 'RECORD'
    placeId?: string
    kindKey?: string
    source?: EvidenceSourceCollection
    recordId?: string
  }
  reason: string
  opId?: string
}

const setLegalHoldArgsSchema: JSONSchemaType<OpsSetLegalHoldArgs> = {
  type: 'object',
  properties: {
    target: {
      type: 'object',
      properties: {
        type: { type: 'string', enum: ['PLACE', 'KIND', 'RECORD'] as const },
        placeId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
        kindKey: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
        source: {
          type: 'string',
          enum: [
            'ops_records',
            'ops_requests',
            'ops_compliance_items',
            'ops_assets',
            'ops_approvals',
            'ops_attendance',
            'ops_rosters',
            'ops_wage_sheets',
          ] as const,
          nullable: true,
        },
        recordId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
      },
      required: ['type'],
      additionalProperties: false,
    },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['target', 'reason'],
  additionalProperties: false,
}

export interface OpsRequestLegalHoldLiftArgs {
  holdId: string
  reason: string
  opId?: string
}

const requestLegalHoldLiftArgsSchema: JSONSchemaType<OpsRequestLegalHoldLiftArgs> = {
  type: 'object',
  properties: {
    holdId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['holdId', 'reason'],
  additionalProperties: false,
}

export interface OpsConfirmLegalHoldLiftArgs {
  holdId: string
  opId?: string
}

const confirmLegalHoldLiftArgsSchema: JSONSchemaType<OpsConfirmLegalHoldLiftArgs> = {
  type: 'object',
  properties: {
    holdId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['holdId'],
  additionalProperties: false,
}

export interface OpsCancelLegalHoldLiftArgs {
  holdId: string
  opId?: string
}

const cancelLegalHoldLiftArgsSchema: JSONSchemaType<OpsCancelLegalHoldLiftArgs> = {
  type: 'object',
  properties: {
    holdId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['holdId'],
  additionalProperties: false,
}

export interface OpsRestoreFromRetentionBinArgs {
  binId: string
  opId?: string
}

const restoreFromRetentionBinArgsSchema: JSONSchemaType<OpsRestoreFromRetentionBinArgs> = {
  type: 'object',
  properties: {
    binId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['binId'],
  additionalProperties: false,
}

export interface OpsListSealedArchivesArgs {
  opId?: string
}

const listSealedArchivesArgsSchema: JSONSchemaType<OpsListSealedArchivesArgs> = {
  type: 'object',
  properties: {
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
}

export interface OpsRequestSealedArchiveExportArgs {
  archiveId: string
  opId?: string
}

const requestSealedArchiveExportArgsSchema: JSONSchemaType<OpsRequestSealedArchiveExportArgs> = {
  type: 'object',
  properties: {
    archiveId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['archiveId'],
  additionalProperties: false,
}

export interface OpsGetRetentionLockSummaryArgs {
  workspaceId?: string
  opId?: string
}

const getRetentionLockSummaryArgsSchema: JSONSchemaType<OpsGetRetentionLockSummaryArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
}

export interface OpsCheckOutRoomArgs {
  roomId: string
  expectedVersion?: number | null
  opId?: string | null
  source?: OpsRoomSource | null
}

const checkOutRoomArgsSchema: JSONSchemaType<OpsCheckOutRoomArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    source: { type: 'string', enum: OPS_ROOM_SOURCE_ENUM, nullable: true },
  },
  required: ['roomId'],
  additionalProperties: false,
}

export interface OpsMarkRoomOccupiedArgs {
  roomId: string
  expectedVersion?: number | null
  opId?: string | null
  source?: OpsRoomSource | null
}

const markRoomOccupiedArgsSchema: JSONSchemaType<OpsMarkRoomOccupiedArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    source: { type: 'string', enum: OPS_ROOM_SOURCE_ENUM, nullable: true },
  },
  required: ['roomId'],
  additionalProperties: false,
}

export interface OpsSetRoomDueOutArgs {
  roomId: string
  dueOutAt: number
  expectedVersion?: number | null
  opId?: string | null
  source?: OpsRoomSource | null
}

const setRoomDueOutArgsSchema: JSONSchemaType<OpsSetRoomDueOutArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    dueOutAt: { type: 'number' },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    source: { type: 'string', enum: OPS_ROOM_SOURCE_ENUM, nullable: true },
  },
  required: ['roomId', 'dueOutAt'],
  additionalProperties: false,
}

export interface OpsStartRoomCleanArgs {
  roomId: string
  expectedVersion?: number | null
  opId?: string | null
  source?: OpsRoomSource | null
}

const startRoomCleanArgsSchema: JSONSchemaType<OpsStartRoomCleanArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    source: { type: 'string', enum: OPS_ROOM_SOURCE_ENUM, nullable: true },
  },
  required: ['roomId'],
  additionalProperties: false,
}

export interface OpsMarkRoomCleanedArgs {
  roomId: string
  values: Record<string, string>
  expectedVersion?: number | null
  opId?: string | null
  source?: OpsRoomSource | null
}

const markRoomCleanedArgsSchema: JSONSchemaType<OpsMarkRoomCleanedArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    values: { type: 'object', required: [] },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    source: { type: 'string', enum: OPS_ROOM_SOURCE_ENUM, nullable: true },
  },
  required: ['roomId', 'values'],
  additionalProperties: false,
}

export interface OpsInspectRoomArgs {
  roomId: string
  passed: boolean
  failedItems?: string[] | null
  selfInspectionReason?: string | null
  expectedVersion?: number | null
  opId?: string | null
  source?: OpsRoomSource | null
}

const inspectRoomArgsSchema: JSONSchemaType<OpsInspectRoomArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    passed: { type: 'boolean' },
    failedItems: { type: 'array', items: { type: 'string' }, nullable: true },
    selfInspectionReason: { type: 'string', nullable: true },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    source: { type: 'string', enum: OPS_ROOM_SOURCE_ENUM, nullable: true },
  },
  required: ['roomId', 'passed'],
  additionalProperties: false,
}

export interface OpsSetRoomServiceArgs {
  roomId: string
  service: 'IN_SERVICE' | 'OUT_OF_ORDER' | 'OUT_OF_SERVICE'
  serviceReason?: string | null
  workOrderRequestId?: string | null
  expectedVersion?: number | null
  opId?: string | null
  source?: OpsRoomSource | null
}

const setRoomServiceArgsSchema: JSONSchemaType<OpsSetRoomServiceArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    service: { type: 'string', enum: ['IN_SERVICE', 'OUT_OF_ORDER', 'OUT_OF_SERVICE'] },
    serviceReason: { type: 'string', nullable: true },
    workOrderRequestId: { type: 'string', minLength: 1, nullable: true },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    source: { type: 'string', enum: OPS_ROOM_SOURCE_ENUM, nullable: true },
  },
  required: ['roomId', 'service'],
  additionalProperties: false,
}

export interface OpsSetRoomTagsArgs {
  roomId: string
  dnd?: boolean | null
  vip?: boolean | null
  expectedVersion?: number | null
  opId?: string | null
  source?: OpsRoomSource | null
}

const setRoomTagsArgsSchema: JSONSchemaType<OpsSetRoomTagsArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    dnd: { type: 'boolean', nullable: true },
    vip: { type: 'boolean', nullable: true },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    source: { type: 'string', enum: OPS_ROOM_SOURCE_ENUM, nullable: true },
  },
  required: ['roomId'],
  additionalProperties: false,
}

export interface OpsSetPropertyMinibarCheckArgs {
  propertyId: string
  minibarCheckOnCheckout: boolean
  opId?: string | null
}

const setPropertyMinibarCheckArgsSchema: JSONSchemaType<OpsSetPropertyMinibarCheckArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1 },
    minibarCheckOnCheckout: { type: 'boolean' },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['propertyId', 'minibarCheckOnCheckout'],
  additionalProperties: false,
}

export interface OpsSetPropertyGuestRequestsEnabledArgs {
  propertyId: string
  guestRequestsEnabled: boolean
  opId?: string | null
}

const setPropertyGuestRequestsEnabledArgsSchema: JSONSchemaType<OpsSetPropertyGuestRequestsEnabledArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1 },
    guestRequestsEnabled: { type: 'boolean' },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['propertyId', 'guestRequestsEnabled'],
  additionalProperties: false,
}

export interface OpsPrepareRoomCleanRecordArgs {
  roomId: string
  opId?: string | null
}

const prepareRoomCleanRecordArgsSchema: JSONSchemaType<OpsPrepareRoomCleanRecordArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['roomId'],
  additionalProperties: false,
}

export interface OpsAssignRoomsArgs {
  propertyId: string
  roomIds: string[]
  attendantUid: string
  day?: string | null
  opId?: string | null
}

const assignRoomsArgsSchema: JSONSchemaType<OpsAssignRoomsArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1 },
    roomIds: { type: 'array', items: { type: 'string' } },
    attendantUid: { type: 'string', minLength: 1 },
    day: { type: 'string', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['propertyId', 'roomIds', 'attendantUid'],
  additionalProperties: false,
}

export interface OpsUnassignRoomArgs {
  roomId: string
  expectedVersion?: number | null
  opId?: string | null
}

const unassignRoomArgsSchema: JSONSchemaType<OpsUnassignRoomArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['roomId'],
  additionalProperties: false,
}

export interface OpsReassignRoomArgs {
  roomId: string
  attendantUid: string
  day?: string | null
  expectedVersion?: number | null
  opId?: string | null
}

const reassignRoomArgsSchema: JSONSchemaType<OpsReassignRoomArgs> = {
  type: 'object',
  properties: {
    roomId: { type: 'string', minLength: 1 },
    attendantUid: { type: 'string', minLength: 1 },
    day: { type: 'string', nullable: true },
    expectedVersion: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['roomId', 'attendantUid'],
  additionalProperties: false,
}

export interface OpsUpsertCustomerArgs {
  customerId?: string
  displayName: string
  sectors: OpsSector[]
  tags?: string[]
  phone: string
  email?: string
  notes?: string
  consent?: {
    whatsapp?: boolean
    reminders?: boolean
    source?: 'DESK' | 'PUBLIC_LINK' | 'MEMBER_APP'
  }
  opId?: string
}

const upsertCustomerArgsSchema: JSONSchemaType<OpsUpsertCustomerArgs> = {
  type: 'object',
  properties: {
    customerId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    displayName: { type: 'string', minLength: 1, maxLength: 120 },
    sectors: {
      type: 'array',
      items: { type: 'string', enum: OPS_SECTOR_ENUM },
      minItems: 1,
    },
    tags: {
      type: 'array',
      items: { type: 'string', minLength: 1, maxLength: 40 },
      maxItems: 10,
      nullable: true,
    },
    phone: { type: 'string', minLength: 1, maxLength: 40 },
    email: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    notes: { type: 'string', maxLength: 500, nullable: true },
    consent: {
      type: 'object',
      properties: {
        whatsapp: { type: 'boolean', nullable: true },
        reminders: { type: 'boolean', nullable: true },
        source: {
          type: 'string',
          enum: ['DESK', 'PUBLIC_LINK', 'MEMBER_APP'] as const,
          nullable: true,
        },
      },
      nullable: true,
    },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['displayName', 'sectors', 'phone'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsUpsertCustomerArgs>

export interface OpsArchiveCustomerArgs {
  customerId: string
  opId?: string
}

const archiveCustomerArgsSchema: JSONSchemaType<OpsArchiveCustomerArgs> = {
  type: 'object',
  properties: {
    customerId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['customerId'],
  additionalProperties: false,
}

export interface OpsFindCustomerByPhoneArgs {
  phone: string
}

const findCustomerByPhoneArgsSchema: JSONSchemaType<OpsFindCustomerByPhoneArgs> = {
  type: 'object',
  properties: {
    phone: { type: 'string', minLength: 1, maxLength: 40 },
  },
  required: ['phone'],
  additionalProperties: false,
}

export interface OpsLinkMyCustomerRecordArgs {
  opId?: string
}

const linkMyCustomerRecordArgsSchema: JSONSchemaType<OpsLinkMyCustomerRecordArgs> = {
  type: 'object',
  properties: {
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
}

export interface OpsCreateBookingPlanArgs {
  sector: OpsSector
  name: string
  kind: 'MEMBERSHIP' | 'PACK' | 'DROP_IN'
  periodDays?: number
  sessionsTotal?: number
  validDays?: number
  weeklyCap?: number
  monthlyCap?: number
  bookableIds?: string[]
  price?: {
    amountMinor: number
    currency?: string
  }
  policy?: OpsBookingPlanPolicy
  opId?: string
}

const createBookingPlanArgsSchema: JSONSchemaType<OpsCreateBookingPlanArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    name: { type: 'string', minLength: 1, maxLength: 120 },
    kind: { type: 'string', enum: ['MEMBERSHIP', 'PACK', 'DROP_IN'] as const },
    periodDays: { type: 'integer', minimum: 1, maximum: 3660, nullable: true },
    sessionsTotal: { type: 'integer', minimum: 1, maximum: 500, nullable: true },
    validDays: { type: 'integer', minimum: 1, maximum: 3660, nullable: true },
    weeklyCap: { type: 'integer', minimum: 1, maximum: 100, nullable: true },
    monthlyCap: { type: 'integer', minimum: 1, maximum: 500, nullable: true },
    bookableIds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      nullable: true,
    },
    price: {
      type: 'object',
      properties: {
        amountMinor: { type: 'integer', minimum: 0 },
        currency: { type: 'string', nullable: true },
      },
      required: ['amountMinor'],
      nullable: true,
    },
    policy: { type: 'object', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['sector', 'name', 'kind'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCreateBookingPlanArgs>

export interface OpsUpdateBookingPlanArgs {
  planId: string
  name?: string
  weeklyCap?: number
  monthlyCap?: number
  bookableIds?: string[]
  price?: {
    amountMinor: number
    currency?: string
  }
  policy?: OpsBookingPlanPolicy
  opId?: string
}

const updateBookingPlanArgsSchema: JSONSchemaType<OpsUpdateBookingPlanArgs> = {
  type: 'object',
  properties: {
    planId: { type: 'string', minLength: 1, maxLength: 120 },
    name: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    weeklyCap: { type: 'integer', minimum: 1, maximum: 100, nullable: true },
    monthlyCap: { type: 'integer', minimum: 1, maximum: 500, nullable: true },
    bookableIds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      nullable: true,
    },
    price: {
      type: 'object',
      properties: {
        amountMinor: { type: 'integer', minimum: 0 },
        currency: { type: 'string', nullable: true },
      },
      required: ['amountMinor'],
      nullable: true,
    },
    policy: { type: 'object', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['planId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsUpdateBookingPlanArgs>

export interface OpsArchiveBookingPlanArgs {
  planId: string
  opId?: string
}

const archiveBookingPlanArgsSchema: JSONSchemaType<OpsArchiveBookingPlanArgs> = {
  type: 'object',
  properties: {
    planId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['planId'],
  additionalProperties: false,
}

export interface OpsGrantMembershipArgs {
  customerId: string
  planId: string
  validFrom?: string
  recordDue?: boolean
  opId?: string
}

const grantMembershipArgsSchema: JSONSchemaType<OpsGrantMembershipArgs> = {
  type: 'object',
  properties: {
    customerId: { type: 'string', minLength: 1, maxLength: 120 },
    planId: { type: 'string', minLength: 1, maxLength: 120 },
    validFrom: { type: 'string', minLength: 10, maxLength: 30, nullable: true },
    recordDue: { type: 'boolean', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['customerId', 'planId'],
  additionalProperties: false,
}

export interface OpsRenewMembershipArgs {
  membershipId: string
  recordDue?: boolean
  opId?: string
}

const renewMembershipArgsSchema: JSONSchemaType<OpsRenewMembershipArgs> = {
  type: 'object',
  properties: {
    membershipId: { type: 'string', minLength: 1, maxLength: 120 },
    recordDue: { type: 'boolean', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['membershipId'],
  additionalProperties: false,
}

export interface OpsFreezeMembershipArgs {
  membershipId: string
  fromDate: string
  toDate: string
  reason?: string
  opId?: string
}

const freezeMembershipArgsSchema: JSONSchemaType<OpsFreezeMembershipArgs> = {
  type: 'object',
  properties: {
    membershipId: { type: 'string', minLength: 1, maxLength: 120 },
    fromDate: { type: 'string', minLength: 10, maxLength: 20 },
    toDate: { type: 'string', minLength: 10, maxLength: 20 },
    reason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['membershipId', 'fromDate', 'toDate'],
  additionalProperties: false,
}

export interface OpsUnfreezeMembershipArgs {
  membershipId: string
  reason?: string
  opId?: string
}

const unfreezeMembershipArgsSchema: JSONSchemaType<OpsUnfreezeMembershipArgs> = {
  type: 'object',
  properties: {
    membershipId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['membershipId'],
  additionalProperties: false,
}

export interface OpsCancelMembershipArgs {
  membershipId: string
  reason: string
  opId?: string
}

const cancelMembershipArgsSchema: JSONSchemaType<OpsCancelMembershipArgs> = {
  type: 'object',
  properties: {
    membershipId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['membershipId', 'reason'],
  additionalProperties: false,
}

export interface OpsRecordDuePaidArgs {
  dueId: string
  receiptRef?: string
  opId?: string
}

const recordDuePaidArgsSchema: JSONSchemaType<OpsRecordDuePaidArgs> = {
  type: 'object',
  properties: {
    dueId: { type: 'string', minLength: 1, maxLength: 120 },
    receiptRef: { type: 'string', minLength: 1, maxLength: 60, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['dueId'],
  additionalProperties: false,
}

export interface OpsWaiveDueArgs {
  dueId: string
  reason: string
  opId?: string
}

const waiveDueArgsSchema: JSONSchemaType<OpsWaiveDueArgs> = {
  type: 'object',
  properties: {
    dueId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['dueId', 'reason'],
  additionalProperties: false,
}

export interface OpsCreateBookableArgs {
  sector: OpsSector
  placeId: string
  kind: 'CLASS' | 'SLOT' | 'APPOINTMENT' | 'RESOURCE'
  name: string
  durationMin: number
  capacity: number
  pattern: Record<string, unknown>
  staff?: Array<{ kind: 'MEMBER' | 'WORKER'; id: string }>
  holds?: string[]
  bookAheadDays?: number
  public?: boolean
  planIds?: string[]
  opId?: string
}

const createBookableArgsSchema: JSONSchemaType<OpsCreateBookableArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    placeId: { type: 'string', minLength: 1 },
    kind: { type: 'string', enum: ['CLASS', 'SLOT', 'APPOINTMENT', 'RESOURCE'] as const },
    name: { type: 'string', minLength: 1, maxLength: 120 },
    durationMin: { type: 'integer', minimum: 5, maximum: 720 },
    capacity: { type: 'integer', minimum: 1, maximum: 500 },
    pattern: { type: 'object' },
    staff: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          kind: { type: 'string', enum: ['MEMBER', 'WORKER'] as const },
          id: { type: 'string', minLength: 1 },
        },
        required: ['kind', 'id'],
      },
      nullable: true,
    },
    holds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      nullable: true,
    },
    bookAheadDays: { type: 'integer', minimum: 1, maximum: 180, nullable: true },
    public: { type: 'boolean', nullable: true },
    planIds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      nullable: true,
    },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['sector', 'placeId', 'kind', 'name', 'durationMin', 'capacity', 'pattern'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCreateBookableArgs>

export interface OpsUpdateBookableArgs {
  bookableId: string
  placeId?: string
  name?: string
  durationMin?: number
  capacity?: number
  pattern?: Record<string, unknown>
  staff?: Array<{ kind: 'MEMBER' | 'WORKER'; id: string }>
  holds?: string[]
  bookAheadDays?: number
  public?: boolean
  planIds?: string[]
  opId?: string
}

const updateBookableArgsSchema: JSONSchemaType<OpsUpdateBookableArgs> = {
  type: 'object',
  properties: {
    bookableId: { type: 'string', minLength: 1, maxLength: 120 },
    placeId: { type: 'string', minLength: 1, nullable: true },
    name: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    durationMin: { type: 'integer', minimum: 5, maximum: 720, nullable: true },
    capacity: { type: 'integer', minimum: 1, maximum: 500, nullable: true },
    pattern: { type: 'object', nullable: true },
    staff: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          kind: { type: 'string', enum: ['MEMBER', 'WORKER'] as const },
          id: { type: 'string', minLength: 1 },
        },
        required: ['kind', 'id'],
      },
      nullable: true,
    },
    holds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      nullable: true,
    },
    bookAheadDays: { type: 'integer', minimum: 1, maximum: 180, nullable: true },
    public: { type: 'boolean', nullable: true },
    planIds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      nullable: true,
    },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['bookableId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsUpdateBookableArgs>

export interface OpsArchiveBookableArgs {
  bookableId: string
  opId?: string
}

const archiveBookableArgsSchema: JSONSchemaType<OpsArchiveBookableArgs> = {
  type: 'object',
  properties: {
    bookableId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['bookableId'],
  additionalProperties: false,
}

export interface OpsBookSessionArgs {
  customerId: string
  bookableId: string
  date: string
  membershipId?: string
  waitlist?: boolean
  opId?: string
}

const bookSessionArgsSchema: JSONSchemaType<OpsBookSessionArgs> = {
  type: 'object',
  properties: {
    customerId: { type: 'string', minLength: 1, maxLength: 120 },
    bookableId: { type: 'string', minLength: 1, maxLength: 120 },
    date: { type: 'string', minLength: 10, maxLength: 20 },
    membershipId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    waitlist: { type: 'boolean', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['customerId', 'bookableId', 'date'],
  additionalProperties: false,
}

export interface OpsBookInHouseExtraArgs {
  bookableId: string
  roomPlaceId: string
  date: string
  guests?: number
  waitlist?: boolean
  opId?: string
}

const bookInHouseExtraArgsSchema: JSONSchemaType<OpsBookInHouseExtraArgs> = {
  type: 'object',
  properties: {
    bookableId: { type: 'string', minLength: 1, maxLength: 120 },
    roomPlaceId: { type: 'string', minLength: 1, maxLength: 120 },
    date: { type: 'string', minLength: 10, maxLength: 20 },
    guests: { type: 'integer', minimum: 1, maximum: 20, nullable: true },
    waitlist: { type: 'boolean', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['bookableId', 'roomPlaceId', 'date'],
  additionalProperties: false,
}

export interface OpsConfirmGuestExtraRequestArgs {
  requestId: string
  sessionKey?: string
  opId?: string
}

const confirmGuestExtraRequestArgsSchema: JSONSchemaType<OpsConfirmGuestExtraRequestArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1, maxLength: 120 },
    sessionKey: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['requestId'],
  additionalProperties: false,
}

export interface OpsDeclineGuestExtraRequestArgs {
  requestId: string
  reason: string
  opId?: string
}

const declineGuestExtraRequestArgsSchema: JSONSchemaType<OpsDeclineGuestExtraRequestArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 1, maxLength: 300 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['requestId', 'reason'],
  additionalProperties: false,
}

export interface OpsCancelBookingArgs {
  bookingId: string
  reason?: string
  opId?: string
}

const cancelBookingArgsSchema: JSONSchemaType<OpsCancelBookingArgs> = {
  type: 'object',
  properties: {
    bookingId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 1, maxLength: 500, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['bookingId'],
  additionalProperties: false,
}

export interface OpsMoveBookingArgs {
  bookingId: string
  targetBookableId: string
  targetDate: string
  waitlist?: boolean
  opId?: string
}

const moveBookingArgsSchema: JSONSchemaType<OpsMoveBookingArgs> = {
  type: 'object',
  properties: {
    bookingId: { type: 'string', minLength: 1, maxLength: 120 },
    targetBookableId: { type: 'string', minLength: 1, maxLength: 120 },
    targetDate: { type: 'string', minLength: 10, maxLength: 20 },
    waitlist: { type: 'boolean', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['bookingId', 'targetBookableId', 'targetDate'],
  additionalProperties: false,
}

export interface OpsLeaveWaitlistArgs {
  bookingId: string
  opId?: string
}

const leaveWaitlistArgsSchema: JSONSchemaType<OpsLeaveWaitlistArgs> = {
  type: 'object',
  properties: {
    bookingId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['bookingId'],
  additionalProperties: false,
}

export interface OpsWaiveStrikeArgs {
  customerId: string
  reason: string
  opId?: string
}

const waiveStrikeArgsSchema: JSONSchemaType<OpsWaiveStrikeArgs> = {
  type: 'object',
  properties: {
    customerId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['customerId', 'reason'],
  additionalProperties: false,
}

export interface OpsClearBookingBlockArgs {
  customerId: string
  reason: string
  opId?: string
}

const clearBookingBlockArgsSchema: JSONSchemaType<OpsClearBookingBlockArgs> = {
  type: 'object',
  properties: {
    customerId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['customerId', 'reason'],
  additionalProperties: false,
}

export interface OpsCreateStaysPmsConnectionArgs {
  adapter?: PmsAdapterType
  writeBack?: {
    enabled: boolean
    callbackUrl?: string
  }
  propertyMap?: Record<string, string>
  opId?: string
}

const createStaysPmsConnectionArgsSchema: JSONSchemaType<OpsCreateStaysPmsConnectionArgs> = {
  type: 'object',
  properties: {
    adapter: { type: 'string', enum: PMS_ADAPTER_ENUM, nullable: true },
    writeBack: {
      type: 'object',
      properties: {
        enabled: { type: 'boolean' },
        callbackUrl: { type: 'string', minLength: 1, maxLength: 500, nullable: true },
      },
      required: ['enabled'],
      nullable: true,
      additionalProperties: false,
    },
    propertyMap: {
      type: 'object',
      required: [],
      nullable: true,
      additionalProperties: { type: 'string' },
    } as unknown as Record<string, unknown>,
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCreateStaysPmsConnectionArgs>

export interface OpsRotateStaysPmsSecretArgs {
  opId?: string
}

const rotateStaysPmsSecretArgsSchema: JSONSchemaType<OpsRotateStaysPmsSecretArgs> = {
  type: 'object',
  properties: {
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
}

export interface OpsUpdateStaysPmsConnectionArgs {
  writeBack?: {
    enabled: boolean
    callbackUrl?: string
  }
  propertyMap?: Record<string, string>
  status?: 'ACTIVE' | 'DISABLED'
  enabled?: boolean
  opId?: string
}

const updateStaysPmsConnectionArgsSchema: JSONSchemaType<OpsUpdateStaysPmsConnectionArgs> = {
  type: 'object',
  properties: {
    writeBack: {
      type: 'object',
      properties: {
        enabled: { type: 'boolean' },
        callbackUrl: { type: 'string', minLength: 1, maxLength: 500, nullable: true },
      },
      required: ['enabled'],
      nullable: true,
      additionalProperties: false,
    },
    propertyMap: {
      type: 'object',
      required: [],
      nullable: true,
      additionalProperties: { type: 'string' },
    } as unknown as Record<string, unknown>,
    status: { type: 'string', enum: ['ACTIVE', 'DISABLED'], nullable: true },
    enabled: { type: 'boolean', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsUpdateStaysPmsConnectionArgs>

export interface OpsDeleteStaysPmsConnectionArgs {
  opId?: string
}

const deleteStaysPmsConnectionArgsSchema: JSONSchemaType<OpsDeleteStaysPmsConnectionArgs> = {
  type: 'object',
  properties: {
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
}

export interface OpsGetStaysPmsStatusArgs {
  opId?: string
}

const getStaysPmsStatusArgsSchema: JSONSchemaType<OpsGetStaysPmsStatusArgs> = {
  type: 'object',
  properties: {
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
}

export interface OpsRetryStaysPmsOutboxArgs {
  outboxIds?: string[]
  opId?: string
}

const retryStaysPmsOutboxArgsSchema: JSONSchemaType<OpsRetryStaysPmsOutboxArgs> = {
  type: 'object',
  properties: {
    outboxIds: {
      type: 'array',
      items: { type: 'string', minLength: 1, maxLength: 120 },
      nullable: true,
    },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsRetryStaysPmsOutboxArgs>

export interface OpsTestStaysPmsEzeeConnectionArgs {
  hotelCode?: string
  authCode?: string
  propertyMap?: Record<string, string>
  opId?: string
}

const testStaysPmsEzeeConnectionArgsSchema: JSONSchemaType<OpsTestStaysPmsEzeeConnectionArgs> = {
  type: 'object',
  properties: {
    hotelCode: { type: 'string', minLength: 1, maxLength: 100, nullable: true },
    authCode: { type: 'string', minLength: 1, maxLength: 256, nullable: true },
    propertyMap: {
      type: 'object',
      required: [],
      nullable: true,
      additionalProperties: { type: 'string' },
    } as unknown as Record<string, unknown>,
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsTestStaysPmsEzeeConnectionArgs>

export interface OpsTestStaysPmsHotelogixConnectionArgs {
  consumerKey?: string
  consumerSecret?: string
  hotelId?: string
  counterId?: string
  email?: string
  password?: string
  propertyMap?: Record<string, string>
  opId?: string
}

const testStaysPmsHotelogixConnectionArgsSchema: JSONSchemaType<OpsTestStaysPmsHotelogixConnectionArgs> = {
  type: 'object',
  properties: {
    consumerKey: { type: 'string', minLength: 1, maxLength: 256, nullable: true },
    consumerSecret: { type: 'string', minLength: 1, maxLength: 256, nullable: true },
    hotelId: { type: 'string', minLength: 1, maxLength: 100, nullable: true },
    counterId: { type: 'string', minLength: 1, maxLength: 100, nullable: true },
    email: { type: 'string', minLength: 1, maxLength: 200, nullable: true },
    password: { type: 'string', minLength: 1, maxLength: 200, nullable: true },
    propertyMap: {
      type: 'object',
      required: [],
      nullable: true,
      additionalProperties: { type: 'string' },
    } as unknown as Record<string, unknown>,
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsTestStaysPmsHotelogixConnectionArgs>

export interface OpsImportStaysPmsReportArgs {
  propertyCode: string
  rows: ReducedStayflexiRow[]
  columnMap?: Record<string, string>
}

const importStaysPmsReportArgsSchema: JSONSchemaType<OpsImportStaysPmsReportArgs> = {
  type: 'object',
  properties: {
    propertyCode: { type: 'string', minLength: 1, maxLength: 64 },
    rows: {
      type: 'array',
      minItems: 1,
      maxItems: 2000,
      items: {
        type: 'object',
        properties: {
          roomName: { type: 'string', minLength: 1, maxLength: 40 },
          occupancy: { type: 'string', enum: ['OCCUPIED', 'VACANT'], nullable: true },
          hkStatus: { type: 'string', enum: ['CLEAN', 'DIRTY', 'INSPECTED'], nullable: true },
          blocked: { type: 'boolean' },
          dueOut: { type: 'boolean' },
          stayover: { type: 'boolean' },
        },
        required: ['roomName', 'blocked', 'dueOut', 'stayover'],
        additionalProperties: false,
      },
    } as unknown as Record<string, unknown>,
    columnMap: {
      type: 'object',
      required: [],
      nullable: true,
      additionalProperties: { type: 'string' },
    } as unknown as Record<string, unknown>,
  },
  required: ['propertyCode', 'rows'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsImportStaysPmsReportArgs>

export interface OpsCreateOpsConnectorArgs {
  name: string
  kind?: string
  sector?: string
  eventMap?: Record<string, string>
  opId?: string
}

const createOpsConnectorArgsSchema: JSONSchemaType<OpsCreateOpsConnectorArgs> = {
  type: 'object',
  properties: {
    name: { type: 'string', minLength: 1, maxLength: 120 },
    kind: { type: 'string', nullable: true },
    sector: { type: 'string', nullable: true },
    eventMap: {
      type: 'object',
      required: [],
      nullable: true,
      additionalProperties: { type: 'string' },
    } as unknown as Record<string, unknown>,
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['name'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCreateOpsConnectorArgs>

export interface OpsRotateOpsConnectorSecretArgs {
  connectorId: string
  opId?: string
}

const rotateOpsConnectorSecretArgsSchema: JSONSchemaType<OpsRotateOpsConnectorSecretArgs> = {
  type: 'object',
  properties: {
    connectorId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['connectorId'],
  additionalProperties: false,
}

export interface OpsUpdateOpsConnectorArgs {
  connectorId: string
  name?: string
  state?: 'ACTIVE' | 'PAUSED'
  eventMap?: Record<string, string>
  opId?: string
}

const updateOpsConnectorArgsSchema: JSONSchemaType<OpsUpdateOpsConnectorArgs> = {
  type: 'object',
  properties: {
    connectorId: { type: 'string', minLength: 1, maxLength: 120 },
    name: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    state: { type: 'string', enum: ['ACTIVE', 'PAUSED'], nullable: true },
    eventMap: {
      type: 'object',
      required: [],
      nullable: true,
      additionalProperties: { type: 'string' },
    } as unknown as Record<string, unknown>,
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['connectorId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsUpdateOpsConnectorArgs>

export interface OpsDeleteOpsConnectorArgs {
  connectorId: string
  opId?: string
}

const deleteOpsConnectorArgsSchema: JSONSchemaType<OpsDeleteOpsConnectorArgs> = {
  type: 'object',
  properties: {
    connectorId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['connectorId'],
  additionalProperties: false,
}

export interface OpsGetOpsConnectorStatusArgs {
  connectorId: string
}

const getOpsConnectorStatusArgsSchema: JSONSchemaType<OpsGetOpsConnectorStatusArgs> = {
  type: 'object',
  properties: {
    connectorId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['connectorId'],
  additionalProperties: false,
}

export interface OpsReplayOpsConnectorEventArgs {
  connectorId: string
  eventId: string
  opId?: string
}

const replayOpsConnectorEventArgsSchema: JSONSchemaType<OpsReplayOpsConnectorEventArgs> = {
  type: 'object',
  properties: {
    connectorId: { type: 'string', minLength: 1, maxLength: 120 },
    eventId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['connectorId', 'eventId'],
  additionalProperties: false,
}

export interface OpsIssueQueueTokenArgs {
  placeId: string
  customerId?: string
  bookingId?: string
  opId?: string
}

const issueQueueTokenArgsSchema: JSONSchemaType<OpsIssueQueueTokenArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1, maxLength: 120 },
    customerId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    bookingId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsCallNextTokenArgs {
  placeId: string
  opId?: string
}

const callNextTokenArgsSchema: JSONSchemaType<OpsCallNextTokenArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsMarkTokenServedArgs {
  tokenId: string
  opId?: string
}

const markTokenServedArgsSchema: JSONSchemaType<OpsMarkTokenServedArgs> = {
  type: 'object',
  properties: {
    tokenId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['tokenId'],
  additionalProperties: false,
}

export interface OpsSkipTokenArgs {
  tokenId: string
  opId?: string
}

const skipTokenArgsSchema: JSONSchemaType<OpsSkipTokenArgs> = {
  type: 'object',
  properties: {
    tokenId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['tokenId'],
  additionalProperties: false,
}

export interface OpsGetQueueBoardArgs {
  placeId: string
}

const getQueueBoardArgsSchema: JSONSchemaType<OpsGetQueueBoardArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsListQueueTokensArgs {
  placeId: string
}

const listQueueTokensArgsSchema: JSONSchemaType<OpsListQueueTokensArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['placeId'],
  additionalProperties: false,
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface OpsGetMyBookingQrArgs {}

const getMyBookingQrArgsSchema: JSONSchemaType<OpsGetMyBookingQrArgs> = {
  type: 'object',
  properties: {},
  additionalProperties: false,
}

export interface OpsCheckInByCustomerQrArgs {
  token: string
  placeId: string
  opId?: string
}

const checkInByCustomerQrArgsSchema: JSONSchemaType<OpsCheckInByCustomerQrArgs> = {
  type: 'object',
  properties: {
    token: { type: 'string', minLength: 1, maxLength: 200 },
    placeId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['token', 'placeId'],
  additionalProperties: false,
}

export interface OpsBookingCheckInByPlaceQrArgs {
  placeToken: string
  opId?: string
}

const bookingCheckInByPlaceQrArgsSchema: JSONSchemaType<OpsBookingCheckInByPlaceQrArgs> = {
  type: 'object',
  properties: {
    placeToken: { type: 'string', minLength: 1, maxLength: 200 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['placeToken'],
  additionalProperties: false,
}

export interface OpsPrepareBookingCheckInOtpArgs {
  bookingId: string
  opId?: string
}

const prepareBookingCheckInOtpArgsSchema: JSONSchemaType<OpsPrepareBookingCheckInOtpArgs> = {
  type: 'object',
  properties: {
    bookingId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['bookingId'],
  additionalProperties: false,
}

export interface OpsCheckInByPhoneOtpArgs {
  idToken: string
  bookingId: string
  opId?: string
}

const checkInByPhoneOtpArgsSchema: JSONSchemaType<OpsCheckInByPhoneOtpArgs> = {
  type: 'object',
  properties: {
    idToken: { type: 'string', minLength: 1, maxLength: 4000 },
    bookingId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['idToken', 'bookingId'],
  additionalProperties: false,
}

export interface OpsManualCheckInArgs {
  bookingId: string
  reason: string
  opId?: string
}

const manualCheckInArgsSchema: JSONSchemaType<OpsManualCheckInArgs> = {
  type: 'object',
  properties: {
    bookingId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['bookingId', 'reason'],
  additionalProperties: false,
}

export interface OpsSetPublicBookingLinkArgs {
  enabled: boolean
  sectors?: OpsSector[]
  opId?: string
}

const setPublicBookingLinkArgsSchema: JSONSchemaType<OpsSetPublicBookingLinkArgs> = {
  type: 'object',
  properties: {
    enabled: { type: 'boolean' },
    sectors: { type: 'array', items: { type: 'string', enum: OPS_SECTOR_ENUM }, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['enabled'],
  additionalProperties: false,
}

export interface OpsGetPublicBookingInfoArgs {
  token: string
}

const getPublicBookingInfoArgsSchema: JSONSchemaType<OpsGetPublicBookingInfoArgs> = {
  type: 'object',
  properties: {
    token: { type: 'string', minLength: 1, maxLength: 200 },
  },
  required: ['token'],
  additionalProperties: false,
}

export interface OpsPublicBookSessionArgs {
  token: string
  bookableId: string
  sessionKey: string
  name?: string
  consent: {
    whatsapp: boolean
    reminders: boolean
  }
  opId?: string
}

const publicBookSessionArgsSchema: JSONSchemaType<OpsPublicBookSessionArgs> = {
  type: 'object',
  properties: {
    token: { type: 'string', minLength: 1, maxLength: 200 },
    bookableId: { type: 'string', minLength: 1, maxLength: 120 },
    sessionKey: { type: 'string', minLength: 1, maxLength: 120 },
    name: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    consent: {
      type: 'object',
      properties: {
        whatsapp: { type: 'boolean' },
        reminders: { type: 'boolean' },
      },
      required: ['whatsapp', 'reminders'],
    },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['token', 'bookableId', 'sessionKey', 'consent'],
  additionalProperties: false,
}

export interface OpsPublicCancelBookingArgs {
  token: string
  bookingId: string
  reason?: string
  opId?: string
}

const publicCancelBookingArgsSchema: JSONSchemaType<OpsPublicCancelBookingArgs> = {
  type: 'object',
  properties: {
    token: { type: 'string', minLength: 1, maxLength: 200 },
    bookingId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 1, maxLength: 500, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['token', 'bookingId'],
  additionalProperties: false,
}

export interface OpsListMyPublicBookingsArgs {
  token: string
}

const listMyPublicBookingsArgsSchema: JSONSchemaType<OpsListMyPublicBookingsArgs> = {
  type: 'object',
  properties: {
    token: { type: 'string', minLength: 1, maxLength: 200 },
  },
  required: ['token'],
  additionalProperties: false,
}

export interface OpsListRenewalsDueArgs {
  sector: OpsSector
}

const listRenewalsDueArgsSchema: JSONSchemaType<OpsListRenewalsDueArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
  },
  required: ['sector'],
  additionalProperties: false,
}

export interface OpsListChurnCallsArgs {
  sector: OpsSector
  days: 14 | 30
}

const listChurnCallsArgsSchema: JSONSchemaType<OpsListChurnCallsArgs> = {
  type: 'object',
  properties: {
    sector: { type: 'string', enum: OPS_SECTOR_ENUM },
    days: { type: 'integer', enum: [14, 30] },
  },
  required: ['sector', 'days'],
  additionalProperties: false,
}

export interface OpsRecordCustomerCallArgs {
  customerId: string
  outcome: OpsCustomerCallOutcome
  note?: string
  opId?: string
}

const recordCustomerCallArgsSchema: JSONSchemaType<OpsRecordCustomerCallArgs> = {
  type: 'object',
  properties: {
    customerId: { type: 'string', minLength: 1, maxLength: 120 },
    outcome: { type: 'string', enum: OPS_CUSTOMER_CALL_OUTCOME_ENUM },
    note: { type: 'string', minLength: 1, maxLength: 500, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['customerId', 'outcome'],
  additionalProperties: false,
}

export interface OpsCreateDeviceEnrolCodeArgs {
  placeId: string
  label: string
  opId?: string
}

const createDeviceEnrolCodeArgsSchema: JSONSchemaType<OpsCreateDeviceEnrolCodeArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1, maxLength: 120 },
    label: { type: 'string', minLength: 1, maxLength: 60 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['placeId', 'label'],
  additionalProperties: false,
}

export interface OpsEnrolSharedDeviceArgs {
  code: string
}

const enrolSharedDeviceArgsSchema: JSONSchemaType<OpsEnrolSharedDeviceArgs> = {
  type: 'object',
  properties: {
    code: { type: 'string', minLength: 1, maxLength: 32 },
  },
  required: ['code'],
  additionalProperties: false,
}

export interface OpsRevokeSharedDeviceArgs {
  deviceId: string
  opId?: string
}

const revokeSharedDeviceArgsSchema: JSONSchemaType<OpsRevokeSharedDeviceArgs> = {
  type: 'object',
  properties: {
    deviceId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['deviceId'],
  additionalProperties: false,
}

export interface OpsSetSharedDeviceSettingsArgs {
  deviceId: string
  idleLockMinutes: number
  label?: string
  opId?: string
}

const setSharedDeviceSettingsArgsSchema: JSONSchemaType<OpsSetSharedDeviceSettingsArgs> = {
  type: 'object',
  properties: {
    deviceId: { type: 'string', minLength: 1, maxLength: 120 },
    idleLockMinutes: { type: 'integer', minimum: 1, maximum: 15 },
    label: { type: 'string', minLength: 1, maxLength: 60, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['deviceId', 'idleLockMinutes'],
  additionalProperties: false,
}

export interface OpsListSharedDevicesArgs {
  dummy?: string
}

const listSharedDevicesArgsSchema: JSONSchemaType<OpsListSharedDevicesArgs> = {
  type: 'object',
  properties: {
    dummy: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: false,
}

export interface OpsListSharedDeviceSessionsArgs {
  deviceId?: string
  limit?: number
}

const listSharedDeviceSessionsArgsSchema: JSONSchemaType<OpsListSharedDeviceSessionsArgs> = {
  type: 'object',
  properties: {
    deviceId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    limit: { type: 'integer', minimum: 1, maximum: 100, nullable: true },
  },
  required: [],
  additionalProperties: false,
}

export interface OpsSetMySharedDevicePinArgs {
  pin: string
  opId?: string
}

const setMySharedDevicePinArgsSchema: JSONSchemaType<OpsSetMySharedDevicePinArgs> = {
  type: 'object',
  properties: {
    pin: { type: 'string', minLength: 4, maxLength: 4 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['pin'],
  additionalProperties: false,
}

export interface OpsSetSharedDevicePinByPhoneArgs {
  deviceId: string
  deviceSecret: string
  phoneIdToken: string
  pin: string
  opId?: string
}

const setSharedDevicePinByPhoneArgsSchema: JSONSchemaType<OpsSetSharedDevicePinByPhoneArgs> = {
  type: 'object',
  properties: {
    deviceId: { type: 'string', minLength: 1, maxLength: 120 },
    deviceSecret: { type: 'string', minLength: 1, maxLength: 200 },
    phoneIdToken: { type: 'string', minLength: 1, maxLength: 4096 },
    pin: { type: 'string', minLength: 4, maxLength: 4 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['deviceId', 'deviceSecret', 'phoneIdToken', 'pin'],
  additionalProperties: false,
}

export interface OpsResetSharedDevicePinArgs {
  uid: string
  opId?: string
}

const resetSharedDevicePinArgsSchema: JSONSchemaType<OpsResetSharedDevicePinArgs> = {
  type: 'object',
  properties: {
    uid: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['uid'],
  additionalProperties: false,
}

export interface OpsListSharedDevicePeopleArgs {
  deviceId: string
  deviceSecret: string
}

const listSharedDevicePeopleArgsSchema: JSONSchemaType<OpsListSharedDevicePeopleArgs> = {
  type: 'object',
  properties: {
    deviceId: { type: 'string', minLength: 1, maxLength: 120 },
    deviceSecret: { type: 'string', minLength: 1, maxLength: 200 },
  },
  required: ['deviceId', 'deviceSecret'],
  additionalProperties: false,
}

export interface OpsSharedDeviceSwitchInArgs {
  deviceId: string
  deviceSecret: string
  uid: string
  pin: string
  opId?: string
}

const sharedDeviceSwitchInArgsSchema: JSONSchemaType<OpsSharedDeviceSwitchInArgs> = {
  type: 'object',
  properties: {
    deviceId: { type: 'string', minLength: 1, maxLength: 120 },
    deviceSecret: { type: 'string', minLength: 1, maxLength: 200 },
    uid: { type: 'string', minLength: 1, maxLength: 120 },
    pin: { type: 'string', minLength: 4, maxLength: 4 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['deviceId', 'deviceSecret', 'uid', 'pin'],
  additionalProperties: false,
}

export interface OpsSharedDeviceSwitchOutArgs {
  deviceId: string
  deviceSecret: string
  sessionId: string
  reason: 'SWITCH' | 'IDLE' | 'SIGN_OUT'
}

const sharedDeviceSwitchOutArgsSchema: JSONSchemaType<OpsSharedDeviceSwitchOutArgs> = {
  type: 'object',
  properties: {
    deviceId: { type: 'string', minLength: 1, maxLength: 120 },
    deviceSecret: { type: 'string', minLength: 1, maxLength: 200 },
    sessionId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', enum: ['SWITCH', 'IDLE', 'SIGN_OUT'] },
  },
  required: ['deviceId', 'deviceSecret', 'sessionId', 'reason'],
  additionalProperties: false,
}

export interface OpsSubmitSharedDeviceQueuedItem {
  personUid: string
  offlineSessionId: string
  at: number
  kind: string
  payload: Record<string, unknown>
  opId: string
}

export interface OpsSubmitSharedDeviceQueuedArgs {
  deviceId: string
  deviceSecret: string
  items: OpsSubmitSharedDeviceQueuedItem[]
}

const submitSharedDeviceQueuedArgsSchema: JSONSchemaType<OpsSubmitSharedDeviceQueuedArgs> = {
  type: 'object',
  properties: {
    deviceId: { type: 'string', minLength: 1, maxLength: 120 },
    deviceSecret: { type: 'string', minLength: 1, maxLength: 200 },
    items: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          personUid: { type: 'string', minLength: 1, maxLength: 120 },
          offlineSessionId: { type: 'string', minLength: 1, maxLength: 120 },
          at: { type: 'number' },
          kind: { type: 'string', minLength: 1, maxLength: 60 },
          payload: { type: 'object', additionalProperties: true, required: [] },
          opId: { type: 'string', minLength: 1, maxLength: 120 },
        },
        required: ['personUid', 'offlineSessionId', 'at', 'kind', 'payload', 'opId'],
        additionalProperties: false,
      },
    },
  },
  required: ['deviceId', 'deviceSecret', 'items'],
  additionalProperties: false,
}

export interface OpsIssueSharedDeviceUploadUrlArgs {
  deviceId: string
  deviceSecret: string
  personUid: string
  offlineSessionId: string
  opId: string
  artifactType: 'HELPER_CHECKLIST_PROOF' | 'HELPER_CHECKLIST_VOICE_MEMO'
  artifactId: string
  fileName: string
  contentType: string
  sizeBytes: number
  checksumSha256: string
}

const issueSharedDeviceUploadUrlArgsSchema: JSONSchemaType<OpsIssueSharedDeviceUploadUrlArgs> = {
  type: 'object',
  properties: {
    deviceId: { type: 'string', minLength: 1, maxLength: 120 },
    deviceSecret: { type: 'string', minLength: 1, maxLength: 200 },
    personUid: { type: 'string', minLength: 1, maxLength: 120 },
    offlineSessionId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120 },
    artifactType: { type: 'string', enum: ['HELPER_CHECKLIST_PROOF', 'HELPER_CHECKLIST_VOICE_MEMO'] },
    artifactId: { type: 'string', minLength: 1, maxLength: 120 },
    fileName: { type: 'string', minLength: 1, maxLength: 240 },
    contentType: { type: 'string', minLength: 1, maxLength: 120 },
    sizeBytes: { type: 'number', minimum: 1 },
    checksumSha256: { type: 'string', minLength: 64, maxLength: 64 },
  },
  required: [
    'deviceId',
    'deviceSecret',
    'personUid',
    'offlineSessionId',
    'opId',
    'artifactType',
    'artifactId',
    'fileName',
    'contentType',
    'sizeBytes',
    'checksumSha256',
  ],
  additionalProperties: false,
}

export interface OpsFinalizeSharedDeviceUploadArgs {
  deviceId: string
  deviceSecret: string
  reservationId: string
}

const finalizeSharedDeviceUploadArgsSchema: JSONSchemaType<OpsFinalizeSharedDeviceUploadArgs> = {
  type: 'object',
  properties: {
    deviceId: { type: 'string', minLength: 1, maxLength: 120 },
    deviceSecret: { type: 'string', minLength: 1, maxLength: 200 },
    reservationId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['deviceId', 'deviceSecret', 'reservationId'],
  additionalProperties: false,
}

export interface OpsLogFoundItemArgs {
  propertyId: string
  placeId: string
  description: string
  category: LostFoundCategory
  valuable?: boolean
  binLabel?: string
  foundAt?: number
  opId?: string
}

const logFoundItemArgsSchema: JSONSchemaType<OpsLogFoundItemArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1, maxLength: 120 },
    placeId: { type: 'string', minLength: 1, maxLength: 120 },
    description: { type: 'string', minLength: 3, maxLength: 500 },
    category: { type: 'string', enum: LF_CATEGORY_ENUM },
    valuable: { type: 'boolean', nullable: true },
    binLabel: { type: 'string', maxLength: 40, nullable: true },
    foundAt: { type: 'number', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['propertyId', 'placeId', 'description', 'category'],
  additionalProperties: false,
}

export interface OpsRecordFoundItemPhotoArgs {
  itemId: string
  path: string
  opId?: string
}

const recordFoundItemPhotoArgsSchema: JSONSchemaType<OpsRecordFoundItemPhotoArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1, maxLength: 120 },
    path: { type: 'string', minLength: 1, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['itemId', 'path'],
  additionalProperties: false,
}

export interface OpsMoveFoundItemBinArgs {
  itemId: string
  binLabel: string
  opId?: string
}

const moveFoundItemBinArgsSchema: JSONSchemaType<OpsMoveFoundItemBinArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1, maxLength: 120 },
    binLabel: { type: 'string', maxLength: 40 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['itemId', 'binLabel'],
  additionalProperties: false,
}

export interface OpsStartLostFoundClaimArgs {
  itemId: string
  claimantName: string
  phoneE164: string
  opId?: string
}

const startLostFoundClaimArgsSchema: JSONSchemaType<OpsStartLostFoundClaimArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1, maxLength: 120 },
    claimantName: { type: 'string', minLength: 1, maxLength: 80 },
    phoneE164: { type: 'string', minLength: 5, maxLength: 20 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['itemId', 'claimantName', 'phoneE164'],
  additionalProperties: false,
}

export interface OpsReissueLostFoundClaimLinkArgs {
  claimId: string
  opId?: string
}

const reissueLostFoundClaimLinkArgsSchema: JSONSchemaType<OpsReissueLostFoundClaimLinkArgs> = {
  type: 'object',
  properties: {
    claimId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['claimId'],
  additionalProperties: false,
}

export interface OpsCancelLostFoundClaimArgs {
  claimId: string
  reason: string
  opId?: string
}

const cancelLostFoundClaimArgsSchema: JSONSchemaType<OpsCancelLostFoundClaimArgs> = {
  type: 'object',
  properties: {
    claimId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['claimId', 'reason'],
  additionalProperties: false,
}

export interface OpsVerifyLostFoundClaimArgs {
  claimId: string
  verdict: LostFoundDeskVerdict
  note?: string
  opId?: string
}

const verifyLostFoundClaimArgsSchema: JSONSchemaType<OpsVerifyLostFoundClaimArgs> = {
  type: 'object',
  properties: {
    claimId: { type: 'string', minLength: 1, maxLength: 120 },
    verdict: { type: 'string', enum: LF_DESK_VERDICT_ENUM },
    note: { type: 'string', maxLength: 500, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['claimId', 'verdict'],
  additionalProperties: false,
}

export interface OpsCompleteLostFoundReturnArgs {
  claimId: string
  mode: LostFoundReturnMode
  courierTrackingRef?: string
  opId?: string
}

const completeLostFoundReturnArgsSchema: JSONSchemaType<OpsCompleteLostFoundReturnArgs> = {
  type: 'object',
  properties: {
    claimId: { type: 'string', minLength: 1, maxLength: 120 },
    mode: { type: 'string', enum: LF_RETURN_MODE_ENUM },
    courierTrackingRef: { type: 'string', maxLength: 60, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['claimId', 'mode'],
  additionalProperties: false,
}

export interface OpsProposeLostFoundDisposalArgs {
  itemId: string
  method: LostFoundDisposalMethod
  note: string
  opId?: string
}

const proposeLostFoundDisposalArgsSchema: JSONSchemaType<OpsProposeLostFoundDisposalArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1, maxLength: 120 },
    method: { type: 'string', enum: LF_DISPOSAL_METHOD_ENUM },
    note: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['itemId', 'method', 'note'],
  additionalProperties: false,
}

export interface OpsSignLostFoundDisposalArgs {
  itemId: string
  opId?: string
}

const signLostFoundDisposalArgsSchema: JSONSchemaType<OpsSignLostFoundDisposalArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1, maxLength: 120 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['itemId'],
  additionalProperties: false,
}

export interface OpsWithdrawLostFoundDisposalArgs {
  itemId: string
  reason: string
  opId?: string
}

const withdrawLostFoundDisposalArgsSchema: JSONSchemaType<OpsWithdrawLostFoundDisposalArgs> = {
  type: 'object',
  properties: {
    itemId: { type: 'string', minLength: 1, maxLength: 120 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['itemId', 'reason'],
  additionalProperties: false,
}

export interface OpsSetLostFoundSettingsArgs {
  propertyId: string
  holdDays: number
  valuableHoldDays: number
  expectedVersion?: number
  opId?: string
}

const setLostFoundSettingsArgsSchema: JSONSchemaType<OpsSetLostFoundSettingsArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1, maxLength: 120 },
    holdDays: { type: 'integer', minimum: 30, maximum: 365 },
    valuableHoldDays: { type: 'integer', minimum: 30, maximum: 730 },
    expectedVersion: { type: 'integer', nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['propertyId', 'holdDays', 'valuableHoldDays'],
  additionalProperties: false,
}

export interface OpsGetLostFoundClaimantArgs {
  claimId: string
}

const getLostFoundClaimantArgsSchema: JSONSchemaType<OpsGetLostFoundClaimantArgs> = {
  type: 'object',
  properties: {
    claimId: { type: 'string', minLength: 1, maxLength: 120 },
  },
  required: ['claimId'],
  additionalProperties: false,
}

export interface OpsSetOpsPhoneRoleAccessArgs {
  role: WorkspaceRole
  items: OpsPhoneItemKey[]
}

const setOpsPhoneRoleAccessArgsSchema: JSONSchemaType<OpsSetOpsPhoneRoleAccessArgs> = {
  type: 'object',
  properties: {
    role: {
      type: 'string',
      enum: ['OWNER', 'ADMIN', 'EDITOR', 'VIEWER', 'HELPER', 'READONLY'],
    },
    items: {
      type: 'array',
      items: {
        type: 'string',
        enum: [
          'h.checkIn',
          'h.dueChecks',
          'h.dpr',
          'h.roll',
          'h.request',
          'h.roomClean',
          'h.foundItem',
          'h.bookingCheckIn',
          'm.approvals',
          'm.roster',
          'm.registers',
          'm.reports',
          'm.requests',
        ],
      },
    },
  },
  required: ['role', 'items'],
  additionalProperties: false,
}

export interface OpsApplyOpsPhoneRecommendedSetArgs {
  role: WorkspaceRole
}

const applyOpsPhoneRecommendedSetArgsSchema: JSONSchemaType<OpsApplyOpsPhoneRecommendedSetArgs> = {
  type: 'object',
  properties: {
    role: {
      type: 'string',
      enum: ['OWNER', 'ADMIN', 'EDITOR', 'VIEWER', 'HELPER', 'READONLY'],
    },
  },
  required: ['role'],
  additionalProperties: false,
}

export interface OpsSetOpsPhonePersonAccessArgs {
  uid: string
  allow: OpsPhoneItemKey[]
  deny: OpsPhoneItemKey[]
}

const setOpsPhonePersonAccessArgsSchema: JSONSchemaType<OpsSetOpsPhonePersonAccessArgs> = {
  type: 'object',
  properties: {
    uid: { type: 'string', minLength: 1 },
    allow: {
      type: 'array',
      items: {
        type: 'string',
        enum: [
          'h.checkIn',
          'h.dueChecks',
          'h.dpr',
          'h.roll',
          'h.request',
          'h.roomClean',
          'h.foundItem',
          'h.bookingCheckIn',
          'm.approvals',
          'm.roster',
          'm.registers',
          'm.reports',
          'm.requests',
        ],
      },
    },
    deny: {
      type: 'array',
      items: {
        type: 'string',
        enum: [
          'h.checkIn',
          'h.dueChecks',
          'h.dpr',
          'h.roll',
          'h.request',
          'h.roomClean',
          'h.foundItem',
          'h.bookingCheckIn',
          'm.approvals',
          'm.roster',
          'm.registers',
          'm.reports',
          'm.requests',
        ],
      },
    },
  },
  required: ['uid', 'allow', 'deny'],
  additionalProperties: false,
}

export interface OpsGetMyOpsPhoneItemsArgs {
  workspaceId?: string
}

const getMyOpsPhoneItemsArgsSchema: JSONSchemaType<OpsGetMyOpsPhoneItemsArgs> = {
  type: 'object',
  properties: {
    workspaceId: { type: 'string', nullable: true },
  },
  additionalProperties: false,
}

export interface OpsCreateConProjectArgs {
  placeId: string
  name: string
  code: string
  status?: ConProjectStatus
  clientPartyId?: string | null
  startDate?: string | null
  endDate?: string | null
  packages?: ConPackage[]
  approvers?: Partial<ConApprovers>
  opId?: string
}

const createConProjectArgsSchema: JSONSchemaType<OpsCreateConProjectArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    name: { type: 'string', minLength: 1, maxLength: 120 },
    code: { type: 'string', minLength: 1, maxLength: 24 },
    status: { type: 'string', enum: CON_PROJECT_STATUS_ENUM, nullable: true },
    clientPartyId: { type: 'string', nullable: true },
    startDate: { type: 'string', nullable: true },
    endDate: { type: 'string', nullable: true },
    packages: { type: 'array', items: { type: 'object' }, nullable: true } as unknown as Record<string, unknown>,
    approvers: { type: 'object', nullable: true } as unknown as Record<string, unknown>,
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['placeId', 'name', 'code'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCreateConProjectArgs>

export interface OpsUpdateConProjectArgs {
  projectId: string
  name?: string
  code?: string
  status?: ConProjectStatus
  clientPartyId?: string | null
  startDate?: string | null
  endDate?: string | null
}

const updateConProjectArgsSchema: JSONSchemaType<OpsUpdateConProjectArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    name: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    code: { type: 'string', minLength: 1, maxLength: 24, nullable: true },
    status: { type: 'string', enum: CON_PROJECT_STATUS_ENUM, nullable: true },
    clientPartyId: { type: 'string', nullable: true },
    startDate: { type: 'string', nullable: true },
    endDate: { type: 'string', nullable: true },
  },
  required: ['projectId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsUpdateConProjectArgs>

export interface OpsSetConProjectPackagesArgs {
  projectId: string
  packages: ConPackage[]
}

const setConProjectPackagesArgsSchema: JSONSchemaType<OpsSetConProjectPackagesArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    packages: { type: 'array', items: { type: 'object' } } as unknown as Record<string, unknown>,
  },
  required: ['projectId', 'packages'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetConProjectPackagesArgs>

export interface OpsSetConProjectApproversArgs {
  projectId: string
  approvers: ConApprovers
}

const setConProjectApproversArgsSchema: JSONSchemaType<OpsSetConProjectApproversArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    approvers: { type: 'object' } as unknown as Record<string, unknown>,
  },
  required: ['projectId', 'approvers'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetConProjectApproversArgs>

export interface OpsCreateConDrawingArgs {
  projectId: string
  number: string
  title: string
  discipline: ConDrawingDiscipline
  packageKey?: string | null
  initialRevision: string
  link: string
  note?: string | null
  opId?: string
}

const createConDrawingArgsSchema: JSONSchemaType<OpsCreateConDrawingArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    number: { type: 'string', minLength: 1, maxLength: 40 },
    title: { type: 'string', minLength: 1, maxLength: 160 },
    discipline: { type: 'string', enum: CON_DRAWING_DISCIPLINE_ENUM },
    packageKey: { type: 'string', nullable: true },
    initialRevision: { type: 'string', minLength: 1, maxLength: 12 },
    link: { type: 'string', minLength: 1, maxLength: 2000 },
    note: { type: 'string', maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'number', 'title', 'discipline', 'initialRevision', 'link'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCreateConDrawingArgs>

export interface OpsIssueConDrawingRevisionArgs {
  drawingId: string
  revision: string
  link: string
  note?: string | null
  opId?: string
}

const issueConDrawingRevisionArgsSchema: JSONSchemaType<OpsIssueConDrawingRevisionArgs> = {
  type: 'object',
  properties: {
    drawingId: { type: 'string', minLength: 1 },
    revision: { type: 'string', minLength: 1, maxLength: 12 },
    link: { type: 'string', minLength: 1, maxLength: 2000 },
    note: { type: 'string', maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['drawingId', 'revision', 'link'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsIssueConDrawingRevisionArgs>

export interface OpsWithdrawConDrawingArgs {
  drawingId: string
}

const withdrawConDrawingArgsSchema: JSONSchemaType<OpsWithdrawConDrawingArgs> = {
  type: 'object',
  properties: {
    drawingId: { type: 'string', minLength: 1 },
  },
  required: ['drawingId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsWithdrawConDrawingArgs>

export interface OpsSaveDprDraftArgs {
  projectId: string
  reportDate: string
  shift: ConDprShift
  lines: ConDprLine[]
  weather?: string | null
  safetyExceptions?: boolean
  safetyNotes?: string | null
  keyReceiptsNotes?: string | null
  inspectionRefsNotes?: string | null
  opId?: string
}

const saveDprDraftArgsSchema: JSONSchemaType<OpsSaveDprDraftArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    reportDate: { type: 'string', minLength: 1 },
    shift: { type: 'string', enum: CON_DPR_SHIFT_ENUM },
    lines: { type: 'array', items: { type: 'object' } } as unknown as Record<string, unknown>,
    weather: { type: 'string', nullable: true },
    safetyExceptions: { type: 'boolean', nullable: true },
    safetyNotes: { type: 'string', nullable: true },
    keyReceiptsNotes: { type: 'string', nullable: true },
    inspectionRefsNotes: { type: 'string', nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'reportDate', 'shift', 'lines'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSaveDprDraftArgs>

export interface OpsSubmitDprArgs {
  projectId: string
  reportDate: string
  shift: ConDprShift
  lines: ConDprLine[]
  weather?: string | null
  safetyExceptions?: boolean
  safetyNotes?: string | null
  keyReceiptsNotes?: string | null
  inspectionRefsNotes?: string | null
  opId?: string
}

const submitDprArgsSchema: JSONSchemaType<OpsSubmitDprArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    reportDate: { type: 'string', minLength: 1 },
    shift: { type: 'string', enum: CON_DPR_SHIFT_ENUM },
    lines: { type: 'array', items: { type: 'object' } } as unknown as Record<string, unknown>,
    weather: { type: 'string', nullable: true },
    safetyExceptions: { type: 'boolean', nullable: true },
    safetyNotes: { type: 'string', nullable: true },
    keyReceiptsNotes: { type: 'string', nullable: true },
    inspectionRefsNotes: { type: 'string', nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'reportDate', 'shift', 'lines'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSubmitDprArgs>

export interface OpsReviewDprArgs {
  recordId: string
  notes?: string | null
  opId?: string
}

const reviewDprArgsSchema: JSONSchemaType<OpsReviewDprArgs> = {
  type: 'object',
  properties: {
    recordId: { type: 'string', minLength: 1 },
    notes: { type: 'string', nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['recordId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsReviewDprArgs>

export interface OpsReturnDprArgs {
  recordId: string
  reason: string
  opId?: string
}

const returnDprArgsSchema: JSONSchemaType<OpsReturnDprArgs> = {
  type: 'object',
  properties: {
    recordId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['recordId', 'reason'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsReturnDprArgs>

export interface OpsApproveDprArgs {
  recordId: string
  notes?: string | null
  opId?: string
}

const approveDprArgsSchema: JSONSchemaType<OpsApproveDprArgs> = {
  type: 'object',
  properties: {
    recordId: { type: 'string', minLength: 1 },
    notes: { type: 'string', nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['recordId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsApproveDprArgs>

export interface OpsAddDiaryEntryArgs {
  projectId: string
  placeId?: string | null
  kind: ConDiaryKind
  text: string
  occurredAt?: number | null
  offline?: boolean
  photoPaths?: string[]
  requestId?: string | null
  supersedesId?: string | null
  opId?: string
}

const addDiaryEntryArgsSchema: JSONSchemaType<OpsAddDiaryEntryArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    placeId: { type: 'string', nullable: true },
    kind: { type: 'string', enum: CON_DIARY_KIND_ENUM },
    text: { type: 'string', minLength: 1, maxLength: 1000 },
    occurredAt: { type: 'number', nullable: true },
    offline: { type: 'boolean', nullable: true },
    photoPaths: { type: 'array', items: { type: 'string' }, nullable: true } as unknown as Record<string, unknown>,
    requestId: { type: 'string', nullable: true },
    supersedesId: { type: 'string', nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'kind', 'text'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsAddDiaryEntryArgs>

export interface OpsSaveConPourCardArgs {
  projectId: string
  cardId?: string
  placeId: string
  kind: ConPourCardKind
  element: string
  plannedAt: number
  checklist: ConPourChecklistItem[]
  inspectorUid: string
  opId?: string
}

const saveConPourCardArgsSchema: JSONSchemaType<OpsSaveConPourCardArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    cardId: { type: 'string', nullable: true },
    placeId: { type: 'string', minLength: 1 },
    kind: { type: 'string', enum: ['POUR', 'PRE_CONCEALMENT'] as unknown as [ConPourCardKind, ...ConPourCardKind[]] },
    element: { type: 'string', minLength: 1, maxLength: 120 },
    plannedAt: { type: 'number' },
    checklist: { type: 'array', items: { type: 'object' } } as unknown as Record<string, unknown>,
    inspectorUid: { type: 'string', minLength: 1 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'placeId', 'kind', 'element', 'plannedAt', 'checklist', 'inspectorUid'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSaveConPourCardArgs>

export interface OpsRequestConInspectionArgs {
  cardId: string
  opId?: string
}

const requestConInspectionArgsSchema: JSONSchemaType<OpsRequestConInspectionArgs> = {
  type: 'object',
  properties: {
    cardId: { type: 'string', minLength: 1 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['cardId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsRequestConInspectionArgs>

export interface OpsRecordConInspectionOutcomeArgs {
  requestId: string
  outcome: ConInspectionOutcome
  notes?: string | null
  photoPaths?: string[]
  opId?: string
}

const recordConInspectionOutcomeArgsSchema: JSONSchemaType<OpsRecordConInspectionOutcomeArgs> = {
  type: 'object',
  properties: {
    requestId: { type: 'string', minLength: 1 },
    outcome: { type: 'string', enum: ['PASS', 'FAIL'] as unknown as [ConInspectionOutcome, ...ConInspectionOutcome[]] },
    notes: { type: 'string', nullable: true },
    photoPaths: { type: 'array', items: { type: 'string' }, nullable: true } as unknown as Record<string, unknown>,
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['requestId', 'outcome'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsRecordConInspectionOutcomeArgs>

export interface OpsReassignConInspectorArgs {
  cardId: string
  newInspectorUid: string
  reason?: string
  opId?: string
}

const reassignConInspectorArgsSchema: JSONSchemaType<OpsReassignConInspectorArgs> = {
  type: 'object',
  properties: {
    cardId: { type: 'string', minLength: 1 },
    newInspectorUid: { type: 'string', minLength: 1 },
    reason: { type: 'string', nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['cardId', 'newInspectorUid'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsReassignConInspectorArgs>

export interface OpsCreateConTestArgs {
  projectId: string
  sampleId: string
  testType: ConTestType
  otherLabel?: string | null
  placeId: string
  pourCardId?: string | null
  castAt: number
  dueDay?: string | null
  spec: ConTestSpec
  opId?: string
}

const createConTestArgsSchema: JSONSchemaType<OpsCreateConTestArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    sampleId: { type: 'string', minLength: 1, maxLength: 40 },
    testType: {
      type: 'string',
      enum: ['CUBE_7D', 'CUBE_28D', 'SLUMP', 'STEEL_TENSILE', 'SOIL_COMPACTION', 'OTHER'] as unknown as [ConTestType, ...ConTestType[]],
    },
    otherLabel: { type: 'string', nullable: true },
    placeId: { type: 'string', minLength: 1 },
    pourCardId: { type: 'string', nullable: true },
    castAt: { type: 'number' },
    dueDay: { type: 'string', nullable: true },
    spec: { type: 'object' } as unknown as Record<string, unknown>,
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'sampleId', 'testType', 'placeId', 'castAt', 'spec'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCreateConTestArgs>

export interface OpsAddConTestResultArgs {
  testId: string
  value: number
  labRef?: string | null
  photoPaths?: string[]
  opId?: string
}

const addConTestResultArgsSchema: JSONSchemaType<OpsAddConTestResultArgs> = {
  type: 'object',
  properties: {
    testId: { type: 'string', minLength: 1 },
    value: { type: 'number' },
    labRef: { type: 'string', nullable: true },
    photoPaths: { type: 'array', items: { type: 'string' }, nullable: true } as unknown as Record<string, unknown>,
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['testId', 'value'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsAddConTestResultArgs>

const OPS_BED_ACTION_ENUM: OpsBedActionType[] = [
  'MARK_OCCUPIED',
  'DISCHARGE',
  'CLEAN_STARTED',
  'CLEAN_DONE',
  'RELEASED',
  'CLEAN_REOPENED',
  'CLEAN_CANCELLED',
  'BLOCK',
  'UNBLOCK',
  'OVERRIDE_READY',
]

export interface OpsSetBedStateArgs {
  placeId: string
  action: OpsBedActionType
  reason?: string | null
  opId?: string | null
}

const setBedStateArgsSchema: JSONSchemaType<OpsSetBedStateArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    action: { type: 'string', enum: OPS_BED_ACTION_ENUM },
    reason: { type: 'string', minLength: 1, maxLength: 500, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['placeId', 'action'],
  additionalProperties: false,
}

export interface OpsListBedEventsArgs {
  placeId: string
  limit?: number | null
  cursor?: string | null
}

const listBedEventsArgsSchema: JSONSchemaType<OpsListBedEventsArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    limit: { type: 'number', minimum: 1, maximum: 100, nullable: true },
    cursor: { type: 'string', minLength: 1, maxLength: 500, nullable: true },
  },
  required: ['placeId'],
  additionalProperties: false,
}

export interface OpsCreateConIssueArgs {
  projectId: string
  placeId: string
  kind: ConIssueKind
  title: string
  description?: string
  firstNoticedAt: number
  responsibleParty: ConResponsibleParty
  drawingRef?: {
    drawingId: string
    revisionId?: string
  }
  contractNoticeRef?: string
  responseDueAt: number
  impact: ConIssueImpact
  photoPaths?: string[]
  options?: ConSelectionOption[]
  opId?: string
}

const createConIssueArgsSchema: JSONSchemaType<OpsCreateConIssueArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    placeId: { type: 'string', minLength: 1 },
    kind: { type: 'string' } as unknown as Record<string, unknown>,
    title: { type: 'string', minLength: 3, maxLength: 120 },
    description: { type: 'string', maxLength: 4000, nullable: true },
    firstNoticedAt: { type: 'number' },
    responsibleParty: { type: 'object' } as unknown as Record<string, unknown>,
    drawingRef: { type: 'object', nullable: true } as unknown as Record<string, unknown>,
    contractNoticeRef: { type: 'string', maxLength: 120, nullable: true },
    responseDueAt: { type: 'number' },
    impact: { type: 'object' } as unknown as Record<string, unknown>,
    photoPaths: { type: 'array', items: { type: 'string' }, nullable: true } as unknown as Record<string, unknown>,
    options: { type: 'array', items: { type: 'object' }, nullable: true } as unknown as Record<string, unknown>,
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'placeId', 'kind', 'title', 'firstNoticedAt', 'responsibleParty', 'responseDueAt', 'impact'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCreateConIssueArgs>

export interface OpsUpdateConIssueArgs {
  issueId: string
  title?: string
  description?: string
  placeId?: string
  responsibleParty?: ConResponsibleParty
  drawingRef?: {
    drawingId: string
    revisionId?: string
  } | null
  contractNoticeRef?: string | null
  responseDueAt?: number
  impact?: ConIssueImpact
  photoPaths?: string[]
  options?: ConSelectionOption[]
  opId?: string
}

const updateConIssueArgsSchema: JSONSchemaType<OpsUpdateConIssueArgs> = {
  type: 'object',
  properties: {
    issueId: { type: 'string', minLength: 1 },
    title: { type: 'string', minLength: 3, maxLength: 120, nullable: true },
    description: { type: 'string', maxLength: 4000, nullable: true },
    placeId: { type: 'string', minLength: 1, nullable: true },
    responsibleParty: { type: 'object', nullable: true } as unknown as Record<string, unknown>,
    drawingRef: { type: 'object', nullable: true } as unknown as Record<string, unknown>,
    contractNoticeRef: { type: 'string', maxLength: 120, nullable: true },
    responseDueAt: { type: 'number', nullable: true },
    impact: { type: 'object', nullable: true } as unknown as Record<string, unknown>,
    photoPaths: { type: 'array', items: { type: 'string' }, nullable: true } as unknown as Record<string, unknown>,
    options: { type: 'array', items: { type: 'object' }, nullable: true } as unknown as Record<string, unknown>,
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['issueId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsUpdateConIssueArgs>

export interface OpsAttachConIssuePhotosArgs {
  issueId: string
  photoPaths: string[]
  projectId?: string
  opId?: string
}

const attachConIssuePhotosArgsSchema: JSONSchemaType<OpsAttachConIssuePhotosArgs> = {
  type: 'object',
  properties: {
    issueId: { type: 'string', minLength: 1 },
    photoPaths: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      minItems: 1,
      maxItems: 10,
    },
    projectId: { type: 'string', minLength: 1, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['issueId', 'photoPaths'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsAttachConIssuePhotosArgs>

export interface OpsRespondConIssueArgs {
  issueId: string
  text: string
  photoPaths?: string[]
  opId?: string
}

const respondConIssueArgsSchema: JSONSchemaType<OpsRespondConIssueArgs> = {
  type: 'object',
  properties: {
    issueId: { type: 'string', minLength: 1 },
    text: { type: 'string', minLength: 1, maxLength: 4000 },
    photoPaths: { type: 'array', items: { type: 'string' }, nullable: true } as unknown as Record<string, unknown>,
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['issueId', 'text'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsRespondConIssueArgs>

export interface OpsWithdrawConIssueArgs {
  issueId: string
  reason: string
  opId?: string
}

const withdrawConIssueArgsSchema: JSONSchemaType<OpsWithdrawConIssueArgs> = {
  type: 'object',
  properties: {
    issueId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['issueId', 'reason'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsWithdrawConIssueArgs>

export interface OpsCloseConIssueArgs {
  issueId: string
  opId?: string
}

const closeConIssueArgsSchema: JSONSchemaType<OpsCloseConIssueArgs> = {
  type: 'object',
  properties: {
    issueId: { type: 'string', minLength: 1 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['issueId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCloseConIssueArgs>

export interface OpsDecideConIssueArgs {
  issueId: string
  text: string
  chosenOptionId?: string
  revisedInstruction?: string
  supersedeReason?: string
  selfDecisionReason?: string
  opId?: string
}

const decideConIssueArgsSchema: JSONSchemaType<OpsDecideConIssueArgs> = {
  type: 'object',
  properties: {
    issueId: { type: 'string', minLength: 1 },
    text: { type: 'string', minLength: 1, maxLength: 4000 },
    chosenOptionId: { type: 'string', nullable: true },
    revisedInstruction: { type: 'string', maxLength: 4000, nullable: true },
    supersedeReason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    selfDecisionReason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['issueId', 'text'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsDecideConIssueArgs>

export interface OpsSendConIssueForApprovalArgs {
  issueId: string
  approvers: Array<{ name: string; phone: string }>
  mode?: OutsideApprovalMode
  expiresInDays?: number
  message?: string
  opId?: string
}

const sendConIssueForApprovalArgsSchema: JSONSchemaType<OpsSendConIssueForApprovalArgs> = {
  type: 'object',
  properties: {
    issueId: { type: 'string', minLength: 1 },
    approvers: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          name: { type: 'string', minLength: 1 },
          phone: { type: 'string', minLength: 1 },
        },
        required: ['name', 'phone'],
        additionalProperties: false,
      },
      minItems: 1,
      maxItems: 5,
    } as unknown as Record<string, unknown>,
    mode: { type: 'string', nullable: true } as unknown as Record<string, unknown>,
    expiresInDays: { type: 'number', nullable: true },
    message: { type: 'string', maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['issueId', 'approvers'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSendConIssueForApprovalArgs>

export interface OpsGetConIssueArgs {
  issueId: string
  projectId?: string
}

const getConIssueArgsSchema: JSONSchemaType<OpsGetConIssueArgs> = {
  type: 'object',
  properties: {
    issueId: { type: 'string', minLength: 1 },
    projectId: { type: 'string', minLength: 1, nullable: true },
  },
  required: ['issueId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetConIssueArgs>

export interface OpsGetConIssuePhotoUrlsArgs {
  issueId: string
  projectId?: string
}

const getConIssuePhotoUrlsArgsSchema: JSONSchemaType<OpsGetConIssuePhotoUrlsArgs> = {
  type: 'object',
  properties: {
    issueId: { type: 'string', minLength: 1 },
    projectId: { type: 'string', minLength: 1, nullable: true },
  },
  required: ['issueId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetConIssuePhotoUrlsArgs>

export interface OpsListConIssuesArgs {
  projectId: string
  kind?: string
  state?: string
  overdueOnly?: boolean
  limit?: number
}

const listConIssuesArgsSchema: JSONSchemaType<OpsListConIssuesArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    kind: { type: 'string', nullable: true },
    state: { type: 'string', nullable: true },
    overdueOnly: { type: 'boolean', nullable: true },
    limit: { type: 'number', nullable: true },
  },
  required: ['projectId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsListConIssuesArgs>

export interface OpsListConIssueDecisionsArgs {
  issueId: string
  projectId?: string
  limit?: number
}

const listConIssueDecisionsArgsSchema: JSONSchemaType<OpsListConIssueDecisionsArgs> = {
  type: 'object',
  properties: {
    issueId: { type: 'string', minLength: 1 },
    projectId: { type: 'string', minLength: 1, nullable: true },
    limit: { type: 'number', nullable: true },
  },
  required: ['issueId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsListConIssueDecisionsArgs>

export interface OpsUpsertConPartyArgs {
  partyId?: string
  name: string
  type: ConPartyType
  trade: string
  externalRef?: string
  projectIds: string[]
  archived?: boolean
  opId?: string
}

const upsertConPartyArgsSchema: JSONSchemaType<OpsUpsertConPartyArgs> = {
  type: 'object',
  properties: {
    partyId: { type: 'string', minLength: 1, nullable: true },
    name: { type: 'string', minLength: 2, maxLength: 120 },
    type: { type: 'string', enum: ['SUBCONTRACTOR', 'SUPPLIER', 'IN_HOUSE_GANG'] as unknown as [ConPartyType, ...ConPartyType[]] },
    trade: { type: 'string', minLength: 1, maxLength: 60 },
    externalRef: { type: 'string', maxLength: 60, nullable: true },
    projectIds: { type: 'array', items: { type: 'string' } } as unknown as Record<string, unknown>,
    archived: { type: 'boolean', nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['name', 'type', 'trade', 'projectIds'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsUpsertConPartyArgs>

export interface OpsListConPartiesArgs {
  projectId?: string
  includeArchived?: boolean
  limit?: number
}

const listConPartiesArgsSchema: JSONSchemaType<OpsListConPartiesArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1, nullable: true },
    includeArchived: { type: 'boolean', nullable: true },
    limit: { type: 'number', nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsListConPartiesArgs>

export interface OpsSaveConMeasurementArgs {
  projectId: string
  measurementId?: string
  partyId: string
  placeId: string
  workOrderRef: string
  itemRef: string
  itemDescription?: string
  unit: string
  measuredDay: string
  periodLabel?: string
  measuredQty: number
  photoPaths?: string[]
  inspectionRefs?: ConInspectionRef[]
  sourceLinks?: ConSourceLink[]
  opId?: string
}

const saveConMeasurementArgsSchema: JSONSchemaType<OpsSaveConMeasurementArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    measurementId: { type: 'string', minLength: 1, nullable: true },
    partyId: { type: 'string', minLength: 1 },
    placeId: { type: 'string', minLength: 1 },
    workOrderRef: { type: 'string', minLength: 1, maxLength: 60 },
    itemRef: { type: 'string', minLength: 1, maxLength: 60 },
    itemDescription: { type: 'string', maxLength: 300, nullable: true },
    unit: { type: 'string', minLength: 1, maxLength: 12 },
    measuredDay: { type: 'string', minLength: 10, maxLength: 10 },
    periodLabel: { type: 'string', maxLength: 40, nullable: true },
    measuredQty: { type: 'number', minimum: 0 },
    photoPaths: { type: 'array', items: { type: 'string' }, nullable: true } as unknown as Record<string, unknown>,
    inspectionRefs: { type: 'array', items: { type: 'object' }, nullable: true } as unknown as Record<string, unknown>,
    sourceLinks: { type: 'array', items: { type: 'object' }, nullable: true } as unknown as Record<string, unknown>,
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'partyId', 'placeId', 'workOrderRef', 'itemRef', 'unit', 'measuredDay', 'measuredQty'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSaveConMeasurementArgs>

export interface OpsSubmitConMeasurementArgs {
  projectId: string
  measurementId: string
  opId?: string
}

const submitConMeasurementArgsSchema: JSONSchemaType<OpsSubmitConMeasurementArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    measurementId: { type: 'string', minLength: 1 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'measurementId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSubmitConMeasurementArgs>

export interface OpsAcceptConMeasurementArgs {
  projectId: string
  measurementId: string
  acceptedQty: number
  note?: string
  selfAcceptReason?: string
  opId?: string
}

const acceptConMeasurementArgsSchema: JSONSchemaType<OpsAcceptConMeasurementArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    measurementId: { type: 'string', minLength: 1 },
    acceptedQty: { type: 'number', minimum: 0 },
    note: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    selfAcceptReason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'measurementId', 'acceptedQty'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsAcceptConMeasurementArgs>

export interface OpsDisputeConMeasurementArgs {
  projectId: string
  measurementId: string
  reason: string
  selfAcceptReason?: string
  opId?: string
}

const disputeConMeasurementArgsSchema: JSONSchemaType<OpsDisputeConMeasurementArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    measurementId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    selfAcceptReason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'measurementId', 'reason'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsDisputeConMeasurementArgs>

export interface OpsCorrectConMeasurementArgs {
  projectId: string
  measurementId: string
  newAcceptedQty: number
  reason: string
  selfAcceptReason?: string
  opId?: string
}

const correctConMeasurementArgsSchema: JSONSchemaType<OpsCorrectConMeasurementArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    measurementId: { type: 'string', minLength: 1 },
    newAcceptedQty: { type: 'number', minimum: 0 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    selfAcceptReason: { type: 'string', minLength: 3, maxLength: 500, nullable: true },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'measurementId', 'newAcceptedQty', 'reason'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCorrectConMeasurementArgs>

export interface OpsCancelConMeasurementArgs {
  projectId: string
  measurementId: string
  reason: string
  opId?: string
}

const cancelConMeasurementArgsSchema: JSONSchemaType<OpsCancelConMeasurementArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    measurementId: { type: 'string', minLength: 1 },
    reason: { type: 'string', minLength: 3, maxLength: 500 },
    opId: { type: 'string', maxLength: 120, nullable: true },
  },
  required: ['projectId', 'measurementId', 'reason'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCancelConMeasurementArgs>

export interface OpsGetConMeasurementArgs {
  projectId: string
  measurementId: string
}

const getConMeasurementArgsSchema: JSONSchemaType<OpsGetConMeasurementArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    measurementId: { type: 'string', minLength: 1 },
  },
  required: ['projectId', 'measurementId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetConMeasurementArgs>

export interface OpsListConMeasurementsArgs {
  projectId: string
  partyId?: string
  state?: ConMeasurementState
  fromDay?: string
  toDay?: string
  limit?: number
}

const listConMeasurementsArgsSchema: JSONSchemaType<OpsListConMeasurementsArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    partyId: { type: 'string', minLength: 1, nullable: true },
    state: { type: 'string', nullable: true },
    fromDay: { type: 'string', minLength: 10, maxLength: 10, nullable: true },
    toDay: { type: 'string', minLength: 10, maxLength: 10, nullable: true },
    limit: { type: 'number', nullable: true },
  },
  required: ['projectId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsListConMeasurementsArgs>

export interface OpsListDisputedConMeasurementsArgs {
  projectId: string
  limit?: number
}

const listDisputedConMeasurementsArgsSchema: JSONSchemaType<OpsListDisputedConMeasurementsArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    limit: { type: 'number', nullable: true },
  },
  required: ['projectId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsListDisputedConMeasurementsArgs>

export interface OpsGetConMeasureTotalsArgs {
  projectId: string
  partyId?: string
  workOrderRef?: string
  limit?: number
}

const getConMeasureTotalsArgsSchema: JSONSchemaType<OpsGetConMeasureTotalsArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    partyId: { type: 'string', minLength: 1, nullable: true },
    workOrderRef: { type: 'string', minLength: 1, nullable: true },
    limit: { type: 'number', nullable: true },
  },
  required: ['projectId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetConMeasureTotalsArgs>

export interface OpsGetConReportArgs {
  projectId: string
  kind: ConReportKind
  fromDay?: string
  toDay?: string
  opId?: string
}

const CON_REPORT_KINDS_ENUM = [...CON_REPORT_KINDS] as unknown as [
  ConReportKind,
  ...ConReportKind[],
]

const getConReportArgsSchema: JSONSchemaType<OpsGetConReportArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', minLength: 1 },
    kind: { type: 'string', enum: CON_REPORT_KINDS_ENUM },
    fromDay: { type: 'string', nullable: true },
    toDay: { type: 'string', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['projectId', 'kind'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetConReportArgs>

export interface OpsGetPlantDowntimeReportArgs {
  plantId: string
  fromDay: string
  toDay: string
}

const getPlantDowntimeReportArgsSchema: JSONSchemaType<OpsGetPlantDowntimeReportArgs> = {
  type: 'object',
  properties: {
    plantId: { type: 'string', minLength: 1 },
    fromDay: { type: 'string', minLength: 10, maxLength: 10 },
    toDay: { type: 'string', minLength: 10, maxLength: 10 },
  },
  required: ['plantId', 'fromDay', 'toDay'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetPlantDowntimeReportArgs>

export interface OpsGetPlantStarterStatusArgs {
  plantId: string
}

const getPlantStarterStatusArgsSchema: JSONSchemaType<OpsGetPlantStarterStatusArgs> = {
  type: 'object',
  properties: {
    plantId: { type: 'string' },
  },
  required: ['plantId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetPlantStarterStatusArgs>

export interface OpsSetPlantNearMissExportPeopleArgs {
  enabled: boolean
  opId?: string
}

const setPlantNearMissExportPeopleArgsSchema: JSONSchemaType<OpsSetPlantNearMissExportPeopleArgs> = {
  type: 'object',
  properties: {
    enabled: { type: 'boolean' },
    opId: { type: 'string', nullable: true },
  },
  required: ['enabled'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetPlantNearMissExportPeopleArgs>

export interface OpsGetConStarterStatusArgs {
  projectId?: string
}

const getConStarterStatusArgsSchema: JSONSchemaType<OpsGetConStarterStatusArgs> = {
  type: 'object',
  properties: {
    projectId: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetConStarterStatusArgs>

export interface OpsAcknowledgeOnCallArgs {
  rosterId: string
  version: number
  dayKey: string
  kind: 'ON_CALL_PRIMARY' | 'ON_CALL_BACKUP'
  opId?: string
}

const acknowledgeOnCallArgsSchema: JSONSchemaType<OpsAcknowledgeOnCallArgs> = {
  type: 'object',
  properties: {
    rosterId: { type: 'string', minLength: 1 },
    version: { type: 'integer', minimum: 1 },
    dayKey: { type: 'string', minLength: 10 },
    kind: { type: 'string', enum: ['ON_CALL_PRIMARY', 'ON_CALL_BACKUP'] },
    opId: { type: 'string', nullable: true },
  },
  required: ['rosterId', 'version', 'dayKey', 'kind'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsAcknowledgeOnCallArgs>

export interface OpsGetOnCallBoardArgs {
  placeId: string
  fromDay: string
  days: number
}

const getOnCallBoardArgsSchema: JSONSchemaType<OpsGetOnCallBoardArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    fromDay: { type: 'string', minLength: 10 },
    days: { type: 'integer', minimum: 1, maximum: 14 },
  },
  required: ['placeId', 'fromDay', 'days'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetOnCallBoardArgs>

export interface OpsListMyOnCallSlotsArgs {
  fromDay: string
  days: number
}

const listMyOnCallSlotsArgsSchema: JSONSchemaType<OpsListMyOnCallSlotsArgs> = {
  type: 'object',
  properties: {
    fromDay: { type: 'string', minLength: 10 },
    days: { type: 'integer', minimum: 1, maximum: 14 },
  },
  required: ['fromDay', 'days'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsListMyOnCallSlotsArgs>

export interface OpsGetHosStarterStatusArgs {
  placeId?: string
}

const getHosStarterStatusArgsSchema: JSONSchemaType<OpsGetHosStarterStatusArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetHosStarterStatusArgs>

export interface OpsSetBmwSettingsArgs {
  storageHours?: number
  unusualKgPerCategory?: number
}

const setBmwSettingsArgsSchema: JSONSchemaType<OpsSetBmwSettingsArgs> = {
  type: 'object',
  properties: {
    storageHours: { type: 'integer', nullable: true },
    unusualKgPerCategory: { type: 'integer', nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetBmwSettingsArgs>

export interface OpsSaveBmwDayArgs {
  placeId: string
  dayKey: string
  weights: Record<string, unknown>
  bagCounts?: Record<string, unknown>
  opId?: string
}

const saveBmwDayArgsSchema: JSONSchemaType<OpsSaveBmwDayArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    dayKey: { type: 'string', minLength: 10 },
    weights: { type: 'object' },
    bagCounts: { type: 'object', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['placeId', 'dayKey', 'weights'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSaveBmwDayArgs>

export interface OpsSubmitBmwDayArgs {
  placeId: string
  dayKey: string
  weights: Record<string, unknown>
  bagCounts?: Record<string, unknown>
  confirmUnusual?: boolean
  opId?: string
}

const submitBmwDayArgsSchema: JSONSchemaType<OpsSubmitBmwDayArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    dayKey: { type: 'string', minLength: 10 },
    weights: { type: 'object' },
    bagCounts: { type: 'object', nullable: true },
    confirmUnusual: { type: 'boolean', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['placeId', 'dayKey', 'weights'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSubmitBmwDayArgs>

export interface OpsCorrectBmwDayArgs {
  placeId: string
  dayKey: string
  weights: Record<string, unknown>
  bagCounts?: Record<string, unknown>
  reason: string
  opId?: string
}

const correctBmwDayArgsSchema: JSONSchemaType<OpsCorrectBmwDayArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    dayKey: { type: 'string', minLength: 10 },
    weights: { type: 'object' },
    bagCounts: { type: 'object', nullable: true },
    reason: { type: 'string', minLength: 3 },
    opId: { type: 'string', nullable: true },
  },
  required: ['placeId', 'dayKey', 'weights', 'reason'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCorrectBmwDayArgs>

export interface OpsRecordBmwPickupArgs {
  placeId: string
  pickedAt: string | number
  manifestRef: string
  vehicleRef?: string
  bagCounts?: Record<string, unknown>
  opId?: string
}

const recordBmwPickupArgsSchema: JSONSchemaType<OpsRecordBmwPickupArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    pickedAt: { type: ['string', 'number'] } as unknown as Record<string, unknown>,
    manifestRef: { type: 'string', minLength: 3 },
    vehicleRef: { type: 'string', nullable: true },
    bagCounts: { type: 'object', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['placeId', 'pickedAt', 'manifestRef'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsRecordBmwPickupArgs>

export interface OpsGetBmwStatusArgs {
  placeId: string
}

const getBmwStatusArgsSchema: JSONSchemaType<OpsGetBmwStatusArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
  },
  required: ['placeId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetBmwStatusArgs>

export interface OpsGetBmwSummaryArgs {
  placeId?: string
  year: number
  month?: number
}

const getBmwSummaryArgsSchema: JSONSchemaType<OpsGetBmwSummaryArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', nullable: true },
    year: { type: 'integer' },
    month: { type: 'integer', nullable: true },
  },
  required: ['year'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetBmwSummaryArgs>

export interface OpsStartBmwHandoverArgs {
  placeId: string
  bagBarcodes?: string[]
}

const startBmwHandoverArgsSchema: JSONSchemaType<OpsStartBmwHandoverArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    bagBarcodes: {
      type: 'array',
      items: { type: 'string' },
      nullable: true,
    },
  },
  required: ['placeId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsStartBmwHandoverArgs>

export interface OpsAttachBmwPickupReceiptArgs {
  pickupId: string
  receiptPhoto: string
}

const attachBmwPickupReceiptArgsSchema: JSONSchemaType<OpsAttachBmwPickupReceiptArgs> = {
  type: 'object',
  properties: {
    pickupId: { type: 'string', minLength: 1 },
    receiptPhoto: { type: 'string', minLength: 1 },
  },
  required: ['pickupId', 'receiptPhoto'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsAttachBmwPickupReceiptArgs>

export type OpsEnsureClinicsStarterSetArgs = Record<string, never>

const ensureClinicsStarterSetArgsSchema: JSONSchemaType<OpsEnsureClinicsStarterSetArgs> = {
  type: 'object',
  properties: {},
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsEnsureClinicsStarterSetArgs>

export interface OpsImportTempLogArgs {
  assetId: string
  csvContent: string
  columnMapping: {
    timestampColumn: string
    temperatureColumn: string
    probeColumn?: string
  }
}

const importTempLogArgsSchema: JSONSchemaType<OpsImportTempLogArgs> = {
  type: 'object',
  properties: {
    assetId: { type: 'string', minLength: 1 },
    csvContent: { type: 'string', minLength: 1 },
    columnMapping: {
      type: 'object',
      properties: {
        timestampColumn: { type: 'string', minLength: 1 },
        temperatureColumn: { type: 'string', minLength: 1 },
        probeColumn: { type: 'string', nullable: true },
      },
      required: ['timestampColumn', 'temperatureColumn'],
      additionalProperties: false,
    },
  },
  required: ['assetId', 'csvContent', 'columnMapping'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsImportTempLogArgs>

export interface OpsSetStaffStatusArgs {
  personKey: string
  inductionTrainingOn?: string
  lastAnnualTrainingOn?: string
  immunisationStatus?: string
  healthCheckOn?: string
  opId?: string
}

const setStaffStatusArgsSchema: JSONSchemaType<OpsSetStaffStatusArgs> = {
  type: 'object',
  properties: {
    personKey: { type: 'string', minLength: 1 },
    inductionTrainingOn: { type: 'string', nullable: true },
    lastAnnualTrainingOn: { type: 'string', nullable: true },
    immunisationStatus: { type: 'string', nullable: true },
    healthCheckOn: { type: 'string', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['personKey'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetStaffStatusArgs>

export interface OpsListStaffStatusArgs {
  staffGroup?: string
  dueOnly?: boolean
}

const listStaffStatusArgsSchema: JSONSchemaType<OpsListStaffStatusArgs> = {
  type: 'object',
  properties: {
    staffGroup: { type: 'string', nullable: true },
    dueOnly: { type: 'boolean', nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsListStaffStatusArgs>

export type OpsGetMyStaffStatusArgs = Record<string, never>

const getMyStaffStatusArgsSchema: JSONSchemaType<OpsGetMyStaffStatusArgs> = {
  type: 'object',
  properties: {},
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetMyStaffStatusArgs>

export interface OpsReportIncidentArgs {
  placeId: string
  kind: string
  severity: string
  occurredAt: string | number
  summary: string
  opId?: string
}

const reportIncidentArgsSchema: JSONSchemaType<OpsReportIncidentArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', minLength: 1 },
    kind: { type: 'string' },
    severity: { type: 'string' },
    occurredAt: { type: ['string', 'number'] } as unknown as Record<string, unknown>,
    summary: { type: 'string' },
    opId: { type: 'string', nullable: true },
  },
  required: ['placeId', 'kind', 'severity', 'occurredAt', 'summary'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsReportIncidentArgs>

export interface OpsReviewIncidentArgs {
  incidentId: string
  reviewNotes?: string
  opId?: string
}

const reviewIncidentArgsSchema: JSONSchemaType<OpsReviewIncidentArgs> = {
  type: 'object',
  properties: {
    incidentId: { type: 'string', minLength: 1 },
    reviewNotes: { type: 'string', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['incidentId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsReviewIncidentArgs>

export interface OpsAddCapaItemsArgs {
  incidentId: string
  capaItems: Array<Record<string, unknown>>
  opId?: string
}

const addCapaItemsArgsSchema: JSONSchemaType<OpsAddCapaItemsArgs> = {
  type: 'object',
  properties: {
    incidentId: { type: 'string', minLength: 1 },
    capaItems: { type: 'array', items: { type: 'object' } } as unknown as Record<string, unknown>,
    opId: { type: 'string', nullable: true },
  },
  required: ['incidentId', 'capaItems'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsAddCapaItemsArgs>

export interface OpsCompleteCapaItemArgs {
  incidentId: string
  capaItemId: string
  opId?: string
}

const completeCapaItemArgsSchema: JSONSchemaType<OpsCompleteCapaItemArgs> = {
  type: 'object',
  properties: {
    incidentId: { type: 'string', minLength: 1 },
    capaItemId: { type: 'string', minLength: 1 },
    opId: { type: 'string', nullable: true },
  },
  required: ['incidentId', 'capaItemId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsCompleteCapaItemArgs>

export interface OpsRecordEffectivenessArgs {
  incidentId: string
  effective: boolean
  effectivenessNotes?: string
  opId?: string
}

const recordEffectivenessArgsSchema: JSONSchemaType<OpsRecordEffectivenessArgs> = {
  type: 'object',
  properties: {
    incidentId: { type: 'string', minLength: 1 },
    effective: { type: 'boolean' },
    effectivenessNotes: { type: 'string', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: ['incidentId', 'effective'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsRecordEffectivenessArgs>

export interface OpsListIncidentsArgs {
  placeId?: string
  state?: string
  kind?: string
  severity?: string
}

const listIncidentsArgsSchema: JSONSchemaType<OpsListIncidentsArgs> = {
  type: 'object',
  properties: {
    placeId: { type: 'string', nullable: true },
    state: { type: 'string', nullable: true },
    kind: { type: 'string', nullable: true },
    severity: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsListIncidentsArgs>

export interface OpsGetIncidentArgs {
  incidentId: string
}

const getIncidentArgsSchema: JSONSchemaType<OpsGetIncidentArgs> = {
  type: 'object',
  properties: {
    incidentId: { type: 'string', minLength: 1 },
  },
  required: ['incidentId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetIncidentArgs>

export interface OpsSetIncidentSettingsArgs {
  reportWithinHours: number | null
  opId?: string
}

const setIncidentSettingsArgsSchema: JSONSchemaType<OpsSetIncidentSettingsArgs> = {
  type: 'object',
  properties: {
    reportWithinHours: { type: 'integer', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetIncidentSettingsArgs>

export type OpsGetIncidentSettingsArgs = Record<string, never>

const getIncidentSettingsArgsSchema: JSONSchemaType<OpsGetIncidentSettingsArgs> = {
  type: 'object',
  properties: {},
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetIncidentSettingsArgs>

export interface OpsAddHospitalComplianceStartersArgs {
  includeImaging?: boolean
  opId?: string
}

const addHospitalComplianceStartersArgsSchema: JSONSchemaType<OpsAddHospitalComplianceStartersArgs> = {
  type: 'object',
  properties: {
    includeImaging: { type: 'boolean', nullable: true },
    opId: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsAddHospitalComplianceStartersArgs>

export interface OpsSetOwnerFeedArgs {
  propertyId: string
  enabled: boolean
  sendAtLocal?: string
  owners: Array<{ name: string; phoneE164: string; email?: string }>
  stockLocationIds?: string[]
  expectedVersion?: number
  opId?: string
}

const setOwnerFeedArgsSchema: JSONSchemaType<OpsSetOwnerFeedArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1 },
    enabled: { type: 'boolean' },
    sendAtLocal: { type: 'string', minLength: 5, maxLength: 5, nullable: true },
    owners: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          name: { type: 'string', minLength: 1, maxLength: 80 },
          phoneE164: { type: 'string', minLength: 5, maxLength: 20 },
          email: { type: 'string', minLength: 3, maxLength: 120, nullable: true },
        },
        required: ['name', 'phoneE164'],
        additionalProperties: false,
      },
      minItems: 1,
      maxItems: 3,
    },
    stockLocationIds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      maxItems: 5,
      nullable: true,
    },
    expectedVersion: { type: 'integer', minimum: 1, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['propertyId', 'enabled', 'owners'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSetOwnerFeedArgs>

export interface OpsRemoveFeedOwnerArgs {
  propertyId: string
  ownerId: string
  expectedVersion?: number
  opId?: string
}

const removeFeedOwnerArgsSchema: JSONSchemaType<OpsRemoveFeedOwnerArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1 },
    ownerId: { type: 'string', minLength: 1 },
    expectedVersion: { type: 'integer', minimum: 1, nullable: true },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['propertyId', 'ownerId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsRemoveFeedOwnerArgs>

export interface OpsReissueOwnerFeedLinkArgs {
  propertyId: string
  ownerId: string
  opId?: string
}

const reissueOwnerFeedLinkArgsSchema: JSONSchemaType<OpsReissueOwnerFeedLinkArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1 },
    ownerId: { type: 'string', minLength: 1 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['propertyId', 'ownerId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsReissueOwnerFeedLinkArgs>

export interface OpsGetOwnerFeedArgs {
  propertyId: string
}

const getOwnerFeedArgsSchema: JSONSchemaType<OpsGetOwnerFeedArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1 },
  },
  required: ['propertyId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetOwnerFeedArgs>

export interface OpsListOwnerDigestsArgs {
  propertyId: string
}

const listOwnerDigestsArgsSchema: JSONSchemaType<OpsListOwnerDigestsArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1 },
  },
  required: ['propertyId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsListOwnerDigestsArgs>

export interface OpsSendOwnerDigestNowArgs {
  propertyId: string
  ownerId: string
  opId?: string
}

const sendOwnerDigestNowArgsSchema: JSONSchemaType<OpsSendOwnerDigestNowArgs> = {
  type: 'object',
  properties: {
    propertyId: { type: 'string', minLength: 1 },
    ownerId: { type: 'string', minLength: 1 },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['propertyId', 'ownerId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsSendOwnerDigestNowArgs>

export interface OpsGetStaysGroupViewArgs {
  groupPlaceId: string
  date?: string
}

const getStaysGroupViewArgsSchema: JSONSchemaType<OpsGetStaysGroupViewArgs> = {
  type: 'object',
  properties: {
    groupPlaceId: { type: 'string', minLength: 1 },
    date: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$', nullable: true },
  },
  required: ['groupPlaceId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsGetStaysGroupViewArgs>

export interface OpsPushStaysGroupTemplateArgs {
  groupPlaceId: string
  templateId: string
  targetPropertyIds?: string[]
  opId?: string
}

const pushStaysGroupTemplateArgsSchema: JSONSchemaType<OpsPushStaysGroupTemplateArgs> = {
  type: 'object',
  properties: {
    groupPlaceId: { type: 'string', minLength: 1 },
    templateId: { type: 'string', minLength: 1 },
    targetPropertyIds: {
      type: 'array',
      items: { type: 'string', minLength: 1 },
      nullable: true,
    },
    opId: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
  },
  required: ['groupPlaceId', 'templateId'],
  additionalProperties: false,
} as unknown as JSONSchemaType<OpsPushStaysGroupTemplateArgs>

/* =======================================================================
   Helper to build Ops ActionDefinitions cleanly
   ======================================================================= */

const generateOpId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `op-${Date.now()}-${Math.random().toString(36).slice(2)}`

function buildOpsAction<Args, Result>(opts: {
  id: string
  label: string
  description: string
  risk: ActionRisk
  argsSchema: JSONSchemaType<Args>
  permission: (context: ActionContext) => boolean
  needsWorkspaceId?: boolean
  handler: (args: Args, workspaceId: string) => Promise<Result>
}): ActionDefinition<Args, Result> {
  return {
    id: opts.id,
    label: opts.label,
    description: opts.description,
    risk: opts.risk,
    domain: 'ops',
    surfaces: ['catalog'],
    argsSchema: opts.argsSchema,
    permission: opts.permission,
    run: async (args, context): Promise<ActionOutcome<Result>> => {
      const correlationId = context.correlationId ?? 'ops-correlation'
      if (opts.needsWorkspaceId !== false && !context.workspaceId) {
        const message = `Workspace ID is required for ${opts.id}`
        context.showToast?.(message, 'error')
        return { kind: 'FAILED', message, recoverable: false, correlationId }
      }
      try {
        const res = await opts.handler(args, context.workspaceId)
        return {
          kind: 'COMPLETED',
          message: `Completed ${opts.label}.`,
          result: res,
          correlationId,
        }
      } catch (err) {
        const message = formatOpsError(err)
        context.showToast?.(message, 'error')
        return {
          kind: 'FAILED',
          message,
          recoverable: true,
          correlationId,
        }
      }
    },
  }
}

import { HOS_PATIENT_DOMAIN_ACTIONS } from './hosPatientActions'

export const OPS_DOMAIN_ACTIONS = {
  ...HOS_PATIENT_DOMAIN_ACTIONS,
  createPlace: buildOpsAction({
    id: OPS_CREATE_PLACE_ACTION_ID,
    label: 'Create place',
    description: 'Creates a place in the sector operations places tree.',
    risk: 'MEDIUM',
    argsSchema: createPlaceArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => createPlace({ workspaceId, ...args } as CreatePlaceInput),
  }),
  renamePlace: buildOpsAction({
    id: OPS_RENAME_PLACE_ACTION_ID,
    label: 'Rename place',
    description: 'Renames a place or updates its code in the sector operations places tree.',
    risk: 'MEDIUM',
    argsSchema: renamePlaceArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => renamePlace({ workspaceId, ...args } as RenamePlaceInput),
  }),
  setPlaceAccess: buildOpsAction({
    id: OPS_SET_PLACE_ACCESS_ACTION_ID,
    label: 'Set place access',
    description: 'Sets or clears the restricted access list for an operations place.',
    risk: 'MEDIUM',
    argsSchema: setPlaceAccessArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) => setPlaceAccess({ workspaceId, ...args } as SetPlaceAccessInput),
  }),
  setPlaceDutyChain: buildOpsAction({
    id: OPS_SET_PLACE_DUTY_CHAIN_ACTION_ID,
    label: 'Set place duty chain',
    description: 'Sets or updates the duty chain, department, and owner role on a place.',
    risk: 'MEDIUM',
    argsSchema: setPlaceDutyChainArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) => setPlaceDutyChain({ workspaceId, ...args } as SetPlaceDutyChainInput),
  }),
  setPlacePreset: buildOpsAction({
    id: OPS_SET_PLACE_PRESET_ACTION_ID,
    label: 'Set place preset',
    description: 'Sets the operating preset on a Sites sector SITE place.',
    risk: 'MEDIUM',
    argsSchema: setPlacePresetArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => setPlacePreset({ workspaceId, ...args } as SetPlacePresetInput),
  }),
  setPropertyRoomTypes: buildOpsAction({
    id: OPS_SET_PROPERTY_ROOM_TYPES_ACTION_ID,
    label: 'Set property room types',
    description: 'Sets or updates the room types on a Stays sector PROPERTY place.',
    risk: 'MEDIUM',
    argsSchema: setPropertyRoomTypesArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setPropertyRoomTypes({ workspaceId, ...args } as SetPropertyRoomTypesInput),
  }),
  setRoomType: buildOpsAction({
    id: OPS_SET_ROOM_TYPE_ACTION_ID,
    label: 'Set room type',
    description: 'Assigns a room type to ROOM places in a Stays property.',
    risk: 'LOW',
    argsSchema: setRoomTypeArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      setRoomType({ workspaceId, ...args } as SetRoomTypeInput),
  }),
  createJobSite: buildOpsAction({
    id: OPS_CREATE_JOB_SITE_ACTION_ID,
    label: 'Create service site',
    description: 'Creates a service site place in the Jobs sector.',
    risk: 'MEDIUM',
    argsSchema: createJobSiteArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createJobSite({ workspaceId, ...args } as CreateJobSiteInput),
  }),
  updateJobSite: buildOpsAction({
    id: OPS_UPDATE_JOB_SITE_ACTION_ID,
    label: 'Update service site',
    description: 'Updates a service site place in the Jobs sector.',
    risk: 'MEDIUM',
    argsSchema: updateJobSiteArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      updateJobSite({ workspaceId, ...args } as UpdateJobSiteInput),
  }),
  movePlace: buildOpsAction({
    id: OPS_MOVE_PLACE_ACTION_ID,
    label: 'Move place',
    description: 'Moves a place to a new parent in the places tree.',
    risk: 'MEDIUM',
    argsSchema: movePlaceArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => movePlace({ workspaceId, ...args } as MovePlaceInput),
  }),
  archivePlace: buildOpsAction({
    id: OPS_ARCHIVE_PLACE_ACTION_ID,
    label: 'Archive place',
    description: 'Archives or unarchives a place.',
    risk: 'MEDIUM',
    argsSchema: archivePlaceArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => archivePlace({ workspaceId, ...args } as ArchivePlaceInput),
  }),
  bulkCreatePlaces: buildOpsAction({
    id: OPS_BULK_CREATE_PLACES_ACTION_ID,
    label: 'Bulk create places',
    description: 'Bulk creates places from an indented outline.',
    risk: 'HIGH',
    argsSchema: bulkCreatePlacesArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      bulkCreatePlaces({ workspaceId, ...args } as BulkCreatePlacesInput),
  }),
  createPlaceToken: buildOpsAction({
    id: OPS_CREATE_PLACE_TOKEN_ACTION_ID,
    label: 'Create place QR token',
    description: 'Creates a signed QR token for a place.',
    risk: 'MEDIUM',
    argsSchema: createPlaceTokenArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createPlaceToken({ workspaceId, ...args } as CreatePlaceTokenInput),
  }),
  revokePlaceToken: buildOpsAction({
    id: OPS_REVOKE_PLACE_TOKEN_ACTION_ID,
    label: 'Revoke place QR token',
    description: 'Revokes a place QR token and issues a replacement.',
    risk: 'HIGH',
    argsSchema: revokePlaceTokenArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      revokePlaceToken({ workspaceId, ...args } as RevokePlaceTokenInput),
  }),
  resolvePlaceToken: buildOpsAction({
    id: OPS_RESOLVE_PLACE_TOKEN_ACTION_ID,
    label: 'Resolve place QR token',
    description: 'Resolves an opaque place QR token.',
    risk: 'READ_ONLY',
    argsSchema: resolvePlaceTokenArgsSchema,
    permission: opsMemberOrHelperPermission,
    needsWorkspaceId: false,
    handler: (args) => resolvePlaceToken(args as ResolvePlaceTokenInput),
  }),
  createParty: buildOpsAction({
    id: OPS_CREATE_PARTY_ACTION_ID,
    label: 'Create party',
    description: 'Creates a contractor, vendor, client, or consultant party.',
    risk: 'MEDIUM',
    argsSchema: createPartyArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => createParty({ workspaceId, ...args } as CreatePartyInput),
  }),
  updateParty: buildOpsAction({
    id: OPS_UPDATE_PARTY_ACTION_ID,
    label: 'Update party',
    description: 'Updates an existing party.',
    risk: 'MEDIUM',
    argsSchema: updatePartyArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => updateParty({ workspaceId, ...args } as UpdatePartyInput),
  }),
  setPartyMembers: buildOpsAction({
    id: OPS_SET_PARTY_MEMBERS_ACTION_ID,
    label: 'Set party members',
    description: 'Assigns workspace members to a party and updates worker visibility.',
    risk: 'HIGH',
    argsSchema: setPartyMembersArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      setPartyMembers({ workspaceId, ...args } as SetPartyMembersInput),
  }),
  createWorker: buildOpsAction({
    id: OPS_CREATE_WORKER_ACTION_ID,
    label: 'Create worker',
    description: 'Creates a workforce worker record.',
    risk: 'MEDIUM',
    argsSchema: createWorkerArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => createWorker({ workspaceId, ...args } as CreateWorkerInput),
  }),
  updateWorker: buildOpsAction({
    id: OPS_UPDATE_WORKER_ACTION_ID,
    label: 'Update worker',
    description: 'Updates a workforce worker record.',
    risk: 'MEDIUM',
    argsSchema: updateWorkerArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => updateWorker({ workspaceId, ...args } as UpdateWorkerInput),
  }),
  archiveWorker: buildOpsAction({
    id: OPS_ARCHIVE_WORKER_ACTION_ID,
    label: 'Archive worker',
    description: 'Archives a workforce worker record.',
    risk: 'MEDIUM',
    argsSchema: archiveWorkerArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => archiveWorker({ workspaceId, ...args } as ArchiveWorkerInput),
  }),
  addGang: buildOpsAction({
    id: OPS_ADD_GANG_ACTION_ID,
    label: 'Add gang of workers',
    description: 'Bulk creates a gang of workers with sequential worker codes.',
    risk: 'HIGH',
    argsSchema: addGangArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => addGang({ workspaceId, ...args } as AddGangInput),
  }),
  setSectorRoles: buildOpsAction({
    id: OPS_SET_SECTOR_ROLES_ACTION_ID,
    label: 'Set sector roles',
    description: 'Assigns sector operations roles (approver/reviewer) to a member.',
    risk: 'HIGH',
    argsSchema: setSectorRolesArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) => setSectorRoles({ workspaceId, ...args } as SetSectorRolesInput),
  }),
  saveTemplateDraft: buildOpsAction({
    id: OPS_SAVE_TEMPLATE_DRAFT_ACTION_ID,
    label: 'Save template draft',
    description: 'Creates or updates a draft register template version.',
    risk: 'MEDIUM',
    argsSchema: saveTemplateDraftArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      saveTemplateDraft({
        workspaceId,
        ...args,
        fields: args.fields as unknown as OpsTemplateField[],
      } as SaveTemplateDraftInput),
  }),
  submitTemplateVersion: buildOpsAction({
    id: OPS_SUBMIT_TEMPLATE_VERSION_ACTION_ID,
    label: 'Submit template version',
    description: 'Submits a draft template version for approval.',
    risk: 'MEDIUM',
    argsSchema: submitTemplateVersionArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      submitTemplateVersion({ workspaceId, ...args } as SubmitTemplateVersionInput),
  }),
  approveTemplateVersion: buildOpsAction({
    id: OPS_APPROVE_TEMPLATE_VERSION_ACTION_ID,
    label: 'Approve template version',
    description: 'Approves and locks a pending template version.',
    risk: 'HIGH',
    argsSchema: approveTemplateVersionArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      approveTemplateVersion({ workspaceId, ...args } as ApproveTemplateVersionInput),
  }),
  submitRecord: buildOpsAction({
    id: OPS_SUBMIT_RECORD_ACTION_ID,
    label: 'Submit register record',
    description: 'Submits a register record against an approved template.',
    risk: 'MEDIUM',
    argsSchema: submitRecordArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) => submitRecord({ workspaceId, ...args } as SubmitRecordInput),
  }),
  reviewRecord: buildOpsAction({
    id: OPS_REVIEW_RECORD_ACTION_ID,
    label: 'Review register record',
    description: 'Marks a submitted register record as reviewed.',
    risk: 'MEDIUM',
    argsSchema: reviewRecordArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => reviewRecord({ workspaceId, ...args } as ReviewRecordInput),
  }),
  returnRecord: buildOpsAction({
    id: OPS_RETURN_RECORD_ACTION_ID,
    label: 'Return register record',
    description: 'Returns a register record to its author with a reason.',
    risk: 'MEDIUM',
    argsSchema: returnRecordArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => returnRecord({ workspaceId, ...args } as ReturnRecordInput),
  }),
  approveRecord: buildOpsAction({
    id: OPS_APPROVE_RECORD_ACTION_ID,
    label: 'Approve register record',
    description: 'Approves and locks a register record.',
    risk: 'HIGH',
    argsSchema: approveRecordArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => approveRecord({ workspaceId, ...args } as ApproveRecordInput),
  }),
  addCorrection: buildOpsAction({
    id: OPS_ADD_CORRECTION_ACTION_ID,
    label: 'Add record correction',
    description: 'Adds an append-only correction entry to a register record.',
    risk: 'HIGH',
    argsSchema: addCorrectionArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => addCorrection({ workspaceId, ...args } as AddCorrectionInput),
  }),
  createDutyRole: buildOpsAction({
    id: OPS_CREATE_DUTY_ROLE_ACTION_ID,
    label: 'Create duty role',
    description: 'Creates a duty role for shift and escalation definitions.',
    risk: 'MEDIUM',
    argsSchema: createDutyRoleArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => createDutyRole({ workspaceId, ...args } as unknown as CreateDutyRoleInput),
  }),
  updateDutyRole: buildOpsAction({
    id: OPS_UPDATE_DUTY_ROLE_ACTION_ID,
    label: 'Update duty role',
    description: 'Updates a duty role.',
    risk: 'MEDIUM',
    argsSchema: updateDutyRoleArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => updateDutyRole({ workspaceId, ...args } as unknown as UpdateDutyRoleInput),
  }),
  archiveDutyRole: buildOpsAction({
    id: OPS_ARCHIVE_DUTY_ROLE_ACTION_ID,
    label: 'Archive duty role',
    description: 'Archives a duty role.',
    risk: 'MEDIUM',
    argsSchema: archiveDutyRoleArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => archiveDutyRole({ workspaceId, ...args }),
  }),
  createDepartment: buildOpsAction({
    id: OPS_CREATE_DEPARTMENT_ACTION_ID,
    label: 'Create department',
    description: 'Creates a workspace department.',
    risk: 'MEDIUM',
    argsSchema: createDepartmentArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      createDepartment({
        workspaceId,
        key: args.key,
        label: args.label,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      } as CreateDepartmentInput),
  }),
  renameDepartment: buildOpsAction({
    id: OPS_RENAME_DEPARTMENT_ACTION_ID,
    label: 'Rename department',
    description: 'Renames a workspace department.',
    risk: 'MEDIUM',
    argsSchema: renameDepartmentArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      renameDepartment({
        workspaceId,
        key: args.key,
        label: args.label,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      } as RenameDepartmentInput),
  }),
  archiveDepartment: buildOpsAction({
    id: OPS_ARCHIVE_DEPARTMENT_ACTION_ID,
    label: 'Archive department',
    description: 'Archives a workspace department.',
    risk: 'MEDIUM',
    argsSchema: archiveDepartmentArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      archiveDepartment({
        workspaceId,
        key: args.key,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      } as ArchiveDepartmentInput),
  }),
  createShift: buildOpsAction({
    id: OPS_CREATE_SHIFT_ACTION_ID,
    label: 'Create shift',
    description: 'Creates a shift definition with start/end minutes.',
    risk: 'MEDIUM',
    argsSchema: createShiftArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => createShift({ workspaceId, ...args } as unknown as CreateShiftInput),
  }),
  updateShift: buildOpsAction({
    id: OPS_UPDATE_SHIFT_ACTION_ID,
    label: 'Update shift',
    description: 'Updates a shift definition.',
    risk: 'MEDIUM',
    argsSchema: updateShiftArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => updateShift({ workspaceId, ...args } as unknown as UpdateShiftInput),
  }),
  archiveShift: buildOpsAction({
    id: OPS_ARCHIVE_SHIFT_ACTION_ID,
    label: 'Archive shift',
    description: 'Archives a shift.',
    risk: 'MEDIUM',
    argsSchema: archiveShiftArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => archiveShift({ workspaceId, ...args }),
  }),
  createCoverageRule: buildOpsAction({
    id: OPS_CREATE_COVERAGE_RULE_ACTION_ID,
    label: 'Create coverage rule',
    description: 'Creates a shift coverage rule.',
    risk: 'MEDIUM',
    argsSchema: createCoverageRuleArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => createCoverageRule({ workspaceId, ...args } as unknown as CreateCoverageRuleInput),
  }),
  updateCoverageRule: buildOpsAction({
    id: OPS_UPDATE_COVERAGE_RULE_ACTION_ID,
    label: 'Update coverage rule',
    description: 'Updates a coverage rule.',
    risk: 'MEDIUM',
    argsSchema: updateCoverageRuleArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => updateCoverageRule({ workspaceId, ...args } as unknown as UpdateCoverageRuleInput),
  }),
  archiveCoverageRule: buildOpsAction({
    id: OPS_ARCHIVE_COVERAGE_RULE_ACTION_ID,
    label: 'Archive coverage rule',
    description: 'Archives a coverage rule.',
    risk: 'MEDIUM',
    argsSchema: archiveCoverageRuleArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => archiveCoverageRule({ workspaceId, ...args }),
  }),
  createRoster: buildOpsAction({
    id: OPS_CREATE_ROSTER_ACTION_ID,
    label: 'Create roster',
    description: 'Creates a roster for a place and date period.',
    risk: 'MEDIUM',
    argsSchema: createRosterArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => createRoster({ workspaceId, ...args } as unknown as CreateRosterInput),
  }),
  saveDraftAssignments: buildOpsAction({
    id: OPS_SAVE_DRAFT_ASSIGNMENTS_ACTION_ID,
    label: 'Save draft assignments',
    description: 'Saves draft assignments on a roster.',
    risk: 'MEDIUM',
    argsSchema: saveDraftAssignmentsArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => saveDraftAssignments({ workspaceId, ...args } as unknown as SaveDraftAssignmentsInput),
  }),
  publishRoster: buildOpsAction({
    id: OPS_PUBLISH_ROSTER_ACTION_ID,
    label: 'Publish roster',
    description: 'Freezes and publishes a roster version.',
    risk: 'HIGH',
    argsSchema: publishRosterArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => publishRoster({ workspaceId, ...args } as unknown as PublishRosterInput),
  }),
  previewRosterWarnings: buildOpsAction({
    id: OPS_PREVIEW_ROSTER_WARNINGS_ACTION_ID,
    label: 'Preview roster warnings',
    description: 'Dry-run evaluates coverage and hard constraint warnings for a draft roster.',
    risk: 'LOW',
    argsSchema: previewRosterWarningsArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => previewRosterWarnings({ workspaceId, ...args } as unknown as PreviewRosterWarningsInput),
  }),
  acknowledgeRoster: buildOpsAction({
    id: OPS_ACKNOWLEDGE_ROSTER_ACTION_ID,
    label: 'Acknowledge roster',
    description: 'Acknowledges assigned shifts in a published roster.',
    risk: 'LOW',
    argsSchema: acknowledgeRosterArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) => acknowledgeRoster({ workspaceId, ...args } as unknown as AcknowledgeRosterInput),
  }),
  requestRosterChange: buildOpsAction({
    id: OPS_REQUEST_ROSTER_CHANGE_ACTION_ID,
    label: 'Request roster change',
    description: 'Requests a shift leave, swap, or on-call swap.',
    risk: 'MEDIUM',
    argsSchema: requestRosterChangeArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) => requestRosterChange({ workspaceId, ...args } as unknown as RequestRosterChangeInput),
  }),
  respondRosterChange: buildOpsAction({
    id: OPS_RESPOND_ROSTER_CHANGE_ACTION_ID,
    label: 'Respond to roster change',
    description: 'Accepts or rejects a shift swap request as counterparty.',
    risk: 'MEDIUM',
    argsSchema: respondRosterChangeArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) => respondRosterChange({ workspaceId, ...args } as unknown as RespondRosterChangeInput),
  }),
  decideRosterChange: buildOpsAction({
    id: OPS_DECIDE_ROSTER_CHANGE_ACTION_ID,
    label: 'Decide roster change',
    description: 'Approves or rejects a roster change request.',
    risk: 'HIGH',
    argsSchema: decideRosterChangeArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => decideRosterChange({ workspaceId, ...args } as unknown as DecideRosterChangeInput),
  }),
  cancelRosterChange: buildOpsAction({
    id: OPS_CANCEL_ROSTER_CHANGE_ACTION_ID,
    label: 'Cancel roster change',
    description: 'Cancels a pending roster change request.',
    risk: 'LOW',
    argsSchema: cancelRosterChangeArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) => cancelRosterChange({ workspaceId, ...args } as unknown as CancelRosterChangeInput),
  }),
  markAttendanceRoll: buildOpsAction({
    id: OPS_MARK_ATTENDANCE_ROLL_ACTION_ID,
    label: 'Mark attendance roll',
    description: 'Marks attendance roll in bulk for a day and shift.',
    risk: 'HIGH',
    argsSchema: markAttendanceRollArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) => markAttendanceRoll({ workspaceId, ...args } as unknown as MarkAttendanceRollInput),
  }),
  requestAttendanceCorrection: buildOpsAction({
    id: OPS_REQUEST_ATTENDANCE_CORRECTION_ACTION_ID,
    label: 'Request attendance correction',
    description: 'Requests a correction on a locked attendance entry.',
    risk: 'MEDIUM',
    argsSchema: requestAttendanceCorrectionArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) => requestAttendanceCorrection({ workspaceId, ...args } as unknown as RequestAttendanceCorrectionInput),
  }),
  decideAttendanceCorrection: buildOpsAction({
    id: OPS_DECIDE_ATTENDANCE_CORRECTION_ACTION_ID,
    label: 'Decide attendance correction',
    description: 'Approves or rejects an attendance correction request.',
    risk: 'HIGH',
    argsSchema: decideAttendanceCorrectionArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => decideAttendanceCorrection({ workspaceId, ...args } as unknown as DecideAttendanceCorrectionInput),
  }),
  importAttendanceCsv: buildOpsAction({
    id: OPS_IMPORT_ATTENDANCE_CSV_ACTION_ID,
    label: 'Import attendance CSV',
    description: 'Imports operational attendance records via CSV.',
    risk: 'HIGH',
    argsSchema: importAttendanceCsvArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => importAttendanceCsv({ workspaceId, ...args } as unknown as ImportAttendanceCsvInput),
  }),
  assignRelief: buildOpsAction({
    id: OPS_ASSIGN_RELIEF_ACTION_ID,
    label: 'Assign relief',
    description: 'Assigns relief person to an open relief request on a published roster.',
    risk: 'HIGH',
    argsSchema: assignReliefArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => assignRelief({ workspaceId, ...args } as unknown as AssignReliefInput),
  }),
  createSchedule: buildOpsAction({
    id: OPS_CREATE_SCHEDULE_ACTION_ID,
    label: 'Create schedule',
    description: 'Creates a recurring register schedule across places.',
    risk: 'MEDIUM',
    argsSchema: createScheduleArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createSchedule({ workspaceId, ...args } as unknown as CreateScheduleInput),
  }),
  updateSchedule: buildOpsAction({
    id: OPS_UPDATE_SCHEDULE_ACTION_ID,
    label: 'Update schedule',
    description: 'Updates a recurring register schedule and regenerates future dues.',
    risk: 'MEDIUM',
    argsSchema: updateScheduleArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      updateSchedule({ workspaceId, ...args } as unknown as UpdateScheduleInput),
  }),
  pauseSchedule: buildOpsAction({
    id: OPS_PAUSE_SCHEDULE_ACTION_ID,
    label: 'Pause schedule',
    description: 'Pauses an active register schedule and deletes not-yet-open dues.',
    risk: 'MEDIUM',
    argsSchema: scheduleIdArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      pauseSchedule({ workspaceId, ...args } as unknown as PauseScheduleInput),
  }),
  resumeSchedule: buildOpsAction({
    id: OPS_RESUME_SCHEDULE_ACTION_ID,
    label: 'Resume schedule',
    description: 'Resumes a paused register schedule from the next future window.',
    risk: 'MEDIUM',
    argsSchema: scheduleIdArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      resumeSchedule({ workspaceId, ...args } as unknown as ResumeScheduleInput),
  }),
  archiveSchedule: buildOpsAction({
    id: OPS_ARCHIVE_SCHEDULE_ACTION_ID,
    label: 'Archive schedule',
    description: 'Archives a register schedule and deletes not-yet-open dues.',
    risk: 'HIGH',
    argsSchema: scheduleIdArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      archiveSchedule({ workspaceId, ...args } as unknown as ArchiveScheduleInput),
  }),
  skipDue: buildOpsAction({
    id: OPS_SKIP_DUE_ACTION_ID,
    label: 'Skip due instance',
    description: 'Marks an open or missed due instance as skipped with a reason.',
    risk: 'MEDIUM',
    argsSchema: skipDueArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) => skipDue({ workspaceId, ...args } as unknown as SkipDueInput),
  }),
  recordCheckpointScan: buildOpsAction({
    id: OPS_RECORD_CHECKPOINT_SCAN_ACTION_ID,
    label: 'Record checkpoint scan',
    description: 'Scans a Sites patrol checkpoint QR token and completes the scheduled due.',
    risk: 'LOW',
    argsSchema: recordCheckpointScanArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      recordCheckpointScan({ workspaceId, ...args } as RecordCheckpointScanInput),
  }),
  upsertRequestKind: buildOpsAction({
    id: OPS_UPSERT_REQUEST_KIND_ACTION_ID,
    label: 'Upsert request kind',
    description: 'Tunes an existing request kind or creates a custom request kind.',
    risk: 'MEDIUM',
    argsSchema: upsertRequestKindArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) => {
      const k = args.kind
      return upsertRequestKind({
        workspaceId,
        kind: {
          workspaceId,
          key: String(k.key || ''),
          sector: String(k.sector || 'CLEANING') as OpsRequestSector,
          ...(typeof k.label === 'string' ? { label: k.label } : {}),
          ...(typeof k.ackMinutes === 'number' ? { ackMinutes: k.ackMinutes } : {}),
          ...(typeof k.dueMinutes === 'number' ? { dueMinutes: k.dueMinutes } : {}),
          ...(typeof k.escalateEveryMinutes === 'number' ? { escalateEveryMinutes: k.escalateEveryMinutes } : {}),
          ...(typeof k.dutyRoleKey === 'string' ? { dutyRoleKey: k.dutyRoleKey } : {}),
          ...(typeof k.verifyRequired === 'boolean' ? { verifyRequired: k.verifyRequired } : {}),
          ...(typeof k.verifierRoleKey === 'string' ? { verifierRoleKey: k.verifierRoleKey } : {}),
          ...(typeof k.restricted === 'boolean' ? { restricted: k.restricted } : {}),
          ...(Array.isArray(k.viewerRoleKeys) ? { viewerRoleKeys: k.viewerRoleKeys.map(String) } : {}),
          ...(typeof k.patientLinked === 'boolean' ? { patientLinked: k.patientLinked } : {}),
          ...(typeof k.photoEnabled === 'boolean' ? { photoEnabled: k.photoEnabled } : {}),
          ...(typeof k.archived === 'boolean' ? { archived: k.archived } : {}),
        },
        opId: args.opId || undefined,
      })
    },
  }),
  listRequestKinds: buildOpsAction({
    id: OPS_LIST_REQUEST_KINDS_ACTION_ID,
    label: 'List request kinds',
    description: 'Lists built-in and workspace-tuned request kinds for a sector.',
    risk: 'LOW',
    argsSchema: listRequestKindsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listRequestKinds({
        workspaceId,
        sector: (args.sector as OpsRequestSector) || undefined,
        includeArchived: args.includeArchived ?? undefined,
      }),
  }),
  raiseRequest: buildOpsAction({
    id: OPS_RAISE_REQUEST_ACTION_ID,
    label: 'Raise request',
    description: 'Raises an operational request for a place.',
    risk: 'LOW',
    argsSchema: raiseRequestArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      raiseRequest({
        workspaceId,
        sector: args.sector as OpsRequestSector,
        kind: args.kind,
        placeId: args.placeId,
        title: args.title,
        details: args.details ?? undefined,
        photos: args.photos ?? undefined,
        opId: args.opId || undefined,
      }),
  }),
  attachRequestPhotos: buildOpsAction({
    id: OPS_ATTACH_REQUEST_PHOTOS_ACTION_ID,
    label: 'Attach request photos',
    description: 'Attaches additional photos to an open request.',
    risk: 'LOW',
    argsSchema: attachRequestPhotosArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      attachRequestPhotos({
        workspaceId,
        requestId: args.requestId,
        photos: args.photos,
        opId: args.opId || undefined,
      }),
  }),
  acknowledgeRequest: buildOpsAction({
    id: OPS_ACKNOWLEDGE_REQUEST_ACTION_ID,
    label: 'Acknowledge request',
    description: 'Acknowledges receipt and responsibility for a request.',
    risk: 'LOW',
    argsSchema: requestIdArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      acknowledgeRequest({
        workspaceId,
        requestId: args.requestId,
        opId: args.opId || undefined,
      }),
  }),
  startRequest: buildOpsAction({
    id: OPS_START_REQUEST_ACTION_ID,
    label: 'Start request',
    description: 'Marks work started on an operational request.',
    risk: 'LOW',
    argsSchema: requestIdArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      startRequest({
        workspaceId,
        requestId: args.requestId,
        opId: args.opId || undefined,
      }),
  }),
  assignRequest: buildOpsAction({
    id: OPS_ASSIGN_REQUEST_ACTION_ID,
    label: 'Assign request',
    description: 'Assigns a request to a member.',
    risk: 'MEDIUM',
    argsSchema: assignRequestArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      assignRequest({
        workspaceId,
        requestId: args.requestId,
        assigneeUid: args.assigneeUid,
        opId: args.opId || undefined,
      }),
  }),
  markRequestDone: buildOpsAction({
    id: OPS_MARK_REQUEST_DONE_ACTION_ID,
    label: 'Mark request done',
    description: 'Marks a request as done with resolution photos.',
    risk: 'LOW',
    argsSchema: markRequestDoneArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      markRequestDone({
        workspaceId,
        requestId: args.requestId,
        photos: args.photos ?? undefined,
        opId: args.opId || undefined,
      }),
  }),
  verifyRequest: buildOpsAction({
    id: OPS_VERIFY_REQUEST_ACTION_ID,
    label: 'Verify request',
    description: 'Approves or rejects a completed request.',
    risk: 'MEDIUM',
    argsSchema: verifyRequestArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      verifyRequest({
        workspaceId,
        requestId: args.requestId,
        decision: args.decision,
        reason: args.reason ?? undefined,
        selfApprovalReason: args.selfApprovalReason ?? undefined,
        opId: args.opId || undefined,
      }),
  }),
  reopenRequest: buildOpsAction({
    id: OPS_REOPEN_REQUEST_ACTION_ID,
    label: 'Reopen request',
    description: 'Reopens a completed or verified request with a reason.',
    risk: 'MEDIUM',
    argsSchema: requestReasonArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      reopenRequest({
        workspaceId,
        requestId: args.requestId,
        reason: args.reason,
        opId: args.opId || undefined,
      }),
  }),
  cancelRequest: buildOpsAction({
    id: OPS_CANCEL_REQUEST_ACTION_ID,
    label: 'Cancel request',
    description: 'Cancels an open request with an explanation.',
    risk: 'MEDIUM',
    argsSchema: requestReasonArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      cancelRequest({
        workspaceId,
        requestId: args.requestId,
        reason: args.reason,
        opId: args.opId || undefined,
      }),
  }),
  scheduleJob: buildOpsAction({
    id: OPS_SCHEDULE_JOB_ACTION_ID,
    label: 'Schedule job',
    description: 'Schedules and assigns a jobs sector request.',
    risk: 'LOW',
    argsSchema: scheduleJobArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      scheduleJob({
        workspaceId,
        requestId: args.requestId,
        schedule: args.schedule ?? null,
        assigneeUid: args.assigneeUid ?? undefined,
        opId: args.opId || undefined,
      }),
  }),
  raiseRevisit: buildOpsAction({
    id: OPS_RAISE_REVISIT_ACTION_ID,
    label: 'Raise revisit',
    description: 'Raises a revisit request for a completed job.',
    risk: 'LOW',
    argsSchema: raiseRevisitArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      raiseRevisit({
        workspaceId,
        requestId: args.requestId,
        reason: args.reason,
        schedule: args.schedule ?? null,
        opId: args.opId || undefined,
      }),
  }),
  listJobBoard: buildOpsAction({
    id: OPS_LIST_JOB_BOARD_ACTION_ID,
    label: 'List job board',
    description: 'Lists scheduled and unscheduled jobs for a date.',
    risk: 'LOW',
    argsSchema: listJobBoardArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listJobBoard({
        workspaceId,
        date: args.date,
      }),
  }),
  createComplianceItem: buildOpsAction({
    id: OPS_CREATE_COMPLIANCE_ITEM_ACTION_ID,
    label: 'Create compliance item',
    description: 'Creates a licence, certificate, registration, or compliance item.',
    risk: 'MEDIUM',
    argsSchema: createComplianceItemArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createComplianceItem({
        workspaceId,
        sector: args.sector,
        title: args.title,
        category: args.category,
        validTo: args.validTo,
        authority: args.authority ?? undefined,
        reference: args.reference ?? undefined,
        placeId: args.placeId ?? undefined,
        validFrom: args.validFrom ?? undefined,
        reminderDays: args.reminderDays ?? undefined,
        ownerUid: args.ownerUid,
        backupUid: args.backupUid ?? undefined,
        notes: args.notes ?? undefined,
        evidence: args.evidence ?? undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  updateComplianceItem: buildOpsAction({
    id: OPS_UPDATE_COMPLIANCE_ITEM_ACTION_ID,
    label: 'Update compliance item',
    description: 'Updates details of an existing compliance item.',
    risk: 'MEDIUM',
    argsSchema: updateComplianceItemArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      updateComplianceItem({
        workspaceId,
        itemId: args.itemId,
        title: args.title,
        category: args.category,
        validTo: args.validTo,
        ownerUid: args.ownerUid,
        expectedVersion: args.expectedVersion,
        authority: args.authority ?? undefined,
        reference: args.reference ?? undefined,
        placeId: args.placeId ?? undefined,
        validFrom: args.validFrom ?? undefined,
        reminderDays: args.reminderDays ?? undefined,
        backupUid: args.backupUid ?? undefined,
        notes: args.notes ?? undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  renewComplianceItem: buildOpsAction({
    id: OPS_RENEW_COMPLIANCE_ITEM_ACTION_ID,
    label: 'Renew compliance item',
    description: 'Renews an existing compliance item with a new validity period.',
    risk: 'MEDIUM',
    argsSchema: renewComplianceItemArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      renewComplianceItem({
        workspaceId,
        itemId: args.itemId,
        validTo: args.validTo,
        validFrom: args.validFrom ?? undefined,
        reference: args.reference ?? undefined,
        evidence: args.evidence ?? undefined,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  attachComplianceEvidence: buildOpsAction({
    id: OPS_ATTACH_COMPLIANCE_EVIDENCE_ACTION_ID,
    label: 'Attach compliance evidence',
    description: 'Attaches evidence documents or photos to a compliance item.',
    risk: 'LOW',
    argsSchema: attachComplianceEvidenceArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      attachComplianceEvidence({
        workspaceId,
        itemId: args.itemId,
        evidence: args.evidence,
        opId: args.opId ?? undefined,
      }),
  }),
  archiveComplianceItem: buildOpsAction({
    id: OPS_ARCHIVE_COMPLIANCE_ITEM_ACTION_ID,
    label: 'Archive compliance item',
    description: 'Archives a compliance item with a recorded reason.',
    risk: 'HIGH',
    argsSchema: archiveComplianceItemArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      archiveComplianceItem({
        workspaceId,
        itemId: args.itemId,
        reason: args.reason,
        opId: args.opId ?? undefined,
      }),
  }),
  createAsset: buildOpsAction({
    id: OPS_CREATE_ASSET_ACTION_ID,
    label: 'Create asset',
    description: 'Creates a new plant, equipment or tool asset in the workspace register.',
    risk: 'MEDIUM',
    argsSchema: createAssetArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createAsset({
        workspaceId,
        sector: args.sector,
        assetCode: args.assetCode,
        name: args.name,
        criticality: args.criticality,
        placeId: args.placeId,
        category: args.category ?? undefined,
        model: args.model ?? undefined,
        serial: args.serial ?? undefined,
        amcUntil: args.amcUntil ?? undefined,
        warrantyUntil: args.warrantyUntil ?? undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  updateAsset: buildOpsAction({
    id: OPS_UPDATE_ASSET_ACTION_ID,
    label: 'Update asset',
    description: 'Updates details of an existing asset record.',
    risk: 'MEDIUM',
    argsSchema: updateAssetArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      updateAsset({
        workspaceId,
        assetId: args.assetId,
        name: args.name,
        criticality: args.criticality,
        placeId: args.placeId,
        expectedVersion: args.expectedVersion,
        category: args.category ?? undefined,
        model: args.model ?? undefined,
        serial: args.serial ?? undefined,
        amcUntil: args.amcUntil ?? undefined,
        warrantyUntil: args.warrantyUntil ?? undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  setAssetStatus: buildOpsAction({
    id: OPS_SET_ASSET_STATUS_ACTION_ID,
    label: 'Set asset status',
    description: 'Changes operational status of an asset between Available, In Use, and Maintenance.',
    risk: 'MEDIUM',
    argsSchema: setAssetStatusArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      setAssetStatus({
        workspaceId,
        assetId: args.assetId,
        toStatus: args.status,
        expectedVersion: args.expectedVersion,
        opId: args.opId ?? undefined,
      }),
  }),
  isolateAsset: buildOpsAction({
    id: OPS_ISOLATE_ASSET_ACTION_ID,
    label: 'Isolate asset',
    description: 'Isolates an asset with a recorded reason to take it out of service.',
    risk: 'HIGH',
    argsSchema: isolateAssetArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      isolateAsset({
        workspaceId,
        assetId: args.assetId,
        reason: args.reason,
        expectedVersion: args.expectedVersion,
        opId: args.opId ?? undefined,
      }),
  }),
  releaseAsset: buildOpsAction({
    id: OPS_RELEASE_ASSET_ACTION_ID,
    label: 'Release asset to use',
    description: 'Releases an isolated or maintenance asset back to service after repair.',
    risk: 'HIGH',
    argsSchema: releaseAssetArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      releaseAsset({
        workspaceId,
        assetId: args.assetId,
        toStatus: args.toStatus,
        expectedVersion: args.expectedVersion,
        selfRelease: args.selfRelease ?? undefined,
        reason: args.reason ?? undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  retireAsset: buildOpsAction({
    id: OPS_RETIRE_ASSET_ACTION_ID,
    label: 'Retire asset',
    description: 'Permanently retires an asset from the workspace register.',
    risk: 'HIGH',
    argsSchema: retireAssetArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      retireAsset({
        workspaceId,
        assetId: args.assetId,
        reason: args.reason,
        expectedVersion: args.expectedVersion,
        opId: args.opId ?? undefined,
      }),
  }),
  recordAssetServiceVisit: buildOpsAction({
    id: OPS_RECORD_ASSET_SERVICE_VISIT_ACTION_ID,
    label: 'Record service visit',
    description: 'Records a vendor PM, calibration, repair or AMC service visit for an asset.',
    risk: 'MEDIUM',
    argsSchema: recordAssetServiceVisitArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      recordAssetServiceVisit({
        workspaceId,
        assetId: args.assetId,
        visitDayKey: args.visitDayKey,
        kind: args.kind,
        summary: args.summary,
        vendorName: args.vendorName ?? undefined,
        engineerName: args.engineerName ?? undefined,
        invoiceRef: args.invoiceRef ?? undefined,
        requestId: args.requestId ?? undefined,
        filePaths: args.filePaths ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  createEngagement: buildOpsAction({
    id: OPS_CREATE_ENGAGEMENT_ACTION_ID,
    label: 'Create engagement',
    description: 'Creates a new client delivery engagement.',
    risk: 'MEDIUM',
    argsSchema: createEngagementArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createEngagement({
        workspaceId,
        clientId: args.clientId,
        name: args.name,
        model: args.model,
        startDate: args.startDate,
        endDate: args.endDate ?? undefined,
        milestones: args.milestones
          ? args.milestones.map((m) => ({
              id: m.id ?? undefined,
              title: m.title,
              dueDate: m.dueDate ?? undefined,
              status: (m.status ?? 'PLANNED') as OpsMilestoneStatus,
            }))
          : undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  updateEngagement: buildOpsAction({
    id: OPS_UPDATE_ENGAGEMENT_ACTION_ID,
    label: 'Update engagement',
    description: 'Updates delivery engagement details.',
    risk: 'MEDIUM',
    argsSchema: updateEngagementArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      updateEngagement({
        workspaceId,
        engagementId: args.engagementId,
        name: args.name ?? undefined,
        startDate: args.startDate ?? undefined,
        endDate: args.endDate ?? undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  setEngagementStatus: buildOpsAction({
    id: OPS_SET_ENGAGEMENT_STATUS_ACTION_ID,
    label: 'Set engagement status',
    description: 'Changes the status of a delivery engagement (ACTIVE, PAUSED, CLOSED).',
    risk: 'MEDIUM',
    argsSchema: setEngagementStatusArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      setEngagementStatus({
        workspaceId,
        engagementId: args.engagementId,
        status: args.status,
        opId: args.opId ?? undefined,
      }),
  }),
  setEngagementMilestones: buildOpsAction({
    id: OPS_SET_ENGAGEMENT_MILESTONES_ACTION_ID,
    label: 'Set engagement milestones',
    description: 'Updates milestone statuses for an engagement.',
    risk: 'MEDIUM',
    argsSchema: setEngagementMilestonesArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      setEngagementMilestones({
        workspaceId,
        engagementId: args.engagementId,
        milestones: args.milestones.map((m) => ({
          id: m.id ?? undefined,
          title: m.title,
          dueDate: m.dueDate ?? undefined,
          status: m.status,
        })),
        reason: args.reason ?? undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  setEngagementFinance: buildOpsAction({
    id: OPS_SET_ENGAGEMENT_FINANCE_ACTION_ID,
    label: 'Set engagement finance',
    description: 'Configures financial terms, budgets, rate cards, and retainer settings.',
    risk: 'HIGH',
    argsSchema: setEngagementFinanceArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setEngagementFinance({
        workspaceId,
        engagementId: args.engagementId,
        currency: args.currency,
        fixedPriceMinor: args.fixedPriceMinor ?? undefined,
        milestoneAmounts: args.milestoneAmounts ?? undefined,
        retainer: args.retainer
          ? {
              monthlyFee: args.retainer.monthlyFee,
              includedHours: args.retainer.includedHours,
              overageRate: args.retainer.overageRate,
              rolloverCapHours: args.retainer.rolloverCapHours ?? undefined,
            }
          : undefined,
        rateCard: args.rateCard
          ? args.rateCard.map((rc) => ({
              uid: rc.uid,
              billRate: rc.billRate,
            }))
          : undefined,
        targetMarginPercent: args.targetMarginPercent ?? undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  upsertAllocation: buildOpsAction({
    id: OPS_UPSERT_ALLOCATION_ACTION_ID,
    label: 'Upsert member allocation',
    description: 'Allocates a workspace member to an engagement with planned hours and rate.',
    risk: 'MEDIUM',
    argsSchema: upsertAllocationArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      upsertAllocation({
        workspaceId,
        allocationId: args.allocationId ?? undefined,
        engagementId: args.engagementId,
        uid: args.uid,
        hoursPerWeek: args.hoursPerWeek ?? undefined,
        percent: args.percent ?? undefined,
        from: args.from,
        to: args.to ?? undefined,
        kind: args.kind,
        opId: args.opId ?? undefined,
      }),
  }),
  endAllocation: buildOpsAction({
    id: OPS_END_ALLOCATION_ACTION_ID,
    label: 'End member allocation',
    description: 'Sets an end date for a member allocation.',
    risk: 'MEDIUM',
    argsSchema: endAllocationArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      endAllocation({
        workspaceId,
        allocationId: args.allocationId,
        toDate: args.toDate,
        opId: args.opId ?? undefined,
      }),
  }),
  linkGoalToEngagement: buildOpsAction({
    id: OPS_LINK_GOAL_TO_ENGAGEMENT_ACTION_ID,
    label: 'Link Goal to engagement',
    description: 'Links a Goal to a delivery engagement, synchronizing client ownership.',
    risk: 'MEDIUM',
    argsSchema: linkGoalToEngagementArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      linkGoalToEngagement({
        workspaceId,
        goalId: args.goalId,
        engagementId: args.engagementId,
        opId: args.opId ?? undefined,
      }),
  }),
  unlinkGoalFromEngagement: buildOpsAction({
    id: OPS_UNLINK_GOAL_FROM_ENGAGEMENT_ACTION_ID,
    label: 'Unlink Goal from engagement',
    description: 'Unlinks a Goal from its delivery engagement.',
    risk: 'MEDIUM',
    argsSchema: unlinkGoalFromEngagementArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      unlinkGoalFromEngagement({
        workspaceId,
        goalId: args.goalId,
        engagementId: args.engagementId,
        opId: args.opId ?? undefined,
      }),
  }),
  recomputeEngagementMargin: buildOpsAction({
    id: OPS_RECOMPUTE_ENGAGEMENT_MARGIN_ACTION_ID,
    label: 'Recompute engagement margin',
    description: 'Recomputes margin and rollups for an engagement from source entries.',
    risk: 'LOW',
    argsSchema: recomputeEngagementMarginArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      recomputeEngagementMargin({
        workspaceId,
        engagementId: args.engagementId,
      }),
  }),
  exportDeliveryCsv: buildOpsAction({
    id: OPS_EXPORT_DELIVERY_CSV_ACTION_ID,
    label: 'Export delivery CSV',
    description: 'Exports delivery engagement financials, hours, and margins to CSV for accountants.',
    risk: 'LOW',
    argsSchema: exportDeliveryCsvArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      exportDeliveryCsv({
        workspaceId,
        from: args.from,
        to: args.to,
        engagementId: args.engagementId ?? undefined,
      }),
  }),
  delRaiseChangeRequest: buildOpsAction({
    id: OPS_DEL_RAISE_CHANGE_REQUEST_ACTION_ID,
    label: 'Raise change request (draft)',
    description: 'Creates a draft delivery change request (creates a DRAFT only; a person always sends).',
    risk: 'LOW',
    argsSchema: delRaiseChangeRequestArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createChangeRequest({
        workspaceId,
        engagementId: args.engagementId,
        title: args.title,
        whatChanges: args.whatChanges,
        hoursEstimate: args.hoursEstimate,
        releaseTag: args.releaseTag ?? undefined,
        linkedNodeIds: args.linkedNodeIds ?? undefined,
        opId: args.opId ?? undefined,
      }),
  }),
  delSendClientLinks: buildOpsAction({
    id: OPS_DEL_SEND_CLIENT_LINKS_ACTION_ID,
    label: 'Send client links',
    description: 'Issues and sends portal links to client approvers of an engagement.',
    risk: 'LOW',
    argsSchema: delSendClientLinksArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      issueClientLinks({
        workspaceId,
        subjectType: 'DELIVERY_ENGAGEMENT',
        subjectId: args.engagementId,
        opId: args.opId ?? undefined,
      }),
  }),
  delRevokeClientLink: buildOpsAction({
    id: OPS_DEL_REVOKE_CLIENT_LINK_ACTION_ID,
    label: 'Revoke client link',
    description: 'Revokes a client portal link.',
    risk: 'LOW',
    argsSchema: delRevokeClientLinkArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      revokeClientLink({
        workspaceId,
        linkId: args.linkId,
        opId: args.opId ?? undefined,
      }),
  }),
  createOutsideApproval: buildOpsAction({
    id: OPS_CREATE_APPROVAL_ACTION_ID,
    label: 'Create outside approval',
    description: 'Creates an outside approval request and freezes the subject snapshot.',
    risk: 'MEDIUM',
    argsSchema: createApprovalArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createOutsideApproval({
        workspaceId,
        subjectType: args.subjectType,
        subjectId: args.subjectId,
        approvers: args.approvers,
        mode: args.mode,
        expiresInDays: args.expiresInDays ?? undefined,
        message: args.message ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  reissueApproverLink: buildOpsAction({
    id: OPS_REISSUE_APPROVAL_LINK_ACTION_ID,
    label: 'Reissue approver link',
    description: 'Reissues a new outside approval link for a specific approver.',
    risk: 'MEDIUM',
    argsSchema: reissueApprovalLinkArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      reissueApproverLink({
        workspaceId,
        approvalId: args.approvalId,
        approverId: args.approverId,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  revokeOutsideApproval: buildOpsAction({
    id: OPS_REVOKE_APPROVAL_ACTION_ID,
    label: 'Revoke outside approval',
    description: 'Revokes an active outside approval and closes all pending approvers.',
    risk: 'HIGH',
    argsSchema: revokeApprovalArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      revokeOutsideApproval({
        workspaceId,
        approvalId: args.approvalId,
        reason: args.reason,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  setApprovalSettings: buildOpsAction({
    id: OPS_SET_APPROVAL_SETTINGS_ACTION_ID,
    label: 'Set approval settings',
    description: 'Configures outside approval settings and admin-only kinds.',
    risk: 'HIGH',
    argsSchema: setApprovalSettingsArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setApprovalSettings({
        workspaceId,
        adminOnlyKinds: args.adminOnlyKinds,
        version: args.version ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  importOpsCsv: buildOpsAction({
    id: OPS_IMPORT_CSV_ACTION_ID,
    label: 'Import CSV',
    description: 'Imports operational records via CSV.',
    risk: 'HIGH',
    argsSchema: importCsvArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      importOpsCsv({ workspaceId, ...args } as unknown as ImportOpsCsvInput),
  }),
  exportOpsCsv: buildOpsAction({
    id: OPS_EXPORT_CSV_ACTION_ID,
    label: 'Export CSV',
    description: 'Exports operational records to CSV.',
    risk: 'LOW',
    argsSchema: exportCsvArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      exportOpsCsv({ workspaceId, ...args } as unknown as ExportOpsCsvInput),
  }),
  setOpsWageRate: buildOpsAction({
    id: OPS_SET_OPS_WAGE_RATE_ACTION_ID,
    label: 'Set wage rate',
    description: 'Sets operational wage rate for a worker or member.',
    risk: 'HIGH',
    argsSchema: setWageRateArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setOpsWageRate({
        workspaceId,
        personRef: args.personRef as unknown as OpsWagePersonRef,
        effectiveFrom: args.effectiveFrom,
        dayRateMinor: args.dayRateMinor,
        otHourRateMinor: args.otHourRateMinor,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  previewOpsWageSheet: buildOpsAction({
    id: OPS_PREVIEW_OPS_WAGE_SHEET_ACTION_ID,
    label: 'Preview wage sheet',
    description: 'Previews operational wage sheet calculations for a period.',
    risk: 'LOW',
    argsSchema: previewWageSheetArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      previewOpsWageSheet({
        workspaceId,
        periodStart: args.periodStart,
        periodEnd: args.periodEnd,
        partyId: args.partyId ?? undefined,
      }),
  }),
  lockOpsWageSheet: buildOpsAction({
    id: OPS_LOCK_OPS_WAGE_SHEET_ACTION_ID,
    label: 'Lock wage sheet',
    description: 'Freezes operational wage sheet for a period.',
    risk: 'HIGH',
    argsSchema: lockWageSheetArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      lockOpsWageSheet({
        workspaceId,
        periodStart: args.periodStart,
        periodEnd: args.periodEnd,
        partyId: args.partyId ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  unlockOpsWageSheet: buildOpsAction({
    id: OPS_UNLOCK_OPS_WAGE_SHEET_ACTION_ID,
    label: 'Unlock wage sheet',
    description: 'Unlocks operational wage sheet for corrections (owner only).',
    risk: 'HIGH',
    argsSchema: unlockWageSheetArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      unlockOpsWageSheet({
        workspaceId,
        sheetId: args.sheetId,
        reason: args.reason,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  exportOpsWageSheetCsv: buildOpsAction({
    id: OPS_EXPORT_OPS_WAGE_SHEET_CSV_ACTION_ID,
    label: 'Export wage sheet CSV',
    description: 'Exports operational wage sheet CSV.',
    risk: 'LOW',
    argsSchema: exportWageSheetCsvArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      exportOpsWageSheetCsv({
        workspaceId,
        periodStart: args.periodStart,
        periodEnd: args.periodEnd,
        partyId: args.partyId ?? undefined,
        sheetId: args.sheetId ?? undefined,
      }),
  }),
  getOpsWageSheetStatus: buildOpsAction({
    id: OPS_GET_OPS_WAGE_SHEET_STATUS_ACTION_ID,
    label: 'Get wage sheet status',
    description: 'Checks if attendance changed since wage sheet lock.',
    risk: 'LOW',
    argsSchema: getWageSheetStatusArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      getOpsWageSheetStatus({
        workspaceId,
        sheetId: args.sheetId,
      }),
  }),
  requestEvidenceExport: buildOpsAction({
    id: OPS_REQUEST_EVIDENCE_EXPORT_ACTION_ID,
    label: 'Request evidence export',
    description: 'Requests evidence export archive for places, periods, or kinds.',
    risk: 'MEDIUM',
    argsSchema: requestEvidenceExportArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      requestEvidenceExport({
        workspaceId,
        scope: args.scope,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  getEvidenceExportDownload: buildOpsAction({
    id: OPS_GET_EVIDENCE_EXPORT_DOWNLOAD_ACTION_ID,
    label: 'Get evidence export download',
    description: 'Generates secure download URLs for an evidence export archive.',
    risk: 'LOW',
    argsSchema: getEvidenceExportDownloadArgsSchema,
    permission: opsEditorPermission,
    handler: (args) =>
      getEvidenceExportDownload({
        exportId: args.exportId,
      }),
  }),
  setRetentionSettings: buildOpsAction({
    id: OPS_SET_RETENTION_SETTINGS_ACTION_ID,
    label: 'Set retention settings',
    description: 'Sets workspace retention duration for operational record classes.',
    risk: 'HIGH',
    argsSchema: setRetentionSettingsArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setRetentionSettings({
        workspaceId,
        classes: args.classes,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  recordOpsRestrictedView: buildOpsAction({
    id: OPS_RECORD_OPS_RESTRICTED_VIEW_ACTION_ID,
    label: 'Record ops restricted view',
    description: 'Audits access when a member or helper opens a restricted ops request or record.',
    risk: 'LOW',
    argsSchema: recordOpsRestrictedViewArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      recordOpsRestrictedView({
        workspaceId,
        sourceType: args.sourceType,
        sourceId: args.sourceId,
      }),
  }),
  setOpsPrivacySettings: buildOpsAction({
    id: OPS_SET_OPS_PRIVACY_SETTINGS_ACTION_ID,
    label: 'Set ops privacy settings',
    description: 'Configures workspace offline duration and privacy controls for operational sector data.',
    risk: 'HIGH',
    argsSchema: setOpsPrivacySettingsArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setOpsPrivacySettings({
        workspaceId,
        offlineMaxDays: args.offlineMaxDays,
      }),
  }),
  setLegalHold: buildOpsAction({
    id: OPS_SET_LEGAL_HOLD_ACTION_ID,
    label: 'Set legal hold',
    description: 'Applies legal hold to prevent retention purging of records, kinds, or places.',
    risk: 'HIGH',
    argsSchema: setLegalHoldArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setLegalHold({
        workspaceId,
        target: args.target,
        reason: args.reason,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  requestLegalHoldLift: buildOpsAction({
    id: OPS_REQUEST_LEGAL_HOLD_LIFT_ACTION_ID,
    label: 'Request legal hold lift',
    description: 'Initiates request to lift an active legal hold.',
    risk: 'HIGH',
    argsSchema: requestLegalHoldLiftArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      requestLegalHoldLift({
        workspaceId,
        holdId: args.holdId,
        reason: args.reason,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  confirmLegalHoldLift: buildOpsAction({
    id: OPS_CONFIRM_LEGAL_HOLD_LIFT_ACTION_ID,
    label: 'Confirm legal hold lift',
    description: 'Confirms and finalizes lifting an active legal hold (requires secondary admin or sole owner).',
    risk: 'HIGH',
    argsSchema: confirmLegalHoldLiftArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      confirmLegalHoldLift({
        workspaceId,
        holdId: args.holdId,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  cancelLegalHoldLift: buildOpsAction({
    id: OPS_CANCEL_LEGAL_HOLD_LIFT_ACTION_ID,
    label: 'Cancel legal hold lift',
    description: 'Cancels pending lift of legal hold, returning it to active.',
    risk: 'MEDIUM',
    argsSchema: cancelLegalHoldLiftArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      cancelLegalHoldLift({
        workspaceId,
        holdId: args.holdId,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  restoreFromRetentionBin: buildOpsAction({
    id: OPS_RESTORE_FROM_RETENTION_BIN_ACTION_ID,
    label: 'Restore from retention bin',
    description: 'Restores a binned record and its subcollections/attachments with a 90-day grace period.',
    risk: 'MEDIUM',
    argsSchema: restoreFromRetentionBinArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      restoreFromRetentionBin({
        workspaceId,
        binId: args.binId,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  listSealedArchives: buildOpsAction({
    id: OPS_LIST_SEALED_ARCHIVES_ACTION_ID,
    label: 'List sealed archives',
    description: 'Lists sealed archives available to former owners.',
    risk: 'LOW',
    argsSchema: listSealedArchivesArgsSchema,
    permission: opsPublicPermission,
    handler: () => listSealedArchives({}),
  }),
  requestSealedArchiveExport: buildOpsAction({
    id: OPS_REQUEST_SEALED_ARCHIVE_EXPORT_ACTION_ID,
    label: 'Request sealed archive export',
    description: 'Requests signed export of a sealed archive.',
    risk: 'MEDIUM',
    argsSchema: requestSealedArchiveExportArgsSchema,
    permission: opsAdminPermission,
    handler: (args) =>
      requestSealedArchiveExport({
        archiveId: args.archiveId,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  getRetentionLockSummary: buildOpsAction({
    id: OPS_GET_RETENTION_LOCK_SUMMARY_ACTION_ID,
    label: 'Get retention lock summary',
    description: 'Returns count of locked records by class and next unlock date.',
    risk: 'LOW',
    argsSchema: getRetentionLockSummaryArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      getRetentionLockSummary({
        workspaceId: args.workspaceId || workspaceId,
      }),
  }),
  checkOutRoom: buildOpsAction({
    id: OPS_CHECK_OUT_ROOM_ACTION_ID,
    label: 'Check out room',
    description: 'Marks a guest check-out for a Stays sector room, transitioning to vacant and dirty.',
    risk: 'MEDIUM',
    argsSchema: checkOutRoomArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      checkOutRoom({
        workspaceId,
        roomId: args.roomId,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        source: args.source ?? undefined,
      } as CheckOutRoomInput),
  }),
  markRoomOccupied: buildOpsAction({
    id: OPS_MARK_ROOM_OCCUPIED_ACTION_ID,
    label: 'Mark room occupied',
    description: 'Marks a room as occupied by front desk or admin.',
    risk: 'MEDIUM',
    argsSchema: markRoomOccupiedArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      markRoomOccupied({
        workspaceId,
        roomId: args.roomId,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        source: args.source ?? undefined,
      } as MarkRoomOccupiedInput),
  }),
  setRoomDueOut: buildOpsAction({
    id: OPS_SET_ROOM_DUE_OUT_ACTION_ID,
    label: 'Set room due out',
    description: 'Sets a room as due out at a scheduled departure time.',
    risk: 'LOW',
    argsSchema: setRoomDueOutArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      setRoomDueOut({
        workspaceId,
        roomId: args.roomId,
        dueOutAt: args.dueOutAt,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        source: args.source ?? undefined,
      } as SetRoomDueOutInput),
  }),
  startRoomClean: buildOpsAction({
    id: OPS_START_ROOM_CLEAN_ACTION_ID,
    label: 'Start room clean',
    description: 'Attendant starts cleaning a room, transitioning housekeeping to cleaning.',
    risk: 'LOW',
    argsSchema: startRoomCleanArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      startRoomClean({
        workspaceId,
        roomId: args.roomId,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        source: args.source ?? undefined,
      } as StartRoomCleanInput),
  }),
  markRoomCleaned: buildOpsAction({
    id: OPS_MARK_ROOM_CLEANED_ACTION_ID,
    label: 'Mark room cleaned',
    description: 'Submits proof of clean photos and marks room cleaned.',
    risk: 'MEDIUM',
    argsSchema: markRoomCleanedArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      markRoomCleaned({
        workspaceId,
        roomId: args.roomId,
        values: args.values,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        source: args.source ?? undefined,
      } as MarkRoomCleanedInput),
  }),
  inspectRoom: buildOpsAction({
    id: OPS_INSPECT_ROOM_ACTION_ID,
    label: 'Inspect room',
    description: 'Passes or fails housekeeping clean inspection for a room.',
    risk: 'MEDIUM',
    argsSchema: inspectRoomArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      inspectRoom({
        workspaceId,
        roomId: args.roomId,
        passed: args.passed,
        failedItems: args.failedItems ?? undefined,
        selfInspectionReason: args.selfInspectionReason ?? undefined,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        source: args.source ?? undefined,
      } as InspectRoomInput),
  }),
  setRoomService: buildOpsAction({
    id: OPS_SET_ROOM_SERVICE_ACTION_ID,
    label: 'Set room service status',
    description: 'Takes a room out of order or out of service, or returns it to service.',
    risk: 'HIGH',
    argsSchema: setRoomServiceArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setRoomService({
        workspaceId,
        roomId: args.roomId,
        service: args.service,
        serviceReason: args.serviceReason ?? undefined,
        workOrderRequestId: args.workOrderRequestId ?? undefined,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        source: args.source ?? undefined,
      } as SetRoomServiceInput),
  }),
  setRoomTags: buildOpsAction({
    id: OPS_SET_ROOM_TAGS_ACTION_ID,
    label: 'Set room tags',
    description: 'Sets or clears DND or VIP tags on a room.',
    risk: 'LOW',
    argsSchema: setRoomTagsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      setRoomTags({
        workspaceId,
        roomId: args.roomId,
        dnd: args.dnd ?? undefined,
        vip: args.vip ?? undefined,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        source: args.source ?? undefined,
      } as SetRoomTagsInput),
  }),
  setPropertyMinibarCheck: buildOpsAction({
    id: OPS_SET_PROPERTY_MINIBAR_CHECK_ACTION_ID,
    label: 'Set property minibar check setting',
    description: 'Toggles automatic minibar check request generation on guest checkout for a property.',
    risk: 'LOW',
    argsSchema: setPropertyMinibarCheckArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setPropertyMinibarCheck({
        workspaceId,
        propertyId: args.propertyId,
        minibarCheckOnCheckout: args.minibarCheckOnCheckout,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      } as SetPropertyMinibarCheckInput),
  }),
  setPropertyGuestRequestsEnabled: buildOpsAction({
    id: OPS_SET_PROPERTY_GUEST_REQUESTS_ENABLED_ACTION_ID,
    label: 'Set property guest requests enabled',
    description: 'Enables or disables guest requests via room QR codes for a property.',
    risk: 'MEDIUM',
    argsSchema: setPropertyGuestRequestsEnabledArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setPropertyGuestRequestsEnabled({
        workspaceId,
        propertyId: args.propertyId,
        guestRequestsEnabled: args.guestRequestsEnabled,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  prepareRoomCleanRecord: buildOpsAction({
    id: OPS_PREPARE_ROOM_CLEAN_RECORD_ACTION_ID,
    label: 'Prepare room clean record',
    description: 'Prepares or reuses a room clean draft record with required photo fields.',
    risk: 'LOW',
    argsSchema: prepareRoomCleanRecordArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      prepareRoomCleanRecord({
        workspaceId,
        roomId: args.roomId,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  assignRooms: buildOpsAction({
    id: OPS_ASSIGN_ROOMS_ACTION_ID,
    label: 'Assign rooms',
    description: 'Assigns rooms to an attendant for a day.',
    risk: 'MEDIUM',
    argsSchema: assignRoomsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      assignRooms({
        workspaceId,
        propertyId: args.propertyId,
        roomIds: args.roomIds,
        attendantUid: args.attendantUid,
        day: args.day ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  unassignRoom: buildOpsAction({
    id: OPS_UNASSIGN_ROOM_ACTION_ID,
    label: 'Unassign room',
    description: 'Unassigns a room.',
    risk: 'MEDIUM',
    argsSchema: unassignRoomArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      unassignRoom({
        workspaceId,
        roomId: args.roomId,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  reassignRoom: buildOpsAction({
    id: OPS_REASSIGN_ROOM_ACTION_ID,
    label: 'Reassign room',
    description: 'Reassigns a room to an attendant.',
    risk: 'MEDIUM',
    argsSchema: reassignRoomArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      reassignRoom({
        workspaceId,
        roomId: args.roomId,
        attendantUid: args.attendantUid,
        day: args.day ?? undefined,
        expectedVersion: args.expectedVersion ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
  }),
  upsertCustomer: buildOpsAction({
    id: OPS_UPSERT_CUSTOMER_ACTION_ID,
    label: 'Upsert customer',
    description: 'Creates or updates a customer in the sector operations customers register.',
    risk: 'MEDIUM',
    argsSchema: upsertCustomerArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      upsertCustomer({
        workspaceId,
        ...args,
      }),
  }),
  archiveCustomer: buildOpsAction({
    id: OPS_ARCHIVE_CUSTOMER_ACTION_ID,
    label: 'Archive customer',
    description: 'Archives a customer record in the register.',
    risk: 'MEDIUM',
    argsSchema: archiveCustomerArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      archiveCustomer({
        workspaceId,
        ...args,
      }),
  }),
  findCustomerByPhone: buildOpsAction({
    id: OPS_FIND_CUSTOMER_BY_PHONE_ACTION_ID,
    label: 'Find customer by phone',
    description: 'Finds an active customer by exact phone number lookup.',
    risk: 'LOW',
    argsSchema: findCustomerByPhoneArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      findCustomerByPhone({
        workspaceId,
        ...args,
      }),
  }),
  listCustomerContacts: buildOpsAction({
    id: OPS_LIST_CUSTOMER_CONTACTS_ACTION_ID,
    label: 'List customer contacts',
    description: 'Retrieves contact phone and email for customer records.',
    risk: 'LOW',
    argsSchema: listCustomerContactsArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      listCustomerContacts({
        workspaceId,
        ...args,
      }),
  }),
  linkMyCustomerRecord: buildOpsAction({
    id: OPS_LINK_MY_CUSTOMER_RECORD_ACTION_ID,
    label: 'Link my customer record',
    description: 'Links the caller phone number to a customer record.',
    risk: 'LOW',
    argsSchema: linkMyCustomerRecordArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      linkMyCustomerRecord({
        workspaceId,
        ...args,
      }),
  }),
  createBookingPlan: buildOpsAction({
    id: OPS_CREATE_BOOKING_PLAN_ACTION_ID,
    label: 'Create booking plan',
    description: 'Creates a membership, pack, or drop-in booking plan.',
    risk: 'MEDIUM',
    argsSchema: createBookingPlanArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      createBookingPlan({
        workspaceId,
        ...args,
        price: args.price
          ? { amountMinor: args.price.amountMinor, currency: args.price.currency || 'INR' }
          : undefined,
      }),
  }),
  updateBookingPlan: buildOpsAction({
    id: OPS_UPDATE_BOOKING_PLAN_ACTION_ID,
    label: 'Update booking plan',
    description: 'Updates a booking plan policy and limits.',
    risk: 'MEDIUM',
    argsSchema: updateBookingPlanArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      updateBookingPlan({
        workspaceId,
        ...args,
        price: args.price
          ? { amountMinor: args.price.amountMinor, currency: args.price.currency || 'INR' }
          : undefined,
      }),
  }),
  archiveBookingPlan: buildOpsAction({
    id: OPS_ARCHIVE_BOOKING_PLAN_ACTION_ID,
    label: 'Archive booking plan',
    description: 'Archives a booking plan.',
    risk: 'MEDIUM',
    argsSchema: archiveBookingPlanArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      archiveBookingPlan({
        workspaceId,
        ...args,
      }),
  }),
  grantMembership: buildOpsAction({
    id: OPS_GRANT_MEMBERSHIP_ACTION_ID,
    label: 'Grant membership',
    description: 'Grants a membership or pack to a customer.',
    risk: 'MEDIUM',
    argsSchema: grantMembershipArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      grantMembership({
        workspaceId,
        ...args,
      }),
  }),
  renewMembership: buildOpsAction({
    id: OPS_RENEW_MEMBERSHIP_ACTION_ID,
    label: 'Renew membership',
    description: 'Renews an existing membership with renewedFromId linking.',
    risk: 'MEDIUM',
    argsSchema: renewMembershipArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      renewMembership({
        workspaceId,
        ...args,
      }),
  }),
  freezeMembership: buildOpsAction({
    id: OPS_FREEZE_MEMBERSHIP_ACTION_ID,
    label: 'Freeze membership',
    description: 'Freezes a membership within policy limits.',
    risk: 'MEDIUM',
    argsSchema: freezeMembershipArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      freezeMembership({
        workspaceId,
        ...args,
      }),
  }),
  unfreezeMembership: buildOpsAction({
    id: OPS_UNFREEZE_MEMBERSHIP_ACTION_ID,
    label: 'Unfreeze membership',
    description: 'Unfreezes a frozen membership, extending validity.',
    risk: 'MEDIUM',
    argsSchema: unfreezeMembershipArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      unfreezeMembership({
        workspaceId,
        ...args,
      }),
  }),
  cancelMembership: buildOpsAction({
    id: OPS_CANCEL_MEMBERSHIP_ACTION_ID,
    label: 'Cancel membership',
    description: 'Cancels an active or frozen membership.',
    risk: 'MEDIUM',
    argsSchema: cancelMembershipArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      cancelMembership({
        workspaceId,
        ...args,
      }),
  }),
  recordDuePaid: buildOpsAction({
    id: OPS_RECORD_DUE_PAID_ACTION_ID,
    label: 'Record due paid',
    description: 'Records payment of an outstanding membership due.',
    risk: 'MEDIUM',
    argsSchema: recordDuePaidArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      recordDuePaid({
        workspaceId,
        ...args,
      }),
  }),
  waiveDue: buildOpsAction({
    id: OPS_WAIVE_DUE_ACTION_ID,
    label: 'Waive due',
    description: 'Waives an outstanding membership due with a stated reason.',
    risk: 'MEDIUM',
    argsSchema: waiveDueArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      waiveDue({
        workspaceId,
        ...args,
      }),
  }),
  createBookable: buildOpsAction({
    id: OPS_CREATE_BOOKABLE_ACTION_ID,
    label: 'Create bookable',
    description: 'Creates a bookable class, slot, appointment, or resource.',
    risk: 'MEDIUM',
    argsSchema: createBookableArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createBookable({
        workspaceId,
        ...args,
      } as unknown as CreateBookableInput),
  }),
  updateBookable: buildOpsAction({
    id: OPS_UPDATE_BOOKABLE_ACTION_ID,
    label: 'Update bookable',
    description: 'Updates a bookable configuration.',
    risk: 'MEDIUM',
    argsSchema: updateBookableArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      updateBookable({
        workspaceId,
        ...args,
      } as unknown as UpdateBookableInput),
  }),
  archiveBookable: buildOpsAction({
    id: OPS_ARCHIVE_BOOKABLE_ACTION_ID,
    label: 'Archive bookable',
    description: 'Archives a bookable.',
    risk: 'MEDIUM',
    argsSchema: archiveBookableArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      archiveBookable({
        workspaceId,
        ...args,
      }),
  }),
  bookSession: buildOpsAction({
    id: OPS_BOOK_SESSION_ACTION_ID,
    label: 'Book session',
    description: 'Books a session or joins waitlist.',
    risk: 'MEDIUM',
    argsSchema: bookSessionArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      bookSession({
        workspaceId,
        ...args,
      }),
  }),
  bookInHouseExtra: buildOpsAction({
    id: OPS_BOOK_IN_HOUSE_EXTRA_ACTION_ID,
    label: 'Book in-house extra',
    description: 'Books an extra session for an occupied in-house room.',
    risk: 'MEDIUM',
    argsSchema: bookInHouseExtraArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      bookInHouseExtra({
        workspaceId,
        bookableId: args.bookableId,
        date: args.date,
        waitlist: args.waitlist ?? undefined,
        opId: args.opId ?? undefined,
        inHouse: {
          roomPlaceId: args.roomPlaceId,
          guests: args.guests ?? 1,
        },
      }),
  }),
  confirmGuestExtraRequest: buildOpsAction({
    id: OPS_CONFIRM_GUEST_EXTRA_REQUEST_ACTION_ID,
    label: 'Confirm guest extra request',
    description: 'Confirms a guest extra booking request and creates the in-house booking.',
    risk: 'MEDIUM',
    argsSchema: confirmGuestExtraRequestArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      confirmGuestExtraRequest({
        workspaceId,
        ...args,
      }),
  }),
  declineGuestExtraRequest: buildOpsAction({
    id: OPS_DECLINE_GUEST_EXTRA_REQUEST_ACTION_ID,
    label: 'Decline guest extra request',
    description: 'Declines a guest extra booking request with a reason.',
    risk: 'LOW',
    argsSchema: declineGuestExtraRequestArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      declineGuestExtraRequest({
        workspaceId,
        ...args,
      }),
  }),
  cancelBooking: buildOpsAction({
    id: OPS_CANCEL_BOOKING_ACTION_ID,
    label: 'Cancel booking',
    description: 'Cancels an existing booking, triggering waitlist promotion.',
    risk: 'MEDIUM',
    argsSchema: cancelBookingArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      cancelBooking({
        workspaceId,
        ...args,
      }),
  }),
  moveBooking: buildOpsAction({
    id: OPS_MOVE_BOOKING_ACTION_ID,
    label: 'Move booking',
    description: 'Moves a booking to another session slot.',
    risk: 'MEDIUM',
    argsSchema: moveBookingArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      moveBooking({
        workspaceId,
        ...args,
      }),
  }),
  leaveWaitlist: buildOpsAction({
    id: OPS_LEAVE_WAITLIST_ACTION_ID,
    label: 'Leave waitlist',
    description: 'Leaves a session waitlist.',
    risk: 'LOW',
    argsSchema: leaveWaitlistArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      leaveWaitlist({
        workspaceId,
        ...args,
      }),
  }),
  waiveStrike: buildOpsAction({
    id: OPS_WAIVE_STRIKE_ACTION_ID,
    label: 'Waive strike',
    description: 'Waives a customer strike with an owner reason.',
    risk: 'HIGH',
    argsSchema: waiveStrikeArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      waiveStrike({
        workspaceId,
        ...args,
      }),
  }),
  clearBookingBlock: buildOpsAction({
    id: OPS_CLEAR_BOOKING_BLOCK_ACTION_ID,
    label: 'Clear booking block',
    description: 'Clears a booking block for a customer with an owner reason.',
    risk: 'HIGH',
    argsSchema: clearBookingBlockArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      clearBookingBlock({
        workspaceId,
        ...args,
      }),
  }),
  createStaysPmsConnection: buildOpsAction({
    id: OPS_CREATE_STAYS_PMS_CONNECTION_ACTION_ID,
    label: 'Create stays PMS connection',
    description: 'Creates a hotel PMS connection and generates a signing secret.',
    risk: 'HIGH',
    argsSchema: createStaysPmsConnectionArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      createStaysPmsConnection({
        workspaceId,
        ...args,
      }),
  }),
  rotateStaysPmsSecret: buildOpsAction({
    id: OPS_ROTATE_STAYS_PMS_SECRET_ACTION_ID,
    label: 'Rotate stays PMS secret',
    description: 'Rotates the webhook signing secret for stays PMS connection.',
    risk: 'HIGH',
    argsSchema: rotateStaysPmsSecretArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      rotateStaysPmsSecret({
        workspaceId,
        ...args,
      }),
  }),
  updateStaysPmsConnection: buildOpsAction({
    id: OPS_UPDATE_STAYS_PMS_CONNECTION_ACTION_ID,
    label: 'Update stays PMS connection',
    description: 'Updates configuration, status, or property map for stays PMS.',
    risk: 'MEDIUM',
    argsSchema: updateStaysPmsConnectionArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      updateStaysPmsConnection({
        workspaceId,
        ...args,
      }),
  }),
  deleteStaysPmsConnection: buildOpsAction({
    id: OPS_DELETE_STAYS_PMS_CONNECTION_ACTION_ID,
    label: 'Delete stays PMS connection',
    description: 'Deletes stays PMS connection and purges secrets.',
    risk: 'HIGH',
    argsSchema: deleteStaysPmsConnectionArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      deleteStaysPmsConnection({
        workspaceId,
        ...args,
      }),
  }),
  getStaysPmsStatus: buildOpsAction({
    id: OPS_GET_STAYS_PMS_STATUS_ACTION_ID,
    label: 'Get stays PMS status',
    description: 'Retrieves connection metadata, recent events, and outbox queue status.',
    risk: 'LOW',
    argsSchema: getStaysPmsStatusArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (_args, workspaceId) =>
      getStaysPmsStatus({
        workspaceId,
      }),
  }),
  retryStaysPmsOutbox: buildOpsAction({
    id: OPS_RETRY_STAYS_PMS_OUTBOX_ACTION_ID,
    label: 'Retry stays PMS outbox',
    description: 'Requeues failed stays PMS outbox items for dispatch.',
    risk: 'MEDIUM',
    argsSchema: retryStaysPmsOutboxArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      retryStaysPmsOutbox({
        workspaceId,
        ...args,
      }),
  }),
  testStaysPmsEzeeConnection: buildOpsAction({
    id: OPS_TEST_STAYS_PMS_EZEE_CONNECTION_ACTION_ID,
    label: 'Test eZee connection',
    description: 'Tests connectivity to eZee Absolute Housekeeping API and returns room counts.',
    risk: 'LOW',
    argsSchema: testStaysPmsEzeeConnectionArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      testStaysPmsEzeeConnection({
        workspaceId,
        ...args,
      }),
  }),
  testStaysPmsHotelogixConnection: buildOpsAction({
    id: OPS_TEST_STAYS_PMS_HOTELOGIX_CONNECTION_ACTION_ID,
    label: 'Test Hotelogix connection',
    description: 'Tests connectivity to Hotelogix HAPI and returns room counts.',
    risk: 'LOW',
    argsSchema: testStaysPmsHotelogixConnectionArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      testStaysPmsHotelogixConnection({
        workspaceId,
        ...args,
      }),
  }),
  importStaysPmsReport: buildOpsAction({
    id: OPS_IMPORT_STAYS_PMS_REPORT_ACTION_ID,
    label: 'Import Stayflexi PMS report',
    description: 'Imports a reduced Stayflexi housekeeping report into room states.',
    risk: 'MEDIUM',
    argsSchema: importStaysPmsReportArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      importStaysPmsReport({
        workspaceId,
        ...args,
      }),
  }),
  createOpsConnector: buildOpsAction({
    id: OPS_CREATE_OPS_CONNECTOR_ACTION_ID,
    label: 'Create sector connector',
    description: 'Creates a sector connector (e.g. lab/clinic integration) and generates a secret.',
    risk: 'HIGH',
    argsSchema: createOpsConnectorArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      createOpsConnector({
        workspaceId,
        ...args,
      }),
  }),
  rotateOpsConnectorSecret: buildOpsAction({
    id: OPS_ROTATE_OPS_CONNECTOR_SECRET_ACTION_ID,
    label: 'Rotate sector connector secret',
    description: 'Rotates the webhook signing secret for a sector connector.',
    risk: 'HIGH',
    argsSchema: rotateOpsConnectorSecretArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      rotateOpsConnectorSecret({
        workspaceId,
        ...args,
      }),
  }),
  updateOpsConnector: buildOpsAction({
    id: OPS_UPDATE_OPS_CONNECTOR_ACTION_ID,
    label: 'Update sector connector',
    description: 'Updates configuration, state, or event map for a sector connector.',
    risk: 'MEDIUM',
    argsSchema: updateOpsConnectorArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      updateOpsConnector({
        workspaceId,
        ...args,
      }),
  }),
  deleteOpsConnector: buildOpsAction({
    id: OPS_DELETE_OPS_CONNECTOR_ACTION_ID,
    label: 'Delete sector connector',
    description: 'Deletes a sector connector and purges its secrets.',
    risk: 'HIGH',
    argsSchema: deleteOpsConnectorArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      deleteOpsConnector({
        workspaceId,
        ...args,
      }),
  }),
  getOpsConnectorStatus: buildOpsAction({
    id: OPS_GET_OPS_CONNECTOR_STATUS_ACTION_ID,
    label: 'Get sector connector status',
    description: 'Retrieves connector configuration, recent events, and error count.',
    risk: 'LOW',
    argsSchema: getOpsConnectorStatusArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      getOpsConnectorStatus({
        workspaceId,
        ...args,
      }),
  }),
  replayOpsConnectorEvent: buildOpsAction({
    id: OPS_REPLAY_OPS_CONNECTOR_EVENT_ACTION_ID,
    label: 'Replay sector connector event',
    description: 'Replays a failed connector error event.',
    risk: 'MEDIUM',
    argsSchema: replayOpsConnectorEventArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      replayOpsConnectorEvent({
        workspaceId,
        ...args,
      }),
  }),
  issueQueueToken: buildOpsAction({
    id: OPS_ISSUE_QUEUE_TOKEN_ACTION_ID,
    label: 'Issue queue token',
    description: 'Issues the next queue token for a place and day.',
    risk: 'LOW',
    argsSchema: issueQueueTokenArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      issueQueueToken({
        workspaceId,
        ...args,
      }),
  }),
  callNextToken: buildOpsAction({
    id: OPS_CALL_NEXT_TOKEN_ACTION_ID,
    label: 'Call next token',
    description: 'Calls the next waiting queue token.',
    risk: 'LOW',
    argsSchema: callNextTokenArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      callNextToken({
        workspaceId,
        ...args,
      }),
  }),
  markTokenServed: buildOpsAction({
    id: OPS_MARK_TOKEN_SERVED_ACTION_ID,
    label: 'Mark token served',
    description: 'Marks a queue token as served.',
    risk: 'LOW',
    argsSchema: markTokenServedArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      markTokenServed({
        workspaceId,
        ...args,
      }),
  }),
  skipToken: buildOpsAction({
    id: OPS_SKIP_TOKEN_ACTION_ID,
    label: 'Skip token',
    description: 'Skips a queue token.',
    risk: 'LOW',
    argsSchema: skipTokenArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      skipToken({
        workspaceId,
        ...args,
      }),
  }),
  getQueueBoard: buildOpsAction({
    id: OPS_GET_QUEUE_BOARD_ACTION_ID,
    label: 'Get queue board',
    description: 'Gets current queue board tokens and numbers.',
    risk: 'LOW',
    argsSchema: getQueueBoardArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getQueueBoard({
        workspaceId,
        ...args,
      }),
  }),
  listQueueTokens: buildOpsAction({
    id: OPS_LIST_QUEUE_TOKENS_ACTION_ID,
    label: 'List queue tokens',
    description: 'Lists all queue tokens for today at a place.',
    risk: 'LOW',
    argsSchema: listQueueTokensArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listQueueTokens({
        workspaceId,
        ...args,
      }),
  }),
  getMyBookingQr: buildOpsAction({
    id: OPS_GET_MY_BOOKING_QR_ACTION_ID,
    label: 'Get my booking QR',
    description: 'Generates a rotating 10-minute QR code for attendance check-in.',
    risk: 'LOW',
    argsSchema: getMyBookingQrArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getMyBookingQr({
        workspaceId,
        ...args,
      }),
  }),
  checkInByCustomerQr: buildOpsAction({
    id: OPS_CHECK_IN_BY_CUSTOMER_QR_ACTION_ID,
    label: 'Check in by customer QR',
    description: 'Scans a customer QR code for booking check-in.',
    risk: 'LOW',
    argsSchema: checkInByCustomerQrArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      checkInByCustomerQr({
        workspaceId,
        ...args,
      }),
  }),
  bookingCheckInByPlaceQr: buildOpsAction({
    id: OPS_BOOKING_CHECK_IN_BY_PLACE_QR_ACTION_ID,
    label: 'Booking check in by place QR',
    description: 'Checks into a booking by scanning place QR code.',
    risk: 'LOW',
    argsSchema: bookingCheckInByPlaceQrArgsSchema,
    permission: opsMemberOrHelperPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      bookingCheckInByPlaceQr(args),
  }),
  prepareBookingCheckInOtp: buildOpsAction({
    id: OPS_PREPARE_BOOKING_CHECK_IN_OTP_ACTION_ID,
    label: 'Prepare booking check in OTP',
    description: 'Prepares an OTP sign-in for desk check-in.',
    risk: 'LOW',
    argsSchema: prepareBookingCheckInOtpArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      prepareBookingCheckInOtp({
        workspaceId,
        ...args,
      }),
  }),
  checkInByPhoneOtp: buildOpsAction({
    id: OPS_CHECK_IN_BY_PHONE_OTP_ACTION_ID,
    label: 'Check in by phone OTP',
    description: 'Verifies customer phone sign-in token and checks into booking.',
    risk: 'LOW',
    argsSchema: checkInByPhoneOtpArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      checkInByPhoneOtp({
        workspaceId,
        ...args,
      }),
  }),
  manualCheckIn: buildOpsAction({
    id: OPS_MANUAL_CHECK_IN_ACTION_ID,
    label: 'Manual check in',
    description: 'Manually marks a booking attended with a staff reason.',
    risk: 'MEDIUM',
    argsSchema: manualCheckInArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      manualCheckIn({
        workspaceId,
        ...args,
      }),
  }),
  setPublicBookingLink: buildOpsAction({
    id: OPS_SET_PUBLIC_BOOKING_LINK_ACTION_ID,
    label: 'Set public booking link',
    description: 'Enables, disables, or rotates public booking link for a workspace.',
    risk: 'MEDIUM',
    argsSchema: setPublicBookingLinkArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setPublicBookingLink({
        workspaceId,
        ...args,
      }),
  }),
  getPublicBookingInfo: buildOpsAction({
    id: OPS_GET_PUBLIC_BOOKING_INFO_ACTION_ID,
    label: 'Get public booking info',
    description: 'Fetches public bookables and sessions for a public link token.',
    risk: 'LOW',
    argsSchema: getPublicBookingInfoArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      getPublicBookingInfo(args),
  }),
  publicBookSession: buildOpsAction({
    id: OPS_PUBLIC_BOOK_SESSION_ACTION_ID,
    label: 'Public book session',
    description: 'Books a session via a public booking link.',
    risk: 'MEDIUM',
    argsSchema: publicBookSessionArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      publicBookSession(args),
  }),
  publicCancelBooking: buildOpsAction({
    id: OPS_PUBLIC_CANCEL_BOOKING_ACTION_ID,
    label: 'Public cancel booking',
    description: 'Cancels a booking via a public booking link.',
    risk: 'MEDIUM',
    argsSchema: publicCancelBookingArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      publicCancelBooking(args),
  }),
  listMyPublicBookings: buildOpsAction({
    id: OPS_LIST_MY_PUBLIC_BOOKINGS_ACTION_ID,
    label: 'List my public bookings',
    description: 'Lists bookings matching caller phone on public link.',
    risk: 'LOW',
    argsSchema: listMyPublicBookingsArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      listMyPublicBookings(args),
  }),
  listRenewalsDue: buildOpsAction({
    id: OPS_LIST_RENEWALS_DUE_ACTION_ID,
    label: 'List renewals due',
    description: 'Lists memberships due for renewal in the next 14 days.',
    risk: 'LOW',
    argsSchema: listRenewalsDueArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listRenewalsDue({
        workspaceId,
        ...args,
      }),
  }),
  listChurnCalls: buildOpsAction({
    id: OPS_LIST_CHURN_CALLS_ACTION_ID,
    label: 'List churn calls',
    description: 'Lists customers with no attendance in 14 or 30 days for follow-up.',
    risk: 'LOW',
    argsSchema: listChurnCallsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listChurnCalls({
        workspaceId,
        ...args,
      }),
  }),
  recordCustomerCall: buildOpsAction({
    id: OPS_RECORD_CUSTOMER_CALL_ACTION_ID,
    label: 'Record customer call',
    description: 'Records a desk outreach call log for a customer.',
    risk: 'LOW',
    argsSchema: recordCustomerCallArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      recordCustomerCall({
        workspaceId,
        ...args,
      }),
  }),
  createDeviceEnrolCode: buildOpsAction({
    id: OPS_CREATE_DEVICE_ENROL_CODE_ACTION_ID,
    label: 'Create device enrol code',
    description: 'Generates a single-use 10-minute code to enrol a shared tablet.',
    risk: 'MEDIUM',
    argsSchema: createDeviceEnrolCodeArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      createDeviceEnrolCode({
        workspaceId,
        ...args,
      }),
  }),
  enrolSharedDevice: buildOpsAction({
    id: OPS_ENROL_SHARED_DEVICE_ACTION_ID,
    label: 'Enrol shared device',
    description: 'Enrols a tablet using an enrolment code.',
    risk: 'MEDIUM',
    argsSchema: enrolSharedDeviceArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      enrolSharedDevice(args),
  }),
  revokeSharedDevice: buildOpsAction({
    id: OPS_REVOKE_SHARED_DEVICE_ACTION_ID,
    label: 'Revoke shared device',
    description: 'Revokes a shared device and terminates open sessions.',
    risk: 'HIGH',
    argsSchema: revokeSharedDeviceArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      revokeSharedDevice({
        workspaceId,
        ...args,
      }),
  }),
  setSharedDeviceSettings: buildOpsAction({
    id: OPS_SET_SHARED_DEVICE_SETTINGS_ACTION_ID,
    label: 'Set shared device settings',
    description: 'Updates label and idle lock timeout for a shared device.',
    risk: 'LOW',
    argsSchema: setSharedDeviceSettingsArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setSharedDeviceSettings({
        workspaceId,
        ...args,
      }),
  }),
  listSharedDevices: buildOpsAction({
    id: OPS_LIST_SHARED_DEVICES_ACTION_ID,
    label: 'List shared devices',
    description: 'Lists all registered shared devices in the workspace.',
    risk: 'LOW',
    argsSchema: listSharedDevicesArgsSchema,
    permission: opsAdminPermission,
    handler: (_args, workspaceId) =>
      listSharedDevices({
        workspaceId,
      }),
  }),
  listSharedDeviceSessions: buildOpsAction({
    id: OPS_LIST_SHARED_DEVICE_SESSIONS_ACTION_ID,
    label: 'List shared device sessions',
    description: 'Lists recent device switch-in sessions.',
    risk: 'LOW',
    argsSchema: listSharedDeviceSessionsArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      listSharedDeviceSessions({
        workspaceId,
        ...args,
      }),
  }),
  setMySharedDevicePin: buildOpsAction({
    id: OPS_SET_MY_SHARED_DEVICE_PIN_ACTION_ID,
    label: 'Set my shared device PIN',
    description: 'Sets or updates personal shared-device PIN.',
    risk: 'LOW',
    argsSchema: setMySharedDevicePinArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      setMySharedDevicePin({
        workspaceId,
        ...args,
      }),
  }),
  setSharedDevicePinByPhone: buildOpsAction({
    id: OPS_SET_SHARED_DEVICE_PIN_BY_PHONE_ACTION_ID,
    label: 'Set shared device PIN by phone',
    description: 'Sets shared device PIN at the device via verified phone OTP.',
    risk: 'MEDIUM',
    argsSchema: setSharedDevicePinByPhoneArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      setSharedDevicePinByPhone(args),
  }),
  resetSharedDevicePin: buildOpsAction({
    id: OPS_RESET_SHARED_DEVICE_PIN_ACTION_ID,
    label: 'Reset shared device PIN',
    description: 'Admin reset of a member PIN.',
    risk: 'HIGH',
    argsSchema: resetSharedDevicePinArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      resetSharedDevicePin({
        workspaceId,
        ...args,
      }),
  }),
  listSharedDevicePeople: buildOpsAction({
    id: OPS_LIST_SHARED_DEVICE_PEOPLE_ACTION_ID,
    label: 'List shared device people',
    description: 'Lists eligible members who can switch into this device.',
    risk: 'LOW',
    argsSchema: listSharedDevicePeopleArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      listSharedDevicePeople(args),
  }),
  sharedDeviceSwitchIn: buildOpsAction({
    id: OPS_SHARED_DEVICE_SWITCH_IN_ACTION_ID,
    label: 'Shared device switch in',
    description: 'Verifies PIN and switches a user into the device session.',
    risk: 'MEDIUM',
    argsSchema: sharedDeviceSwitchInArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      sharedDeviceSwitchIn(args),
  }),
  sharedDeviceSwitchOut: buildOpsAction({
    id: OPS_SHARED_DEVICE_SWITCH_OUT_ACTION_ID,
    label: 'Shared device switch out',
    description: 'Ends the active user session on the device.',
    risk: 'LOW',
    argsSchema: sharedDeviceSwitchOutArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      sharedDeviceSwitchOut(args),
  }),
  submitSharedDeviceQueued: buildOpsAction({
    id: OPS_SUBMIT_SHARED_DEVICE_QUEUED_ACTION_ID,
    label: 'Submit shared device queued items',
    description: 'Flushes offline outbox items from the shared device.',
    risk: 'MEDIUM',
    argsSchema: submitSharedDeviceQueuedArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      submitSharedDeviceQueued(args as unknown as SubmitSharedDeviceQueuedInput),
  }),
  issueSharedDeviceUploadUrl: buildOpsAction({
    id: OPS_ISSUE_SHARED_DEVICE_UPLOAD_URL_ACTION_ID,
    label: 'Issue shared device upload URL',
    description: 'Issues signed storage upload URL for offline shared device proof or voice memo.',
    risk: 'LOW',
    argsSchema: issueSharedDeviceUploadUrlArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      issueSharedDeviceUploadUrl(args as unknown as IssueSharedDeviceUploadUrlInput),
  }),
  finalizeSharedDeviceUpload: buildOpsAction({
    id: OPS_FINALIZE_SHARED_DEVICE_UPLOAD_ACTION_ID,
    label: 'Finalize shared device upload',
    description: 'Finalizes upload reservation for offline shared device proof or voice memo.',
    risk: 'LOW',
    argsSchema: finalizeSharedDeviceUploadArgsSchema,
    permission: opsPublicPermission,
    needsWorkspaceId: false,
    handler: (args) =>
      finalizeSharedDeviceUpload(args as unknown as FinalizeSharedDeviceUploadInput),
  }),
  logFoundItem: buildOpsAction({
    id: OPS_LOG_FOUND_ITEM_ACTION_ID,
    label: 'Log found item',
    description: 'Logs a discovered lost item under a stays property.',
    risk: 'LOW',
    argsSchema: logFoundItemArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      logFoundItem({
        workspaceId,
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  recordFoundItemPhoto: buildOpsAction({
    id: OPS_RECORD_FOUND_ITEM_PHOTO_ACTION_ID,
    label: 'Record found item photo',
    description: 'Records a storage photo path on a lost & found item.',
    risk: 'LOW',
    argsSchema: recordFoundItemPhotoArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args) =>
      recordFoundItemPhoto({
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  moveFoundItemBin: buildOpsAction({
    id: OPS_MOVE_FOUND_ITEM_BIN_ACTION_ID,
    label: 'Move found item bin',
    description: 'Updates the storage bin label of a lost & found item.',
    risk: 'LOW',
    argsSchema: moveFoundItemBinArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args) =>
      moveFoundItemBin({
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  startLostFoundClaim: buildOpsAction({
    id: OPS_START_LOST_FOUND_CLAIM_ACTION_ID,
    label: 'Start lost & found claim',
    description: 'Initiates a guest claim for a logged lost & found item.',
    risk: 'MEDIUM',
    argsSchema: startLostFoundClaimArgsSchema,
    permission: opsEditorPermission,
    handler: (args) =>
      startLostFoundClaim({
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  reissueLostFoundClaimLink: buildOpsAction({
    id: OPS_REISSUE_LOST_FOUND_CLAIM_LINK_ACTION_ID,
    label: 'Reissue lost & found claim link',
    description: 'Reissues a claim link with a fresh token.',
    risk: 'LOW',
    argsSchema: reissueLostFoundClaimLinkArgsSchema,
    permission: opsEditorPermission,
    handler: (args) =>
      reissueLostFoundClaimLink({
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  cancelLostFoundClaim: buildOpsAction({
    id: OPS_CANCEL_LOST_FOUND_CLAIM_ACTION_ID,
    label: 'Cancel lost & found claim',
    description: 'Cancels an active lost & found claim.',
    risk: 'MEDIUM',
    argsSchema: cancelLostFoundClaimArgsSchema,
    permission: opsEditorPermission,
    handler: (args) =>
      cancelLostFoundClaim({
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  verifyLostFoundClaim: buildOpsAction({
    id: OPS_VERIFY_LOST_FOUND_CLAIM_ACTION_ID,
    label: 'Verify lost & found claim',
    description: 'Verifies whether guest description matches the found item.',
    risk: 'MEDIUM',
    argsSchema: verifyLostFoundClaimArgsSchema,
    permission: opsEditorPermission,
    handler: (args) =>
      verifyLostFoundClaim({
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  completeLostFoundReturn: buildOpsAction({
    id: OPS_COMPLETE_LOST_FOUND_RETURN_ACTION_ID,
    label: 'Complete lost & found return',
    description: 'Marks an item as returned via pickup or courier.',
    risk: 'HIGH',
    argsSchema: completeLostFoundReturnArgsSchema,
    permission: opsEditorPermission,
    handler: (args) =>
      completeLostFoundReturn({
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  proposeLostFoundDisposal: buildOpsAction({
    id: OPS_PROPOSE_LOST_FOUND_DISPOSAL_ACTION_ID,
    label: 'Propose lost & found disposal',
    description: 'Proposes disposal method after hold period expiry.',
    risk: 'HIGH',
    argsSchema: proposeLostFoundDisposalArgsSchema,
    permission: opsEditorPermission,
    handler: (args) =>
      proposeLostFoundDisposal({
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  signLostFoundDisposal: buildOpsAction({
    id: OPS_SIGN_LOST_FOUND_DISPOSAL_ACTION_ID,
    label: 'Sign lost & found disposal',
    description: 'Provides second signature to confirm item disposal.',
    risk: 'HIGH',
    argsSchema: signLostFoundDisposalArgsSchema,
    permission: opsEditorPermission,
    handler: (args) =>
      signLostFoundDisposal({
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  withdrawLostFoundDisposal: buildOpsAction({
    id: OPS_WITHDRAW_LOST_FOUND_DISPOSAL_ACTION_ID,
    label: 'Withdraw lost & found disposal',
    description: 'Withdraws a proposed item disposal.',
    risk: 'MEDIUM',
    argsSchema: withdrawLostFoundDisposalArgsSchema,
    permission: opsEditorPermission,
    handler: (args) =>
      withdrawLostFoundDisposal({
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  setLostFoundSettings: buildOpsAction({
    id: OPS_SET_LOST_FOUND_SETTINGS_ACTION_ID,
    label: 'Set lost & found settings',
    description: 'Updates lost & found hold days configuration.',
    risk: 'HIGH',
    argsSchema: setLostFoundSettingsArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setLostFoundSettings({
        workspaceId,
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  getLostFoundClaimant: buildOpsAction({
    id: OPS_GET_LOST_FOUND_CLAIMANT_ACTION_ID,
    label: 'Get lost & found claimant PII',
    description: 'Fetches claimant name, phone, and courier address for an active claim.',
    risk: 'HIGH',
    argsSchema: getLostFoundClaimantArgsSchema,
    permission: opsEditorPermission,
    handler: (args) => getLostFoundClaimant(args),
  }),
  setOpsPhoneRoleAccess: buildOpsAction({
    id: OPS_SET_OPS_PHONE_ROLE_ACCESS_ACTION_ID,
    label: 'Set phone role access',
    description: 'Sets allowed phone surfaces and items for a workspace role.',
    risk: 'HIGH',
    argsSchema: setOpsPhoneRoleAccessArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setOpsPhoneRoleAccess({
        workspaceId,
        ...args,
      }),
  }),
  applyOpsPhoneRecommendedSet: buildOpsAction({
    id: OPS_APPLY_OPS_PHONE_RECOMMENDED_SET_ACTION_ID,
    label: 'Apply recommended phone access',
    description: 'Applies the recommended phone items for a workspace role in one tap.',
    risk: 'HIGH',
    argsSchema: applyOpsPhoneRecommendedSetArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      applyOpsPhoneRecommendedSet({
        workspaceId,
        ...args,
      }),
  }),
  setOpsPhonePersonAccess: buildOpsAction({
    id: OPS_SET_OPS_PHONE_PERSON_ACCESS_ACTION_ID,
    label: 'Set phone person access override',
    description: 'Sets person-level allow and deny overrides for phone items.',
    risk: 'HIGH',
    argsSchema: setOpsPhonePersonAccessArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setOpsPhonePersonAccess({
        workspaceId,
        ...args,
      }),
  }),
  getMyOpsPhoneItems: buildOpsAction({
    id: OPS_GET_MY_OPS_PHONE_ITEMS_ACTION_ID,
    label: 'Get my ops phone items',
    description: 'Retrieves allowed phone items for the current user in the workspace.',
    risk: 'LOW',
    argsSchema: getMyOpsPhoneItemsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getMyOpsPhoneItems({
        workspaceId: args.workspaceId || workspaceId,
      }),
  }),
  createConProject: buildOpsAction({
    id: OPS_CREATE_CON_PROJECT_ACTION_ID,
    label: 'Create construction project',
    description: 'Creates a new construction project on a SITE place.',
    risk: 'MEDIUM',
    argsSchema: createConProjectArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createConProject({ workspaceId, ...args } as CreateConProjectInput),
  }),
  updateConProject: buildOpsAction({
    id: OPS_UPDATE_CON_PROJECT_ACTION_ID,
    label: 'Update construction project',
    description: 'Updates construction project details.',
    risk: 'MEDIUM',
    argsSchema: updateConProjectArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      updateConProject({ workspaceId, ...args } as UpdateConProjectInput),
  }),
  setConProjectPackages: buildOpsAction({
    id: OPS_SET_CON_PROJECT_PACKAGES_ACTION_ID,
    label: 'Set construction project packages',
    description: 'Configures trade packages for a construction project.',
    risk: 'MEDIUM',
    argsSchema: setConProjectPackagesArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      setConProjectPackages({ workspaceId, ...args } as SetConProjectPackagesInput),
  }),
  setConProjectApprovers: buildOpsAction({
    id: OPS_SET_CON_PROJECT_APPROVERS_ACTION_ID,
    label: 'Set construction project approvers',
    description: 'Sets review, approval, indent, inspection, and change approvers for a project.',
    risk: 'MEDIUM',
    argsSchema: setConProjectApproversArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setConProjectApprovers({ workspaceId, ...args } as SetConProjectApproversInput),
  }),
  createConDrawing: buildOpsAction({
    id: OPS_CREATE_CON_DRAWING_ACTION_ID,
    label: 'Create construction drawing',
    description: 'Registers a new drawing with its initial revision in the drawing register.',
    risk: 'MEDIUM',
    argsSchema: createConDrawingArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createConDrawing({ workspaceId, ...args } as CreateConDrawingInput),
  }),
  issueConDrawingRevision: buildOpsAction({
    id: OPS_ISSUE_CON_DRAWING_REVISION_ACTION_ID,
    label: 'Issue construction drawing revision',
    description: 'Issues a new revision of a drawing and supersedes the prior revision.',
    risk: 'MEDIUM',
    argsSchema: issueConDrawingRevisionArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      issueConDrawingRevision({ workspaceId, ...args } as IssueConDrawingRevisionInput),
  }),
  withdrawConDrawing: buildOpsAction({
    id: OPS_WITHDRAW_CON_DRAWING_ACTION_ID,
    label: 'Withdraw construction drawing',
    description: 'Withdraws an active drawing from the drawing register.',
    risk: 'HIGH',
    argsSchema: withdrawConDrawingArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      withdrawConDrawing({ workspaceId, ...args } as WithdrawConDrawingInput),
  }),
  saveDprDraft: buildOpsAction({
    id: OPS_SAVE_DPR_DRAFT_ACTION_ID,
    label: 'Save DPR draft',
    description: 'Saves a draft Daily Progress Report for a construction project.',
    risk: 'LOW',
    argsSchema: saveDprDraftArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      saveDprDraft({ workspaceId, ...args } as SaveDprDraftInput),
  }),
  submitDpr: buildOpsAction({
    id: OPS_SUBMIT_DPR_ACTION_ID,
    label: 'Submit DPR',
    description: 'Submits a Daily Progress Report for engineering review.',
    risk: 'MEDIUM',
    argsSchema: submitDprArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      submitDpr({ workspaceId, ...args } as SubmitDprInput),
  }),
  reviewDpr: buildOpsAction({
    id: OPS_REVIEW_DPR_ACTION_ID,
    label: 'Review DPR',
    description: 'Records engineering review on a submitted DPR.',
    risk: 'MEDIUM',
    argsSchema: reviewDprArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      reviewDpr({ workspaceId, ...args } as ReviewDprInput),
  }),
  returnDpr: buildOpsAction({
    id: OPS_RETURN_DPR_ACTION_ID,
    label: 'Return DPR',
    description: 'Returns a DPR to author for corrections with reason.',
    risk: 'MEDIUM',
    argsSchema: returnDprArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      returnDpr({ workspaceId, ...args } as ReturnDprInput),
  }),
  approveDpr: buildOpsAction({
    id: OPS_APPROVE_DPR_ACTION_ID,
    label: 'Approve DPR',
    description: 'Project Manager approval for a Daily Progress Report.',
    risk: 'HIGH',
    argsSchema: approveDprArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      approveDpr({ workspaceId, ...args } as ApproveDprInput),
  }),
  addDiaryEntry: buildOpsAction({
    id: OPS_ADD_DIARY_ENTRY_ACTION_ID,
    label: 'Add site diary entry',
    description: 'Adds an entry to the site diary.',
    risk: 'LOW',
    argsSchema: addDiaryEntryArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      addDiaryEntry({ workspaceId, ...args } as AddDiaryEntryInput),
  }),
  saveConPourCard: buildOpsAction({
    id: OPS_SAVE_CON_POUR_CARD_ACTION_ID,
    label: 'Save pour card',
    description: 'Creates or updates a pour or pre-concealment card.',
    risk: 'MEDIUM',
    argsSchema: saveConPourCardArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      saveConPourCard({ workspaceId, ...args } as SaveConPourCardInput),
  }),
  requestConInspection: buildOpsAction({
    id: OPS_REQUEST_CON_INSPECTION_ACTION_ID,
    label: 'Request quality inspection',
    description: 'Requests a hold point inspection for a pour card.',
    risk: 'MEDIUM',
    argsSchema: requestConInspectionArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      requestConInspection({ workspaceId, ...args } as RequestConInspectionInput),
  }),
  recordConInspectionOutcome: buildOpsAction({
    id: OPS_RECORD_CON_INSPECTION_OUTCOME_ACTION_ID,
    label: 'Record inspection outcome',
    description: 'Records PASS or FAIL outcome for a quality inspection hold point.',
    risk: 'HIGH',
    argsSchema: recordConInspectionOutcomeArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      recordConInspectionOutcome({ workspaceId, ...args } as RecordConInspectionOutcomeInput),
  }),
  reassignConInspector: buildOpsAction({
    id: OPS_REASSIGN_CON_INSPECTOR_ACTION_ID,
    label: 'Reassign inspector',
    description: 'Reassigns the named inspector on a pour card before release.',
    risk: 'MEDIUM',
    argsSchema: reassignConInspectorArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      reassignConInspector({ workspaceId, ...args } as ReassignConInspectorInput),
  }),
  createConTest: buildOpsAction({
    id: OPS_CREATE_CON_TEST_ACTION_ID,
    label: 'Create test sample',
    description: 'Registers a construction quality test sample.',
    risk: 'MEDIUM',
    argsSchema: createConTestArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createConTest({ workspaceId, ...args } as CreateConTestInput),
  }),
  addConTestResult: buildOpsAction({
    id: OPS_ADD_CON_TEST_RESULT_ACTION_ID,
    label: 'Add test result',
    description: 'Enters a lab result for a test sample and evaluates against spec.',
    risk: 'MEDIUM',
    argsSchema: addConTestResultArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      addConTestResult({ workspaceId, ...args } as AddConTestResultInput),
  }),
  setBedState: buildOpsAction({
    id: OPS_SET_BED_STATE_ACTION_ID,
    label: 'Set bed state',
    description: 'Updates a hospital bed state (occupied, discharge, block, unblock, override).',
    risk: 'MEDIUM',
    argsSchema: setBedStateArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      setBedState({
        workspaceId,
        placeId: args.placeId,
        action: args.action,
        reason: args.reason ?? undefined,
        opId: args.opId || `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      } as SetBedStateInput),
  }),
  listBedEvents: buildOpsAction({
    id: OPS_LIST_BED_EVENTS_ACTION_ID,
    label: 'List bed events',
    description: 'Lists audit events for a hospital bed state.',
    risk: 'LOW',
    argsSchema: listBedEventsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listBedEvents({
        workspaceId,
        placeId: args.placeId,
        limit: args.limit ?? undefined,
        cursor: args.cursor ?? undefined,
      } as ListBedEventsInput),
  }),
  createConIssue: buildOpsAction({
    id: OPS_CREATE_CON_ISSUE_ACTION_ID,
    label: 'Create construction issue',
    description: 'Raises a hindrance, RFI, selection, or change request.',
    risk: 'MEDIUM',
    argsSchema: createConIssueArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      createConIssue({ workspaceId, ...args } as CreateConIssueInput),
  }),
  updateConIssue: buildOpsAction({
    id: OPS_UPDATE_CON_ISSUE_ACTION_ID,
    label: 'Update construction issue',
    description: 'Updates open or responded issue details.',
    risk: 'MEDIUM',
    argsSchema: updateConIssueArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      updateConIssue({ workspaceId, ...args } as UpdateConIssueInput),
  }),
  attachConIssuePhotos: buildOpsAction({
    id: OPS_ATTACH_CON_ISSUE_PHOTOS_ACTION_ID,
    label: 'Attach construction issue photos',
    description: 'Attaches photos to an open construction issue.',
    risk: 'LOW',
    argsSchema: attachConIssuePhotosArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      attachConIssuePhotos({ workspaceId, ...args } as AttachConIssuePhotosInput),
  }),
  respondConIssue: buildOpsAction({
    id: OPS_RESPOND_CON_ISSUE_ACTION_ID,
    label: 'Respond to construction issue',
    description: 'Adds a response to an issue.',
    risk: 'LOW',
    argsSchema: respondConIssueArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      respondConIssue({ workspaceId, ...args } as RespondConIssueInput),
  }),
  withdrawConIssue: buildOpsAction({
    id: OPS_WITHDRAW_CON_ISSUE_ACTION_ID,
    label: 'Withdraw construction issue',
    description: 'Withdraws an issue with reason.',
    risk: 'MEDIUM',
    argsSchema: withdrawConIssueArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      withdrawConIssue({ workspaceId, ...args } as WithdrawConIssueInput),
  }),
  closeConIssue: buildOpsAction({
    id: OPS_CLOSE_CON_ISSUE_ACTION_ID,
    label: 'Close construction issue',
    description: 'Closes a decided issue and marks linked request done.',
    risk: 'MEDIUM',
    argsSchema: closeConIssueArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      closeConIssue({ workspaceId, ...args } as CloseConIssueInput),
  }),
  decideConIssue: buildOpsAction({
    id: OPS_DECIDE_CON_ISSUE_ACTION_ID,
    label: 'Decide construction issue',
    description: 'Records a versioned decision on an issue.',
    risk: 'MEDIUM',
    argsSchema: decideConIssueArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      decideConIssue({ workspaceId, ...args } as DecideConIssueInput),
  }),
  sendConIssueForApproval: buildOpsAction({
    id: OPS_SEND_CON_ISSUE_FOR_APPROVAL_ACTION_ID,
    label: 'Send issue for client approval',
    description: 'Creates outside approval for the issue linked request.',
    risk: 'HIGH',
    argsSchema: sendConIssueForApprovalArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      sendConIssueForApproval({ workspaceId, ...args } as SendConIssueForApprovalInput),
  }),
  getConIssue: buildOpsAction({
    id: OPS_GET_CON_ISSUE_ACTION_ID,
    label: 'Get construction issue',
    description: 'Retrieves an issue with responses and decisions.',
    risk: 'LOW',
    argsSchema: getConIssueArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getConIssue({ workspaceId, ...args } as GetConIssueInput),
  }),
  getConIssuePhotoUrls: buildOpsAction({
    id: OPS_GET_CON_ISSUE_PHOTO_URLS_ACTION_ID,
    label: 'Get construction issue photo URLs',
    description: 'Retrieves signed download URLs for construction issue photos.',
    risk: 'LOW',
    argsSchema: getConIssuePhotoUrlsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getConIssuePhotoUrls({ workspaceId, ...args } as GetConIssuePhotoUrlsInput),
  }),
  listConIssues: buildOpsAction({
    id: OPS_LIST_CON_ISSUES_ACTION_ID,
    label: 'List construction issues',
    description: 'Lists issues for a project.',
    risk: 'LOW',
    argsSchema: listConIssuesArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listConIssues({ workspaceId, ...args } as ListConIssuesInput),
  }),
  listConIssueDecisions: buildOpsAction({
    id: OPS_LIST_CON_ISSUE_DECISIONS_ACTION_ID,
    label: 'List construction issue decisions',
    description: 'Lists versioned decisions for an issue.',
    risk: 'LOW',
    argsSchema: listConIssueDecisionsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listConIssueDecisions({ workspaceId, ...args } as ListConIssueDecisionsInput),
  }),
  upsertConParty: buildOpsAction({
    id: OPS_UPSERT_CON_PARTY_ACTION_ID,
    label: 'Upsert construction party',
    description: 'Creates or updates a subcontractor, supplier, or in-house gang.',
    risk: 'MEDIUM',
    argsSchema: upsertConPartyArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      upsertConParty({ workspaceId, ...args } as UpsertConPartyInput),
  }),
  listConParties: buildOpsAction({
    id: OPS_LIST_CON_PARTIES_ACTION_ID,
    label: 'List construction parties',
    description: 'Lists parties active or archived in a workspace.',
    risk: 'LOW',
    argsSchema: listConPartiesArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listConParties({ workspaceId, ...args } as ListConPartiesInput),
  }),
  saveConMeasurement: buildOpsAction({
    id: OPS_SAVE_CON_MEASUREMENT_ACTION_ID,
    label: 'Save construction measurement',
    description: 'Creates a draft or updates a draft/disputed measurement line.',
    risk: 'MEDIUM',
    argsSchema: saveConMeasurementArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      saveConMeasurement({ workspaceId, ...args } as SaveConMeasurementInput),
  }),
  submitConMeasurement: buildOpsAction({
    id: OPS_SUBMIT_CON_MEASUREMENT_ACTION_ID,
    label: 'Submit construction measurement',
    description: 'Submits a draft or disputed measurement line.',
    risk: 'MEDIUM',
    argsSchema: submitConMeasurementArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      submitConMeasurement({ workspaceId, ...args } as SubmitConMeasurementInput),
  }),
  acceptConMeasurement: buildOpsAction({
    id: OPS_ACCEPT_CON_MEASUREMENT_ACTION_ID,
    label: 'Accept construction measurement',
    description: 'Accepts a submitted measurement line with accepted quantity.',
    risk: 'MEDIUM',
    argsSchema: acceptConMeasurementArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      acceptConMeasurement({ workspaceId, ...args } as AcceptConMeasurementInput),
  }),
  disputeConMeasurement: buildOpsAction({
    id: OPS_DISPUTE_CON_MEASUREMENT_ACTION_ID,
    label: 'Dispute construction measurement',
    description: 'Disputes a submitted measurement line with reason.',
    risk: 'MEDIUM',
    argsSchema: disputeConMeasurementArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      disputeConMeasurement({ workspaceId, ...args } as DisputeConMeasurementInput),
  }),
  correctConMeasurement: buildOpsAction({
    id: OPS_CORRECT_CON_MEASUREMENT_ACTION_ID,
    label: 'Correct construction measurement',
    description: 'Corrects accepted quantity on an accepted line.',
    risk: 'MEDIUM',
    argsSchema: correctConMeasurementArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      correctConMeasurement({ workspaceId, ...args } as CorrectConMeasurementInput),
  }),
  cancelConMeasurement: buildOpsAction({
    id: OPS_CANCEL_CON_MEASUREMENT_ACTION_ID,
    label: 'Cancel construction measurement',
    description: 'Cancels a draft or disputed measurement line with reason.',
    risk: 'MEDIUM',
    argsSchema: cancelConMeasurementArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      cancelConMeasurement({ workspaceId, ...args } as CancelConMeasurementInput),
  }),
  getConMeasurement: buildOpsAction({
    id: OPS_GET_CON_MEASUREMENT_ACTION_ID,
    label: 'Get construction measurement',
    description: 'Gets measurement line details with revisions and corrections.',
    risk: 'LOW',
    argsSchema: getConMeasurementArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getConMeasurement({ workspaceId, ...args } as GetConMeasurementInput),
  }),
  listConMeasurements: buildOpsAction({
    id: OPS_LIST_CON_MEASUREMENTS_ACTION_ID,
    label: 'List construction measurements',
    description: 'Lists measurement lines for a project.',
    risk: 'LOW',
    argsSchema: listConMeasurementsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listConMeasurements({ workspaceId, ...args } as ListConMeasurementsInput),
  }),
  listDisputedConMeasurements: buildOpsAction({
    id: OPS_LIST_DISPUTED_CON_MEASUREMENTS_ACTION_ID,
    label: 'List disputed construction measurements',
    description: 'Lists disputed measurement lines for a project.',
    risk: 'LOW',
    argsSchema: listDisputedConMeasurementsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listDisputedConMeasurements({ workspaceId, ...args } as ListDisputedConMeasurementsInput),
  }),
  getConMeasureTotals: buildOpsAction({
    id: OPS_GET_CON_MEASURE_TOTALS_ACTION_ID,
    label: 'Get construction measurement totals',
    description: 'Gets cumulative measurement totals.',
    risk: 'LOW',
    argsSchema: getConMeasureTotalsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getConMeasureTotals({ workspaceId, ...args } as GetConMeasureTotalsInput),
  }),
  getConReport: buildOpsAction({
    id: OPS_GET_CON_REPORT_ACTION_ID,
    label: 'Get construction report',
    description: 'Generates construction report for approved DPRs, hindrances, overdue quality, provisional attendance, or handover index.',
    risk: 'LOW',
    argsSchema: getConReportArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getConReport({ workspaceId, ...args } as GetConReportInput),
  }),
  getPlantDowntimeReport: buildOpsAction({
    id: OPS_GET_PLANT_DOWNTIME_REPORT_ACTION_ID,
    label: 'Get plant downtime report',
    description: 'Generates downtime summary report for plant machines.',
    risk: 'LOW',
    argsSchema: getPlantDowntimeReportArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getPlantDowntimeReport({ workspaceId, ...args } as GetPlantDowntimeReportInput),
  }),
  getPlantStarterStatus: buildOpsAction({
    id: OPS_GET_PLANT_STARTER_STATUS_ACTION_ID,
    label: 'Get plant starter registers status',
    description: 'Lists status of plant starter register templates and schedules.',
    risk: 'LOW',
    argsSchema: getPlantStarterStatusArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getPlantStarterStatus({ workspaceId, ...args } as GetPlantStarterStatusInput),
  }),
  setPlantNearMissExportPeople: buildOpsAction({
    id: OPS_SET_PLANT_NEAR_MISS_EXPORT_PEOPLE_ACTION_ID,
    label: 'Set plant near-miss export people setting',
    description: 'Controls whether near-miss export includes employee identities and free text (owner only).',
    risk: 'MEDIUM',
    argsSchema: setPlantNearMissExportPeopleArgsSchema,
    permission: opsOwnerPermission,
    handler: (args, workspaceId) =>
      setPlantNearMissExportPeople({ workspaceId, ...args } as SetPlantNearMissExportPeopleInput),
  }),
  getConStarterStatus: buildOpsAction({
    id: OPS_GET_CON_STARTER_STATUS_ACTION_ID,
    label: 'Get construction starter templates status',
    description: 'Lists status of construction starter templates for a workspace.',
    risk: 'LOW',
    argsSchema: getConStarterStatusArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getConStarterStatus({ workspaceId, ...args } as GetConStarterStatusInput),
  }),
  acknowledgeOnCall: buildOpsAction({
    id: OPS_ACKNOWLEDGE_ON_CALL_ACTION_ID,
    label: 'Acknowledge on-call shift',
    description: 'Acknowledges an on-call primary or backup duty assignment in published roster.',
    risk: 'LOW',
    argsSchema: acknowledgeOnCallArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      acknowledgeOnCall({ workspaceId, ...args } as AcknowledgeOnCallInput),
  }),
  getOnCallBoard: buildOpsAction({
    id: OPS_GET_ON_CALL_BOARD_ACTION_ID,
    label: 'Get on-call board',
    description: 'Retrieves the on-call schedule and acknowledgement status board for a facility.',
    risk: 'LOW',
    argsSchema: getOnCallBoardArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getOnCallBoard({ workspaceId, ...args } as GetOnCallBoardInput),
  }),
  listMyOnCallSlots: buildOpsAction({
    id: OPS_LIST_MY_ON_CALL_SLOTS_ACTION_ID,
    label: 'List my on-call slots',
    description: 'Retrieves the caller own on-call duty slots and acknowledgement states.',
    risk: 'LOW',
    argsSchema: listMyOnCallSlotsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listMyOnCallSlots({ workspaceId, ...args } as ListMyOnCallSlotsInput),
  }),
  getHosStarterStatus: buildOpsAction({
    id: OPS_GET_HOS_STARTER_STATUS_ACTION_ID,
    label: 'Get hospital starter status',
    description: 'Lists status of hospital starter templates.',
    risk: 'LOW',
    argsSchema: getHosStarterStatusArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getHosStarterStatus({ workspaceId, ...args } as GetHosStarterStatusInput),
  }),
  setBmwSettings: buildOpsAction({
    id: OPS_SET_BMW_SETTINGS_ACTION_ID,
    label: 'Set BMW settings',
    description: 'Configures biomedical waste storage limit hours and unusual weight thresholds.',
    risk: 'MEDIUM',
    argsSchema: setBmwSettingsArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setBmwSettings({ workspaceId, ...args } as SetBmwSettingsInput),
  }),
  saveBmwDay: buildOpsAction({
    id: OPS_SAVE_BMW_DAY_ACTION_ID,
    label: 'Save BMW daily draft',
    description: 'Saves draft daily biomedical waste weight record.',
    risk: 'LOW',
    argsSchema: saveBmwDayArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      saveBmwDay({ workspaceId, ...args } as unknown as SaveBmwDayInput),
  }),
  submitBmwDay: buildOpsAction({
    id: OPS_SUBMIT_BMW_DAY_ACTION_ID,
    label: 'Submit BMW daily record',
    description: 'Submits and locks daily biomedical waste register entry.',
    risk: 'MEDIUM',
    argsSchema: submitBmwDayArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      submitBmwDay({ workspaceId, ...args } as unknown as SubmitBmwDayInput),
  }),
  correctBmwDay: buildOpsAction({
    id: OPS_CORRECT_BMW_DAY_ACTION_ID,
    label: 'Correct BMW daily record',
    description: 'Appends correction to a submitted daily biomedical waste record.',
    risk: 'MEDIUM',
    argsSchema: correctBmwDayArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      correctBmwDay({ workspaceId, ...args } as unknown as CorrectBmwDayInput),
  }),
  recordBmwPickup: buildOpsAction({
    id: OPS_RECORD_BMW_PICKUP_ACTION_ID,
    label: 'Record BMW pickup',
    description: 'Records biomedical waste vehicle pickup and covers accumulated daily records.',
    risk: 'MEDIUM',
    argsSchema: recordBmwPickupArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      recordBmwPickup({ workspaceId, ...args } as unknown as RecordBmwPickupInput),
  }),
  getBmwStatus: buildOpsAction({
    id: OPS_GET_BMW_STATUS_ACTION_ID,
    label: 'Get BMW storage status',
    description: 'Checks biomedical waste storage hours and compliance limit.',
    risk: 'LOW',
    argsSchema: getBmwStatusArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getBmwStatus({ workspaceId, ...args } as GetBmwStatusInput),
  }),
  getBmwSummary: buildOpsAction({
    id: OPS_GET_BMW_SUMMARY_ACTION_ID,
    label: 'Get BMW summary',
    description: 'Generates monthly or annual biomedical waste summary report.',
    risk: 'LOW',
    argsSchema: getBmwSummaryArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getBmwSummary({ workspaceId, ...args } as GetBmwSummaryInput),
  }),
  startBmwHandover: buildOpsAction({
    id: OPS_START_BMW_HANDOVER_ACTION_ID,
    label: 'Start BMW hand-off',
    description: 'Generates secure handover token and URL for biomedical waste collector pickup.',
    risk: 'MEDIUM',
    argsSchema: startBmwHandoverArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      startBmwHandover({ workspaceId, ...args } as StartBmwHandoverInput),
  }),
  attachBmwPickupReceipt: buildOpsAction({
    id: OPS_ATTACH_BMW_PICKUP_RECEIPT_ACTION_ID,
    label: 'Attach BMW pickup receipt',
    description: 'Attaches photo of the signed waste manifest/receipt to a completed pickup.',
    risk: 'LOW',
    argsSchema: attachBmwPickupReceiptArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      attachBmwPickupReceipt({ workspaceId, ...args } as AttachBmwPickupReceiptInput),
  }),
  ensureClinicsStarterSet: buildOpsAction({
    id: OPS_ENSURE_CLINICS_STARTER_SET_ACTION_ID,
    label: 'Ensure clinics starter set',
    description: 'Seeds cold chain and water quality starter templates for clinics and labs.',
    risk: 'MEDIUM',
    argsSchema: ensureClinicsStarterSetArgsSchema,
    permission: opsAdminPermission,
    handler: (_args, workspaceId) =>
      ensureClinicsStarterSet({ workspaceId } as EnsureClinicsStarterSetInput),
  }),
  importTempLog: buildOpsAction({
    id: OPS_IMPORT_TEMP_LOG_ACTION_ID,
    label: 'Import temperature logger CSV',
    description: 'Imports data-logger temperature readings CSV and raises cold chain excursions.',
    risk: 'HIGH',
    argsSchema: importTempLogArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      importTempLog({ workspaceId, ...args } as ImportTempLogInput),
  }),
  setStaffStatus: buildOpsAction({
    id: OPS_SET_STAFF_STATUS_ACTION_ID,
    label: 'Set staff compliance status',
    description: 'Updates training and health check dates for a worker.',
    risk: 'MEDIUM',
    argsSchema: setStaffStatusArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      setStaffStatus({ workspaceId, ...args } as unknown as SetStaffStatusInput),
  }),
  listStaffStatus: buildOpsAction({
    id: OPS_LIST_STAFF_STATUS_ACTION_ID,
    label: 'List staff compliance status',
    description: 'Lists staff training and health check statuses.',
    risk: 'LOW',
    argsSchema: listStaffStatusArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      listStaffStatus({ workspaceId, ...args } as unknown as ListStaffStatusInput),
  }),
  getMyStaffStatus: buildOpsAction({
    id: OPS_GET_MY_STAFF_STATUS_ACTION_ID,
    label: 'Get my staff compliance status',
    description: 'Reads current signed-in user staff compliance record.',
    risk: 'LOW',
    argsSchema: getMyStaffStatusArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getMyStaffStatus({ workspaceId, ...args } as GetMyStaffStatusInput),
  }),
  reportIncident: buildOpsAction({
    id: OPS_REPORT_INCIDENT_ACTION_ID,
    label: 'Report incident',
    description: 'Reports an operational safety or facility incident.',
    risk: 'MEDIUM',
    argsSchema: reportIncidentArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      reportIncident({ workspaceId, ...args } as unknown as ReportIncidentInput),
  }),
  reviewIncident: buildOpsAction({
    id: OPS_REVIEW_INCIDENT_ACTION_ID,
    label: 'Review incident',
    description: 'Acknowledges and reviews reported operational incident.',
    risk: 'MEDIUM',
    argsSchema: reviewIncidentArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      reviewIncident({ workspaceId, ...args } as ReviewIncidentInput),
  }),
  addCapaItems: buildOpsAction({
    id: OPS_ADD_CAPA_ITEMS_ACTION_ID,
    label: 'Add CAPA items',
    description: 'Adds corrective or preventive action items to an incident.',
    risk: 'MEDIUM',
    argsSchema: addCapaItemsArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      addCapaItems({ workspaceId, ...args } as unknown as AddCapaItemsInput),
  }),
  completeCapaItem: buildOpsAction({
    id: OPS_COMPLETE_CAPA_ITEM_ACTION_ID,
    label: 'Complete CAPA item',
    description: 'Marks a CAPA action item as completed.',
    risk: 'MEDIUM',
    argsSchema: completeCapaItemArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      completeCapaItem({ workspaceId, ...args } as CompleteCapaItemInput),
  }),
  recordEffectiveness: buildOpsAction({
    id: OPS_RECORD_EFFECTIVENESS_ACTION_ID,
    label: 'Record CAPA effectiveness',
    description: 'Reviews and closes or reopens incident following CAPA effectiveness check.',
    risk: 'HIGH',
    argsSchema: recordEffectivenessArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      recordEffectiveness({ workspaceId, ...args } as RecordEffectivenessInput),
  }),
  listIncidents: buildOpsAction({
    id: OPS_LIST_INCIDENTS_ACTION_ID,
    label: 'List incidents',
    description: 'Lists operational incidents for a workspace.',
    risk: 'LOW',
    argsSchema: listIncidentsArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      listIncidents({ workspaceId, ...args } as unknown as ListIncidentsInput),
  }),
  getIncident: buildOpsAction({
    id: OPS_GET_INCIDENT_ACTION_ID,
    label: 'Get incident details',
    description: 'Retrieves full details of an operational incident.',
    risk: 'LOW',
    argsSchema: getIncidentArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getIncident({ workspaceId, ...args } as GetIncidentInput),
  }),
  setIncidentSettings: buildOpsAction({
    id: OPS_SET_INCIDENT_SETTINGS_ACTION_ID,
    label: 'Set incident settings',
    description: 'Configures incident report deadline in hours.',
    risk: 'MEDIUM',
    argsSchema: setIncidentSettingsArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setIncidentSettings({ workspaceId, ...args } as SetIncidentSettingsInput),
  }),
  getIncidentSettings: buildOpsAction({
    id: OPS_GET_INCIDENT_SETTINGS_ACTION_ID,
    label: 'Get incident settings',
    description: 'Retrieves incident configuration settings for a workspace.',
    risk: 'LOW',
    argsSchema: getIncidentSettingsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getIncidentSettings({ workspaceId, ...args } as GetIncidentSettingsInput),
  }),
  addHospitalComplianceStarters: buildOpsAction({
    id: OPS_ADD_HOSPITAL_COMPLIANCE_STARTERS_ACTION_ID,
    label: 'Add hospital compliance starter licences',
    description: 'Seeds approved hospital statutory licence checklist templates.',
    risk: 'MEDIUM',
    argsSchema: addHospitalComplianceStartersArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      addHospitalComplianceStarters({ workspaceId, ...args } as AddHospitalComplianceStartersInput),
  }),
  setOwnerFeed: buildOpsAction({
    id: OPS_SET_OWNER_FEED_ACTION_ID,
    label: 'Set stays owner feed',
    description: 'Configures daily owner digest recipients and schedule.',
    risk: 'HIGH',
    argsSchema: setOwnerFeedArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setOwnerFeed({
        workspaceId,
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  removeFeedOwner: buildOpsAction({
    id: OPS_REMOVE_FEED_OWNER_ACTION_ID,
    label: 'Remove feed owner',
    description: 'Removes an owner recipient from the stays owner feed.',
    risk: 'HIGH',
    argsSchema: removeFeedOwnerArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      removeFeedOwner({
        workspaceId,
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  reissueOwnerFeedLink: buildOpsAction({
    id: OPS_REISSUE_OWNER_FEED_LINK_ACTION_ID,
    label: 'Reissue owner feed link',
    description: 'Bumps link version and invalidates existing outside owner link.',
    risk: 'HIGH',
    argsSchema: reissueOwnerFeedLinkArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      reissueOwnerFeedLink({
        workspaceId,
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  getOwnerFeed: buildOpsAction({
    id: OPS_GET_OWNER_FEED_ACTION_ID,
    label: 'Get owner feed configuration',
    description: 'Retrieves owner feed settings and masked owner recipients.',
    risk: 'LOW',
    argsSchema: getOwnerFeedArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      getOwnerFeed({
        workspaceId,
        ...args,
      }),
  }),
  listOwnerDigests: buildOpsAction({
    id: OPS_LIST_OWNER_DIGESTS_ACTION_ID,
    label: 'List owner digests',
    description: 'Retrieves historical daily owner digests for a property.',
    risk: 'LOW',
    argsSchema: listOwnerDigestsArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      listOwnerDigests({
        workspaceId,
        ...args,
      }),
  }),
  sendOwnerDigestNow: buildOpsAction({
    id: OPS_SEND_OWNER_DIGEST_NOW_ACTION_ID,
    label: 'Send owner digest now',
    description: 'Immediately dispatches today daily digest to an owner recipient.',
    risk: 'MEDIUM',
    argsSchema: sendOwnerDigestNowArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      sendOwnerDigestNow({
        workspaceId,
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
  getStaysGroupView: buildOpsAction({
    id: OPS_GET_STAYS_GROUP_VIEW_ACTION_ID,
    label: 'Get stays group view',
    description:
      'Retrieves cross-property readiness, guest requests, missed checks, and compliance scorecard for a group place.',
    risk: 'LOW',
    argsSchema: getStaysGroupViewArgsSchema,
    permission: opsEditorPermission,
    handler: (args, workspaceId) =>
      getStaysGroupView({
        workspaceId,
        ...args,
      }),
  }),
  pushStaysGroupTemplate: buildOpsAction({
    id: OPS_PUSH_STAYS_GROUP_TEMPLATE_ACTION_ID,
    label: 'Push stays group template',
    description:
      'Pushes a checklist or register template from a group place to child properties.',
    risk: 'MEDIUM',
    argsSchema: pushStaysGroupTemplateArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      pushStaysGroupTemplate({
        workspaceId,
        opId: args.opId || generateOpId(),
        ...args,
      }),
  }),
}

export const registerOpsActions = (): void => {
  for (const actionDef of Object.values(OPS_DOMAIN_ACTIONS)) {
    if (!getAction(actionDef.id)) {
      registerAction(actionDef as ActionDefinition<unknown, unknown>)
    }
  }
}

registerOpsActions()
